import { api, cleanParams } from "@/common/http/axios";
import type { JobCustomParams } from "@/utils/jobCustomParameters";

// 任务类型接口
export interface TaskType {
  id: number;
  name: string;
  taskTypeCode?: string; // 任务类型编码：smart（智驾仿真）、LPL（LPL仿真）
  creator?: string;
  createTime?: string;
  enableFlag?: number; // 1: 可选, 0: 禁用
}

// 场景树节点接口
export interface SceneTreeNode {
  id: number;
  parentId: number;
  name: string;
  nodeFlag: number;
  sceneType: string;
  /** 锁定标记：true=禁止对该节点（含子树）做删/改/加场景；后端按祖先链向下传递 */
  isLocked?: boolean;
  /** 隐藏标记：true=前台默认不展示；后台 admin 通过 includeHidden=true 可见 */
  isHidden?: boolean;
  /** 数据密级：0=公开（默认），>0=受控（仅 data:confidential:view 可见） */
  securityLevel?: number;
  /** 创建人：约定 email 优先，回退到 name；老数据可能为 null */
  creator?: string | null;
  /** 创建时间（ISO 字符串）；老数据由 DB 默认值 now() 兜底 */
  createTime?: string | null;
  children?: SceneTreeNode[];
}

// 创建任务 BuildType 枚举（与后端 BuildType 对齐）
export type BuildType = "package_neodata" | "package_bazel" | "compile_platform";

// 数据中台包：仅 algorithmVersion
export interface NeodataBuildSpec {
  type: "package_neodata";
  algorithmVersion: string;
}

// bazel 编译包：algorithmVersion（= 后端自动生成 version）；与中台包同形，仅区分来源
export interface BazelPackageBuildSpec {
  type: "package_bazel";
  algorithmVersion: string;
}

// 平台编译：commitId（多模块逗号串）
export interface CompileBuildSpec {
  type: "compile_platform";
  commitId: string;
  buildProfile?: string;
}

export type BuildSpec = NeodataBuildSpec | BazelPackageBuildSpec | CompileBuildSpec;

// 创建任务参数接口
export interface CreateJobParams {
  priority: number;
  taskTypeId: number;
  jobName?: string;
  scenarioTypes?: string;
  // 场景来源二选一：sceneSetIds=按场景集发起；sceneIds=按场景发起（上限 100，
  // 「按场景·输入场景名」由前端反查为 id 后走此字段）
  sceneSetIds?: string;
  sceneIds?: number[];
  /** 「按任务发起」标记:由后端 by_task_query 反查填充 sceneIds 时置 true,
   *  同一提交里跳过 MAX_SCENE_IDS_PER_JOB 100 上限。前端不直接传;走
   *  byTaskQuery 时后端自动置。 */
  byTask?: boolean;
  /** 「按任务查询」入口:前端只传 jobId + filters(TaskListParams 快照),后端复用
   *  TaskList SQL builder 反查 scene_ids 填 sceneIds,不再让前端搬大列表。
   *  sceneIds 不传;后端反查空集报错。 */
  byTaskQuery?: {
    jobId: number;
    filters: Record<string, unknown>;
    /** 手动勾选的任务对应 scene_id；非空时后端跳过 filters 反查，直接建匿名场景集 */
    sceneIds?: number[];
  };
  // 推荐路径：buildType + buildSpec（discriminated union）
  buildType?: BuildType;
  buildSpec?: BuildSpec;
  // legacy 字段：后端在兼容期内仍接受，新前端不要再传
  algorithmVersion?: string;
  params?: Record<string, string>;
  /** 用户选择的自定义参数；模型值为完整发布版本名。 */
  customParams?: JobCustomParams;
  /** 高级选项：重复运行次数 1-10，默认 1；N>1 时后端循环创建并给 jobName 加 _#i/N 后缀 */
  repeatTimes?: number;
  /** 数据更新（PR_DATA）页专用：**预测数据版本串**（完整串，如 prediction.AD3.1.3）。
   *  前端下拉候选 = 目录版本 + 最近产出的版本；填已存在版本 = 补进该版本，不存在 = 以该串新建。
   *  orin_dev 通道留 ``prediction.orin_dev``（后端追加时间戳区分多次产出）。 */
  predictionVersion?: string;
  /** 数据更新（PR_DATA）页专用：兼容字段（老前端 / 排程 / 重复运行）——只给**短版本号**
   *  （AD3.1.3 / release.10.12.34 / orin_dev），后端自行拼 ``prediction.{该值}``。
   *  新前端优先传 ``predictionVersion`` 完整串。 */
  dataVersion?: string;
  /** 数据更新（PR_DATA）页专用：命中已存在版本时，该场景已有数据的处理策略。
   *  ``skip``（默认）= 保留旧数据、只补缺；``overwrite`` = 重新预测并覆盖。
   *  仅当 ``predictionVersion`` 指向已存在版本时生效。 */
  fillMode?: 'skip' | 'overwrite';
  /** LPL（PNC 仿真）页专用：预测数据版本（非必填）。
   *  取值 ``default``（用 record 包内原始预测）/ ``latest``（该 job 所属 daily tag
   *  下当前生效版本）/ 显式版本号 ``prediction.{tag}.{seq}``。
   *  留空 = 走「系统参数运维」配置的默认值；非 LPL 类型后端忽略。 */
  predictionDataVersion?: string;
  /** 感知数据刷写（PE_DATA）页专用：版本号 perception.{tag}.{seq} 里的 tag（非必填，
   *  留空后端从算法版本解析 daily tag，再没有用 orin_dev）；其余类型后端忽略。 */
  perceptionTag?: string;
}

// Task列表项
export interface TaskListItem {
  taskId: number;
  jobId: number;
  taskTypeId: number;
  taskTypeName: string;
  /** 任务类型编码（task_type.task_type_code，如 LPL / PE）；前端据此区分本地仿真命令 */
  taskTypeCode?: string | null;
  status: number;
  statusName: string;
  sceneId: number;
  sceneName: string;
  /** 关联 scene_inspection.api_code（Jira Issue Key 或 Case-Flow case id，按 source 区分） */
  apiCode?: string | null;
  /** 场景来源（scene_inspection.source）：jira / case_flow / data_platform / ...
   *  前端按此决定 apiCode 跳转目标——jira→Jira 外链，case_flow→Case-Flow 外链，其他→纯文本。 */
  source?: string | null;
  /** case 场景区域 (scene_inspection.region): 'cn'/'ae'/'sea'; 前端按此决定 case-flow apiCode 跳的 waypulse 域名 */
  region?: string | null;
  createTime: string;
  endTime: string | null;
  creator: string;
  sceneType: string;
  /** 场景 logsim label(scene_inspection.label);NULL/空 = 未分类 */
  label?: string | null;
  taskDispatchId: string;
  /** 数据密级：0=公开（默认），>0=受控（仅 data:confidential:view 可见） */
  securityLevel?: number;
  evaluationStatic?: {
    evaluationResult?: boolean;
    failedCount?: number;
    successCount?: number;
    /** type=1（评测器）总行数，不分 result —— 列表 chip「评测 n」直接消费 */
    evaluatorCount?: number;
    /** type=2（respEvent / 有无责）行数，列表 chip「有无责 m」直接消费 */
    respEventCount?: number;
    /** 是否存在碰撞：type=1 中任一「*collision*」评测器判 Failed。列表「仿真结果」
     *  列据此在 chip 下方追加一行碰撞提示。 */
    hasCollision?: boolean;
    /** 碰撞相对速度(m/s)：从任意碰撞评估器双源提取（物理/责任碰撞 msg 带速度，
     *  碰路肩/碰栅栏等无）。缺失为 null，此时只显示「碰撞」不带速度。 */
    collisionRelativeSpeed?: number | null;
    /** 真实(record 侧)碰撞相对速度(m/s)：从碰撞评估器 msg "record relative speed X m/s"
     *  提取的回放真值碰撞速度。缺失为 null，此时不展示「真实碰撞」行。 */
    collisionRecordSpeed?: number | null;
    /** 本 task 跑过的 checker（type=1 评测器 + type=3 检查项）名称与判定，按名去重取最新，
     *  已按 fail → pass → other 排好序。「仿真结果」列逐个着色展示。 */
    checkers?: CheckerVerdict[];
    /** 最新一次评测的 evaluation_result.id：checkers 只按这一次判定；点开单个 checker
     *  看明细时据此过滤 /evaluation/evaluator/list 的历史行。 */
    latestEvaluationResultId?: number | null;
  } | null;
}

/** 单个 checker 在某 task 上的判定（后端 CheckerVerdictVo）。
 *  pass=通过、fail=不通过、other=pass/success/result 都判不出（如纯统计型评测器）。 */
export interface CheckerVerdict {
  name: string;
  verdict: "pass" | "fail" | "other";
  /** 1 评测器 / 3 检查项 */
  type?: number;
}

/** Daily 仿真差异模式：对比两 Job，筛选差异场景。
 *
 * - `new_success` / `new_fail`：以基准为视角的新增成功/新增失败（含单边缺失）
 * - `pass_pass` / `pass_fail` / `fail_pass` / `fail_fail`：仅交集场景的 2x2 矩阵，
 *   命名顺序 `<对比>_<基准>`（对比/历史在前、基准/今日在后），与
 *   `DailyCompare.passPass` 等字段一一对应。例：`pass_fail` = 对比通过、基准未通过
 *   = 当前新增失败。
 */
export type TaskDiffMode =
  | "new_success"
  | "new_fail"
  | "pass_pass"
  | "pass_fail"
  | "fail_pass"
  | "fail_fail"
  | "all";

/** 对比明细「差异」列筛选取值：same=一致 / diff=不一致 / only_current=仅当前 */
export type CompareConsistency = "same" | "diff" | "only_current";

/** 任务对比列表的「行为结论」筛选值：五个行为标签，外加 MISMATCH（两侧平台结论不一致）。 */
export type BehaviorFilterValue = BehaviorLabel | "MISMATCH";

/** 指标对比方向：increase=当前任务数值 > 对比任务 / decrease=当前 < 对比 / equal=两侧相等（持平） */
export type MetricCompare = "increase" | "decrease" | "equal";

/** 指标列排序方向：desc=差值（当前 − 对比）从大到小（默认）/ asc=从小到大。
 *  与 MetricCompare 正交 —— 只点表头排序时不带方向筛选。服务端跨页全局排序。 */
export type MetricSort = "asc" | "desc";

/** 整场景一致性判定：same=一致 / diff=不一致 / only_current=仅当前 Job 有该场景 */
export type CompareConsistencyVerdict = "same" | "diff" | "only_current";

/** 单条原因类别的**精确统计**（v2）。
 *
 *  kind: ``fail`` = 失败原因（为什么挂了）/ ``exempt`` = 豁免原因（为什么该挂但被放行）。
 *  code 是**机器比对键**（对比判等只比 ``(kind, code, count)``）；label 是给人看的文案。
 *  ``code=null`` 只出现在 fail 桶：算法侧尚未上报 reason 的失败行仍计数（label 为
 *  「未上报原因」）。
 *  ``count`` = 一次运行里该原因出现次数（聚合版上报的 count 之和）；
 *  ``time`` = 首次出现时间（复用结果行 timestamp）；``position`` = 代表位置，UTM 米，
 *  与 ``scene_info.events[].trigger.position`` 同坐标系，供地图标点。 */
export interface EvaluatorReasonStat {
  kind: "fail" | "exempt";
  code: string | null;
  label: string;
  /** 评估器上报的说明文案（可选）；只用于 Tooltip 展示，不参与比对。 */
  desc?: string | null;
  count: number;
  time: string | null;
  position: { x: number; y: number } | null;
  /** 全部发生点（后端上限 50），「原因同·时空异」的匹配依据；旧数据可能缺省 */
  occurrences?: { time: string | null; position: { x: number; y: number } | null; desc?: string | null }[];
}

/** 单侧原因统计：失败原因 / 豁免原因两桶（不互相抵消）。 */
export interface EvaluatorReasonStats {
  fail: EvaluatorReasonStat[];
  exempt: EvaluatorReasonStat[];
}

/** 对比页「原因」判定：same=两侧 ``{(kind, code): count}`` 完全相等（含两侧都无原因）/
 *  diff=code、次数、桶任一不同，或一侧有原因一侧无（无豁免 ⇄ 有豁免也算差异）/
 *  only_current=对比侧无该评测器结果 */
/** 原因种类：失败原因 / 豁免原因（与 ``evaluate_info.reason.kind`` 同集合）。 */
export type ReasonKind = "fail" | "exempt";

/** ``/daily/job/evaluator-reasons`` 的单条：评估器自描述的原因（stats 页两个下拉的选项）。 */
export interface JobEvaluatorReason {
  kind: ReasonKind;
  code: string;
  label: string;
}

/** same=一致 / diff=不一致（code、次数不同，或一侧有原因一侧无）/
 *  diff_spatial=原因同·时空异（code、次数相同，但发生点在位置/时间阈值内配不上）/
 *  only_current=仅当前（对比侧无该评测器结果） */
export type ReasonDiffVerdict = "same" | "diff" | "diff_spatial" | "only_current";

/** 「原因」列筛选取值，与 {@link ReasonDiffVerdict} 同集合（判定既筛选取值同源） */
export type ReasonDiff = ReasonDiffVerdict;

/** ``/daily/reason-diff`` 的单场景结果：判定 + **两侧原因精确统计**。
 *
 *  两侧统计一并返回（而不是只给判定让前端自己去评测明细里捞 label），故「判定源唯一」
 *  —— 拿到的就是判定它的那份数据；``today`` / ``compare`` 为该侧无该评测器结果时为 null，
 *  该侧跑了但干净通过时为 ``{fail: [], exempt: []}``。 */
export interface ReasonDiffCell {
  verdict: ReasonDiffVerdict;
  today: EvaluatorReasonStats | null;
  compare: EvaluatorReasonStats | null;
  /** 仅 ``diff_spatial``：每个今天发生点在对比侧最近点的位置差 / 时间差 / 是否越阈值，
   *  只用于 Tooltip 解释「为什么判时空异」，判定不读它。 */
  gaps?: ReasonSpacetimeGap[];
}

export interface ReasonSpacetimeGap {
  kind: "fail" | "exempt";
  code: string | null;
  /** 今天侧该 (kind, code) 的第几个发生点（0 起） / 它在对比侧最近的那个 */
  todayIndex: number;
  compareIndex: number;
  /** 位置差（米）；任一侧缺位置为 null */
  distM: number | null;
  /** 时间差（秒）；任一侧缺时间为 null */
  dtS: number | null;
  overPos: boolean;
  overTime: boolean;
}

// Task列表查询参数
/** 有责/无责筛选（按 type=2 行的 pass 判定，pass=false 即有责）：
 *  with=有责（task 下存在判定为有责的 type=2 行），without=无责 */
export type ResponsibilityFilter = "with" | "without";

/** 评估项状态筛选（按每场景最新 task 判定）：pass=通过 / fail=不通过 / error=异常 */
export type EvaluatorStatusFilter = "pass" | "fail" | "error";

export interface TaskListParams {
  current?: number;
  jobId: number;
  pageSize?: number;
  sceneName?: string;
  status?: string | number;
  /** 多状态筛选(Task.status IN);与 status 单值正交,承接「基于状态多选发任务」等入口 */
  statuses?: number[];
  taskId?: number;
  sceneIds?: number[];
  evaluatorName?: string;
  /** 评估项状态筛选（按每场景最新 task 的 pass/fail/error 判定）：pass=通过 / fail=不通过
   *  / error=异常（执行/系统异常、coredump 等）；与 evaluatorName 联动（同传只看该评测器，
   *  否则整体口径）。「按评测器聚合明细」格子点击即灌入此筛选，复用同一套列表过滤。 */
  evaluatorStatus?: EvaluatorStatusFilter;
  /** API_CODE 筛选（scene_inspection.api_code 精确匹配） */
  apiCode?: string;
  /** 有责/无责筛选：基于 evaluator.type == 2 行的 pass 判定（pass=false 即有责） */
  responsibility?: ResponsibilityFilter;
  /** 有无责事件筛选：按 evaluate_info.evaluator_name 精确匹配 type=2 责任事件（下拉候选按 job 读取） */
  respEventName?: string;
  /** 检查项筛选：按 evaluate_info.evaluator_name 精确匹配 type=3 检查项（下拉候选按 job 读取） */
  checkName?: string;
  /** 检查项状态筛选（按 type=3 检查项行的 pass 判定）：pass=通过 / fail=不通过；与 checkName 联动 */
  checkStatus?: "pass" | "fail";
  /** 场景 logsim label 筛选（scene_inspection.label 整串精确匹配；
   *  哨兵值 "__unlabeled__" 匹配 NULL/空 label，UI 显示为「未分类」） */
  label?: string;
  /** 地图名筛选(scene_inspection.map_name 子串 ILIKE) */
  mapName?: string;
  /** 场景区域筛选(scene_inspection.region 多值精确匹配, IN/OR 语义):
   *  'cn'=国内 / 'ae'=阿联酋 / 'sea'=新加坡。不传=不过滤(全部区域, 不能默认 cn,
   *  否则海外任务被静默隐藏)；值域外的值后端 fail-fast 拒绝。
   *  共用方：GET/POST /task/list、/task/list/count、/task/export、
   *  「按任务查询」建 Job 的场景池反查。 */
  regions?: string[];
  /** Daily 仿真对比 Job ID（与 ``diffMode`` 联用） */
  compareJobId?: number;
  /** Daily 仿真差异模式 */
  diffMode?: TaskDiffMode;
  /** 「按评测器聚合矩阵」格子联动筛选：true 时 diffMode 按单评测器口径计算差异场景集
   *  （须与 compareJobId + diffMode + evaluatorName 同传），与矩阵格子数严格对齐 */
  evaluatorDiff?: boolean;
  /** 对比明细「差异」列筛选：same=一致 / diff=不一致（须与 compareJobId 同传）。
   *  与 diffMode/evaluatorDiff 正交，同传时各自场景集取交集 */
  /** 行为对比标签筛选；须与 compareJobId 同传，与 diffMode / diffConsistency 等正交取交集。
   *  该对任务没有行为对比报告时后端返回空集，不退化成无过滤。 */
  behaviorLabel?: BehaviorFilterValue;
  /** 行为对比强事件筛选（六类强事件 code 或 ANY）；须与 compareJobId 同传，与 behaviorLabel 等正交取交集。 */
  behaviorStrong?: string;
  diffConsistency?: CompareConsistency;
  /** 对比页「原因」列筛选：same=原因一致 / diff=原因不一致 / only_current=仅当前有该评测器
   *  结果。须与 compareJobId + evaluatorName 同传（原因是评测器维度的）。
   *  与 diffConsistency/diffMode/metricCompare 正交，同传时各自场景集取交集。
   *  与 metricSort 相反：它**会收窄行集**，故 /task/list/count 一并透传。 */
  reasonDiff?: ReasonDiff;
  /** 按原因**类别**筛：只留**当前侧**该评测器 reason.code 等于此值的场景（如
   *  `yellow_light_enter`）。须与 compareJobId + evaluatorName 同传。
   *  与 reasonDiff（判定桶）正交，同传时各自场景集取交集；
   *  同样**会收窄行集**，故 /task/list/count 一并透传。
   *  取值由评估器自描述上报（前端从 /daily/reason-diff 的 today 侧汇总），不维护注册表。 */
  reasonCode?: string;
  /** 按原因**种类**筛：`fail` = 失败原因 / `exempt` = 豁免原因。**stats 页（单 Job，无
   *  compareJobId）专用**，须与 evaluatorName 同传；可与 reasonCode 同传取交集（该种类里的某个
   *  具体 code）。有 compareJobId 时后端忽略（对比页走 reasonDiff / reasonCode）。
   *  **会收窄行集**，故 /task/list/count 一并透传。 */
  reasonKind?: ReasonKind;
  /** 评测器量化指标 key（如 lc_fail）。两种用法：① 与 metricCompare 同传 → 按该指标在两
   *  Job 间的方向筛选场景；② 与 metricSort 同传 → 按该指标差值全局排序。两者正交可叠加。
   *  均须 compareJobId + evaluatorName 同传；stats 页（无 compareJobId）下它只作展示列标记，
   *  不触发任何过滤。 */
  metricKey?: string;
  /** 指标对比方向：increase=当前 > 对比 / decrease=当前 < 对比（须与 metricKey 同传） */
  metricCompare?: MetricCompare;
  /** 指标列排序方向：desc=差值（当前 − 对比）从大到小（默认）/ asc=从小到大。
   *  **跨页全局排序**（服务端对整个过滤结果集排序后分页），须与 compareJobId + evaluatorName
   *  + metricKey 同传，缺任一个则静默退回默认序；**不要求** metricCompare。
   *  只改顺序不改行集，故 /task/list/count 与 byTaskQuery 入口刻意不透传。 */
  metricSort?: MetricSort;
  /** 场景集合节点 ID（scene_tree.id），过滤属于该节点的场景 */
  /** 对比场景集：任务快照归属，包含后代；0 为未归属/不可见。 */
  compareSceneSetId?: number;
  /** 单 Job 场景集（任务快照归属，含后代；0=未归属/不可见）。不可与 compareJobId 同传。 */
  sceneSetId?: number;
  /** 单 Job 场景集判定下钻（每场景最新 task）；须与 sceneSetId 同传。 */
  sceneSetVerdict?: SceneSetVerdict;
  /** 对比配对取每场景最新 task，避免重复 task 截断另一侧数据。 */
  latestPerScene?: boolean;
  treeNodeId?: number;
  /** 场景集合节点 ID 列表（可多选、含目录节点）。目录节点会展开成其下全部叶子场景集
   *  再过滤（选父=选中其下全部场景），多选取并集；与旧单值 treeNodeId 兼容，同传优先此字段。 */
  treeNodeIds?: number[];
  /** 任务评论标题模糊搜索（当前 job + scene 维度，scene_comment.title ILIKE） */
  commentTitle?: string;
  /** 任务评论关键词：按 commentScope 匹配 title / content ILIKE（当前 job + scene 维度） */
  commentKeyword?: string;
  /** commentKeyword 匹配范围：both=title OR content（默认）/ title=仅标题 / body=仅正文 */
  commentScope?: "both" | "title" | "body";
  /** true=只看有评论的场景；false=只看无评论；不传=不过滤 */
  hasComment?: boolean;
  /** 对比任务侧评论关键词（口径限 scene + compareJobId，须同传 compareJobId） */
  compareCommentKeyword?: string;
  /** compareCommentKeyword 匹配范围，取值同 commentScope */
  compareCommentScope?: "both" | "title" | "body";
  /** 对比任务侧「有/无评论」；与 hasComment 的组合方式见 commentCombine（须同传 compareJobId） */
  compareHasComment?: boolean;
  /** 两侧评论条件的组合：and=都满足（默认）/ or=任一满足（如「任一边有评论」） */
  commentCombine?: "and" | "or";
}

// Task列表响应
export interface TaskListResponse {
  total: number;
  tasks: TaskListItem[];
  page?: PageInfo;
}

// 任务列表查询参数
export interface JobListParams {
  jobId?: number;
  /** 多个 job_id 精确查（对比页一次拉当前 + 对比双方 JobItem）。
   *  序列化成重复 query key（?jobIds=1&jobIds=2），与 FastAPI List[int] 对齐；
   *  传了它后端会忽略默认 30 天时间窗（见 getJobList 的 paramsSerializer）。 */
  jobIds?: number[];
  jobName?: string;
  priority?: string | number;
  status?: string | number;
  creator?: string;
  /** 只看当前用户收藏的 Job（与 creator 等其余过滤 AND 叠加）；不传 = 不过滤 */
  onlyFavorite?: boolean;
  /** 数据源（JobSourceEnum.id）：1=日常仿真 2=daily 仿真 3=cicd 仿真 */
  source?: string | number;
  /** 按 task_type.sim_type 过滤：'planning' / 'perception' / 'other'；不传 = 全量 */
  simType?: SimType;
  /** 场景生成 Job 列表专用:模糊匹配任一 Task 场景的 scene_inspection.api_code */
  apiCode?: string;
  /** 场景生成 Job 列表专用:模糊匹配任一 Task.scene_name */
  sceneName?: string;
  /**
   * 分区时间范围过滤(左闭右开), ISO datetime 字符串,与 Job 创建时间等价。
   * 冷热分离 v2 一期:前端时间选择器统一走 partition_time,触发 PG 分区
   * pruning 只扫命中的两周子分区。列表页默认最近 30 天(和物理 3 个月保留期对齐)。
   */
  partitionTimeStart?: string;
  partitionTimeEnd?: string;
  pageSize?: number;
  current?: number;
}

// 任务列表项
export interface JobItem {
  customParams?: JobCustomParams | null;
  /** 感知数据刷写「复制」专用：原 Job 手填的版本 tag（/perception-refresh/jobs/{id}/copy-source 返回）。 */
  perceptionTag?: string | null;
  jobId: number;
  jobName: string;
  taskTypeId: number;
  taskTypeName: string;
  /**
   * 归属仿真类型（后端 task_type.sim_type）：'planning' = PNC 仿真，
   * 'perception' = 感知仿真，'other' = 其他。旁路接口未回填时为 undefined。
   *
   * 按仿真类型分叉页面入口**只能**看这个字段：`source` 是创建入口（手工发起的
   * 感知 Job 也是 manual），`taskTypeId` 是自增主键、test/prod 两环境不一致。
   */
  simType?: string;
  priority: number;
  priorityName: string;
  status: number;
  statusName: string;
  sceneSetIds: string;
  /** 场景来源：'scene_set'=按场景集（缺省）；'scene'=按场景发起（sceneSetIds 存场景 id 串）。 */
  sceneSource?: string;
  /**
   * daily 分组虚拟节点勾选发起时的场景过滤引用（来自后端 external.scene_group_refs）。
   * 元素形如 ``{"leafId":91,"groupKey":"detour_problem"}``。scene_set_ids 列
   * varchar(64) 装不下时的托底存储；「场景集合」列渲染时同时读取。
   */
  sceneGroupRefs?: { leafId: number; groupKey: string }[] | null;
  createTime: string;
  endTime: string | null;
  creator: string | null;
  dispatchJobId: string;
  algorithmVersion: string | null;
  /** LPL（PNC 仿真）任务：建 Job 时冻结的「预测数据版本」实际生效值
   *  （``default`` 或 ``prediction.{tag}.{seq}``）；非 LPL / 存量 job 为空。
   *  来自后端 job.external['prediction_data_version_resolved']。 */
  predictionDataVersion?: string | null;
  totalTaskNum: number;
  successTaskNum: number;
  failTaskNum: number;
  /** 运行结果分桶（按 task 终态聚合，仅前台 Job 列表回填；未回填为 undefined）：
   *  resultSuccessNum=成功(通过+不通过)、resultUserFailNum=用户失败(coredump)、
   *  resultSimFailNum=仿真失败(执行异常+系统异常)、resultAbortedNum=终止(取消)。
   *  中间态不计入，故四者之和可能 < totalTaskNum。 */
  resultSuccessNum?: number | null;
  resultUserFailNum?: number | null;
  resultSimFailNum?: number | null;
  resultAbortedNum?: number | null;
  isolated?: boolean;
  /** 当前用户是否已收藏该 Job（后端 /job/list 按 favorite_of 回填；其余出口不回填） */
  isFavorite?: boolean;
  /** 数据源（JobSourceEnum.id） */
  source?: number;
  /** 数据源中文名（如"日常仿真"） */
  sourceName?: string;
  /** 创建类型（与后端 BuildType 对齐）；老数据为空 */
  buildType?: BuildType;
  /** 创建参数快照（typed BuildSpec 序列化）；老数据为空 */
  buildSpec?: BuildSpec;
  /** 场景生成 Job 专用：生成完成自动入集的目标场景集节点 id（来自 external）；未选为空 */
  addToSceneSetNodeId?: number | null;
  /** 场景生成 Job 专用：目标场景集名称（后端列表回填）；未选为空 */
  addToSceneSetName?: string | null;
  /** bazel 编译 Job 专用：发起编译时的原始入参，供「复制」回填；非 bazel job 为空 */
  bazelInputs?: BazelInputs | null;
  /** bazel 编译 Job 专用：最近一次编译触发记录的 branch_dp_info 原文（module:branch,...）；
   *  用于完整包路径弹层展示 module branch(commit)。来自 bazel_build_result。 */
  bazelBranchDpInfo?: string | null;
  /** bazel 编译 Job 专用：最近一次编译解析出的 module→commitSHA 映射；来自 bazel_build_result。 */
  bazelModuleCommits?: Record<string, string> | null;
  /** 算法版本「基准版本」递归下钻的**追溯入口版本号**（后端归一化好）：
   *  package_neodata→自身版本；package_bazel→基线版本；其他类型为空（不接入下钻）。
   *  前端据此把「基线版本号 / 完整包路径」做成可点击对象，点击调 getVersionTrace 逐层展开。 */
  traceEntryVersion?: string | null;
  /** 数据密级：0=公开（默认），>0=受控（仅 data:confidential:view 可见） */
  securityLevel?: number;
  /** 数据更新页（PR_DATA）专用：该 job 产出的预测版本行数（成功迭代场景数）。
   *  仅 /prediction-update/jobs 回填；主 /job/list 不带（undefined）。 */
  iteratedSceneNum?: number | null;
  /** 数据更新页（PR_DATA）专用：建 job 时冻结的完整迭代版本号串
   *  （prediction.{group}.{seq}，发起即定号，后端 external['prediction_version']）。
   *  仅 /prediction-update/jobs 回填；主 /job/list 不带（undefined）。 */
  predictionVersion?: string | null;
  /** 数据更新页（PR_DATA）专用：建 job 时选定并冻结的模型主版本摘要
   *  （customParams.model_release + 冻结清单）；未选为 null。
   *  仅 /prediction-update/jobs 回填；主 /job/list 不带（undefined）。 */
  modelRelease?: PredictionModelRelease | null;
}

/** 数据更新页「模型版本」列：冻结的模型主版本中的单个模型。 */
export interface PredictionModelItem {
  name: string;
  version: string;
  category?: string | null;
  type?: string | null;
  installDir?: string | null;
  fileTags: string[];
}

/** 数据更新页「模型版本」列：Job 冻结的模型主版本摘要（后端 PredictionModelReleaseVo）。 */
export interface PredictionModelRelease {
  name: string;
  releaseId?: number | null;
  models: PredictionModelItem[];
}

/** bazel 编译 Job 的原始入参快照（后端 external.bazel_inputs）。 */
export interface BazelInputs {
  base_version?: string;
  version_description?: string;
  change_module?: string;
  branch_dp_info?: string;
}

/** 算法版本递归下钻追溯：一层的完整详情（后端 VersionTraceLayerVo）。 */
export interface VersionTraceLayer {
  /** 本层裸版本号（归一化后）。 */
  version: string;
  /** 本层完整包路径。 */
  packageUrl?: string | null;
  /** 本层基准版本号（原文，展示用）。 */
  baseVersion?: string | null;
  /** 基准版本归一化后（前端续查 / 层内定位用）。 */
  baseVersionNorm?: string | null;
  /** module → { commit, 当时分支 }；每层独立，不合并。 */
  moduleCommits: Record<string, { commit?: string | null; branch?: string | null }>;
  /** 产出本层的 package_bazel job_id。 */
  sourceJobId: number;
  /** 达单次层数上限但基线仍可查 → true；前端以 baseVersionNorm 续查。 */
  hasMore: boolean;
}

/** 算法版本递归下钻追溯结果（后端 VersionTraceVo，GET /job/version-trace）。 */
export interface VersionTrace {
  /** 归一化后的追溯入口版本号；入参空/无法归一化时为空。 */
  entryVersion?: string | null;
  /** 是否至少命中一层平台 bazel（false=外部/打板包）。 */
  traceable: boolean;
  /** 停止原因：external_base / not_found / cycle / has_more。 */
  stopReason?: string | null;
  /** 逐层明细，单次一层。 */
  layers: VersionTraceLayer[];
}

/** root 汇总里的一个仓库变化项（后端 VersionTraceChangedModuleVo）。 */
export interface VersionTraceChangedModule {
  module: string;
  commit?: string | null;
  branch?: string | null;
  /** 该改动来自链上哪一层版本。 */
  sourceVersion?: string | null;
}

/** 「追到底」汇总结果（后端 VersionTraceRootVo，GET /job/version-trace/root）。 */
export interface VersionTraceRoot {
  entryVersion?: string | null;
  /** 是否至少命中一层平台 bazel（false=入口即外部/打板包）。 */
  traceable: boolean;
  /** 能追溯到的最原始平台版本。 */
  rootVersion?: string | null;
  /** 最原始平台版本的基准（多为 .release. 外部基线），即整条链的源头。 */
  rootBaseVersion?: string | null;
  /** 命中的平台层数（链长）。 */
  depth: number;
  /** 入口 → 最原始版本之间所有层改动的并集（按模块名排序）。 */
  changedModules: VersionTraceChangedModule[];
  stopReason?: string | null;
}

// 分页信息
export interface PageInfo {
  current: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

// 任务列表响应
export interface JobListResponse {
  total: number;
  tasks: JobItem[];
  page?: PageInfo;
}

// 优先级选项
export interface PriorityOption {
  value: string | number;
  label: string;
  code?: number;
  name?: string;
}

// 状态选项
export interface StatusOption {
  value: string | number;
  label: string;
  code?: number;
  name?: string;
}

/**
 * 通用解析列表响应
 */
const parseListResponse = (responseData: any) => {
  const data = responseData?.data || [];
  const page = responseData?.page || {};
  return {
    total: page.totalElements || (Array.isArray(data) ? data.length : 0),
    list: Array.isArray(data) ? data : [],
    page,
  };
};

/**
 * 通用映射选项列表
 */
const mapOptions = (data: any[]) => {
  if (!Array.isArray(data)) return [];
  return data.map((item: any) => ({
    value: item.value ?? item.code ?? item.id,
    label: item.label ?? item.name ?? item.text,
    code: item.code,
    name: item.name,
  }));
};

/**
 * 获取任务类型列表
 */
export const getTaskTypes = async (): Promise<TaskType[]> => {
  const response = await api.get("/job/taskType/list");
  return response.data?.data || response.data || [];
};

// ========== 任务类型管理（后台管理页） ==========

/**
 * DAG 模板按 build_type 的多路映射。
 *
 * 形态：`{"default": "...", "compile_platform": "...", "package_neodata": "..."}`
 * - `default`：兜底必填（前后端校验，dispatch 时按 build_type 命中失败时使用）
 * - 其他 key：与后端 `BuildType` 枚举值对齐，目前已知 `compile_platform` /
 *   `package_neodata`，未来可扩展，前端不强制限制 key 集合
 */
export type DagTemplateNameMap = Record<string, string>;

/** 已知 BuildType 列表（与后端 `simulation_platform.enums.BuildType` 对齐）。 */
export const KNOWN_BUILD_TYPES: ReadonlyArray<string> = [
  'compile_platform',
  'package_neodata',
  'package_bazel',
];

export interface TaskTypeDetail {
  id: number;
  name: string;
  taskTypeCode?: string | null;
  dagTemplateName?: DagTemplateNameMap | null;
  /** 调度平台（compute-flow）项目 ID */
  dispatchProjectId?: string | null;
  enableFlag?: number | null;
  /** 归属仿真类型：'planning' | 'perception'；后端 DEFAULT 'planning' */
  simType?: SimType | null;
  /** 运营给该任务类型的说明,用于后台管理页展示 */
  description?: string | null;
  creator?: string | null;
  createTime?: string | null;
}

/** 归属仿真类型：与前端 Sidebar / URL sim_type query 对齐
 * - planning：PNC 仿真（默认）
 * - perception：感知仿真
 * - map：地图仿真(2026-08 加,scene_category 引入时对齐)
 * - other：其他 / 试验型任务；左栏两大仿真分组都不显示
 */
export type SimType = "planning" | "perception" | "map" | "hil" | "other";

export interface TaskTypeCreatePayload {
  name: string;
  taskTypeCode?: string;
  dagTemplateName?: DagTemplateNameMap;
  dispatchProjectId?: string;
  enableFlag?: number;
  /** 归属仿真类型；不传默认 'planning' */
  simType?: SimType;
  /** 运营说明,可选 */
  description?: string;
}

export interface TaskTypeUpdatePayload {
  id: number;
  name?: string;
  taskTypeCode?: string;
  dagTemplateName?: DagTemplateNameMap;
  dispatchProjectId?: string;
  enableFlag?: number;
  /** 归属仿真类型；不传保持不变 */
  simType?: SimType;
  /** 运营说明;不传 = 不改;传空串 = 清空 */
  description?: string;
}

/**
 * 后台管理：获取任务类型全量列表（含禁用）
 */
export const listTaskTypesForAdmin = async (
  params: { name?: string; enableFlag?: number; simType?: SimType } = {},
): Promise<TaskTypeDetail[]> => {
  const response = await api.get("/task-type/list", { params: cleanParams(params) });
  return response.data?.data || [];
};

/**
 * 后台管理：新建任务类型
 */
export const createTaskType = async (
  payload: TaskTypeCreatePayload,
): Promise<TaskTypeDetail> => {
  const response = await api.post("/task-type/create", payload);
  return response.data?.data;
};

/**
 * 后台管理：更新任务类型
 */
export const updateTaskType = async (
  payload: TaskTypeUpdatePayload,
): Promise<TaskTypeDetail> => {
  const response = await api.put("/task-type/update", payload);
  return response.data?.data;
};

/**
 * 后台管理：删除任务类型（被 Job 引用时会拒绝）
 */
export const deleteTaskType = async (id: number): Promise<void> => {
  await api.delete("/task-type/delete", { params: { id } });
};

// ========== 场景类型(scene_category) ==========
// 详见 docs/scene-category-design.md v2

/** 场景类型详情(admin CRUD 与前端软校验反查共用) */
export interface SceneCategory {
  id: number;
  name: string;
  functionTag: string;
  simType: SimType;
  /** 该场景类型支持的 task_type id 数组;空数组 = "任何 task_type 都能支持" */
  taskTypeIds: number[];
  description?: string | null;
  creator?: string | null;
  createTime?: string | null;
}

/** 新建场景类型请求;name / functionTag 未软删时唯一 */
export interface SceneCategoryCreatePayload {
  name: string;
  functionTag: string;
  simType?: SimType;
  taskTypeIds?: number[];
  description?: string;
}

export interface SceneCategoryUpdatePayload {
  id: number;
  name?: string;
  functionTag?: string;
  simType?: SimType;
  taskTypeIds?: number[];
  description?: string;
}

/** 建议的 function_tag 下拉预设(与后端 TaskTypeEnum 现有 6 值对齐);
 *  不做前端强枚举,允许自定义。 */
export const SUGGESTED_FUNCTION_TAGS: ReadonlyArray<string> = [
  "PL", "PLC", "PLO", "PR", "PE", "MP",
];

/** 后台管理:场景类型列表(admin,支持 name / simType 过滤) */
export const listSceneCategoriesForAdmin = async (
  params: { name?: string; simType?: SimType } = {},
): Promise<SceneCategory[]> => {
  const response = await api.get("/scene-category/list", { params: cleanParams(params) });
  return response.data?.data || [];
};

/** 全量启用列表:普通登录用户可读,供 CreateJobModal 反查用 */
export const listSceneCategoriesEnabled = async (): Promise<SceneCategory[]> => {
  const response = await api.get("/scene-category/enabled");
  return response.data?.data || [];
};

/** 按 function_tag 反查单条(找不到返回 null) */
export const getSceneCategoryByFunctionTag = async (
  functionTag: string,
): Promise<SceneCategory | null> => {
  const response = await api.get(`/scene-category/by-function-tag/${encodeURIComponent(functionTag)}`);
  return response.data?.data ?? null;
};

export const createSceneCategory = async (
  payload: SceneCategoryCreatePayload,
): Promise<SceneCategory> => {
  const response = await api.post("/scene-category/create", payload);
  return response.data?.data;
};

export const updateSceneCategory = async (
  payload: SceneCategoryUpdatePayload,
): Promise<SceneCategory> => {
  const response = await api.put("/scene-category/update", payload);
  return response.data?.data;
};

export const deleteSceneCategory = async (id: number): Promise<void> => {
  await api.delete("/scene-category/delete", { params: { id } });
};

// ========== 后台配置项（system-config） ==========

/** 配置来源：redis=Redis 设置 / code_default=代码兜底 */
export type SystemConfigSource = "db" | "redis" | "snapshot" | "code_default";

export interface GenerateScenePackageUrlVo {
  /** 当前生效值（下游链路实际使用） */
  currentValue: string;
  /** 当前值来源 */
  source: SystemConfigSource;
  /** 代码内置兜底值（不会随 Redis 改动） */
  defaultValue: string;
}

/** 获取 generate-scene 当前 package_url 与来源 */
export const getGenerateScenePackageUrl = async (): Promise<GenerateScenePackageUrlVo> => {
  const response = await api.get("/system-config/generate-scene/package-url");
  return response.data?.data || response.data;
};

/** 更新 generate-scene 的 package_url（写入 Redis，永久生效直到下次修改 / 重置） */
export const updateGenerateScenePackageUrl = async (
  packageUrl: string,
): Promise<GenerateScenePackageUrlVo> => {
  const response = await api.put("/system-config/generate-scene/package-url", {
    packageUrl,
  });
  return response.data?.data || response.data;
};

/** 重置 generate-scene 的 package_url（删除 Redis 配置，回退代码默认） */
export const resetGenerateScenePackageUrl = async (): Promise<GenerateScenePackageUrlVo> => {
  const response = await api.delete("/system-config/generate-scene/package-url");
  return response.data?.data || response.data;
};

export const getGeneratePerceptionScenePackageUrl = async (): Promise<GenerateScenePackageUrlVo> => {
  const response = await api.get("/system-config/perception-generate-scene/package-url");
  return response.data?.data || response.data;
};

export const updateGeneratePerceptionScenePackageUrl = async (
  packageUrl: string,
): Promise<GenerateScenePackageUrlVo> => {
  const response = await api.put("/system-config/perception-generate-scene/package-url", {
    packageUrl,
  });
  return response.data?.data || response.data;
};

export const resetGeneratePerceptionScenePackageUrl = async (): Promise<GenerateScenePackageUrlVo> => {
  const response = await api.delete("/system-config/perception-generate-scene/package-url");
  return response.data?.data || response.data;
};

/** 场景生成页读取感知子树根。登录即可，不走系统参数运维权限。 */
export const getPerceptionSceneTreeRoot = async (): Promise<string> => {
  const response = await api.get("/scene-creation/perception-scene-tree-root");
  return response.data?.data?.rootId ?? "";
};

export const getPerceptionSceneTreeRootId = async (): Promise<GenerateScenePackageUrlVo> => {
  const response = await api.get("/system-config/perception-scene-tree-root-id");
  return response.data?.data || response.data;
};

export const updatePerceptionSceneTreeRootId = async (
  rootId: string,
): Promise<GenerateScenePackageUrlVo> => {
  const response = await api.put("/system-config/perception-scene-tree-root-id", { rootId });
  return response.data?.data || response.data;
};

export const resetPerceptionSceneTreeRootId = async (): Promise<GenerateScenePackageUrlVo> => {
  const response = await api.delete("/system-config/perception-scene-tree-root-id");
  return response.data?.data || response.data;
};

// ---- generate-scene-preview package_url（同体系，独立配置项）----

/** 获取 scene-preview 当前 package_url 与来源 */
export const getGenerateScenePreviewPackageUrl =
  async (): Promise<GenerateScenePackageUrlVo> => {
    const response = await api.get(
      "/system-config/generate-scene-preview/package-url",
    );
    return response.data?.data || response.data;
  };

/** 更新 scene-preview 的 package_url */
export const updateGenerateScenePreviewPackageUrl = async (
  packageUrl: string,
): Promise<GenerateScenePackageUrlVo> => {
  const response = await api.put(
    "/system-config/generate-scene-preview/package-url",
    { packageUrl },
  );
  return response.data?.data || response.data;
};

/** 重置 scene-preview 的 package_url（删除 Redis，回退代码默认） */
export const resetGenerateScenePreviewPackageUrl =
  async (): Promise<GenerateScenePackageUrlVo> => {
    const response = await api.delete(
      "/system-config/generate-scene-preview/package-url",
    );
    return response.data?.data || response.data;
  };

// ---- admin/scene-preview 三入口 ----

export interface ScenePreviewDispatchVo {
  jobId: number;
  dispatchJobId: string;
  total: number;
  skippedNoSceneInfo?: number;
  skippedNotFound?: number;
}

/** 后台 admin：按只读 SQL 查 scene_inspection.id 列表 → 批量发起预览 */
export const triggerScenePreviewBySql = async (
  sql: string,
  jobName?: string,
): Promise<ScenePreviewDispatchVo> => {
  const response = await api.post("/admin/scene-preview/by-sql", {
    sql,
    ...(jobName ? { jobName } : {}),
  });
  return response.data?.data || response.data;
};

/** 后台 admin：按场景名列表批量发起预览 */
export const triggerScenePreviewByNames = async (
  sceneNames: string[],
  jobName?: string,
): Promise<ScenePreviewDispatchVo> => {
  const response = await api.post("/admin/scene-preview/by-names", {
    sceneNames,
    ...(jobName ? { jobName } : {}),
  });
  return response.data?.data || response.data;
};

/** 后台 admin：按场景树节点 ID（叶子或目录）批量发起预览 */
export const triggerScenePreviewByTreeNodes = async (
  treeNodeIds: number[],
  jobName?: string,
): Promise<ScenePreviewDispatchVo> => {
  const response = await api.post("/admin/scene-preview/by-tree-nodes", {
    treeNodeIds,
    ...(jobName ? { jobName } : {}),
  });
  return response.data?.data || response.data;
};

/** 后台 admin：分页查询「场景预览」Job 列表（task_type_name=SCENE_PREVIEW，绕开 enable_flag 限制）。 */
export interface ScenePreviewJobsParams {
  jobName?: string;
  status?: number;
  current?: number;
  pageSize?: number;
}

export const getScenePreviewJobs = async (
  params: ScenePreviewJobsParams = {},
): Promise<JobListResponse> => {
  const response = await api.get("/admin/scene-preview/jobs", {
    params: cleanParams(params),
  });
  const { total, list, page } = parseListResponse(response.data);
  return { total, tasks: list, page };
};

export interface AlgorithmPackageUrlTemplateVo {
  /** 当前生效模板，含 {version} 占位符 */
  currentValue: string;
  source: SystemConfigSource;
  defaultValue: string;
}

export interface DailyBaseVersionVo {
  /** 当前生效的 daily base_version */
  currentValue: string;
  source: SystemConfigSource;
  /** 代码默认值（settings 中配置） */
  defaultValue: string;
}

/** 获取规划仿真算法包 URL 模板（含 {version} 占位符）当前生效值 */
export const getAlgorithmPackageUrlTemplate = async (): Promise<AlgorithmPackageUrlTemplateVo> => {
  const response = await api.get("/system-config/algorithm/package-url-template");
  return response.data?.data || response.data;
};

/** 更新算法包 URL 模板，必须含 {version} 占位符 */
export const updateAlgorithmPackageUrlTemplate = async (
  template: string,
): Promise<AlgorithmPackageUrlTemplateVo> => {
  const response = await api.put("/system-config/algorithm/package-url-template", {
    template,
  });
  return response.data?.data || response.data;
};

/** 重置算法包 URL 模板（删除 Redis 配置，回退代码默认模板） */
export const resetAlgorithmPackageUrlTemplate = async (): Promise<AlgorithmPackageUrlTemplateVo> => {
  const response = await api.delete("/system-config/algorithm/package-url-template");
  return response.data?.data || response.data;
};

/** 获取 daily bazel base_version 当前生效值 */
export const getDailyBaseVersion = async (): Promise<DailyBaseVersionVo> => {
  const response = await api.get("/system-config/daily/base-version");
  return response.data?.data || response.data;
};

/** 更新 daily bazel base_version（写入 Redis） */
export const updateDailyBaseVersion = async (
  baseVersion: string,
): Promise<DailyBaseVersionVo> => {
  const response = await api.put("/system-config/daily/base-version", {
    baseVersion,
  });
  return response.data?.data || response.data;
};

/** 重置 daily bazel base_version（删除 Redis，回退代码默认） */
export const resetDailyBaseVersion = async (): Promise<DailyBaseVersionVo> => {
  const response = await api.delete("/system-config/daily/base-version");
  return response.data?.data || response.data;
};

// ========== bazel 一键填版本预设 ==========

/** 单条 bazel 编译预设（10.10 / 10.9 / 11.0 ...）。 */
export interface BazelPreset {
  /** 下拉显示文案 */
  label: string;
  /** textarea 内容：一行一对 ``module branch`` */
  branchDpInfo: string;
  /** 基线 tag 占位（用户可改） */
  baseVersion?: string;
}
/** bazel 预设字典：key 是稳定 ID（用作 Select value 与版本侦测正则源）。 */
export type BazelPresets = Record<string, BazelPreset>;

/** admin 配置页用的三元组（currentValue/defaultValue 都是 JSON 字符串）。 */
export interface BazelPresetsVo {
  currentValue: string;
  source: SystemConfigSource;
  defaultValue: string;
}

/** 获取 bazel 预设当前生效 JSON 与来源（admin） */
export const getBazelPresets = async (): Promise<BazelPresetsVo> => {
  const response = await api.get("/system-config/bazel-presets");
  return response.data?.data || response.data;
};

/** 更新 bazel 预设 JSON（admin；后端做语法 + 结构校验，失败抛 400） */
export const updateBazelPresets = async (
  presets: string,
): Promise<BazelPresetsVo> => {
  const response = await api.put("/system-config/bazel-presets", { presets });
  return response.data?.data || response.data;
};

/** 重置 bazel 预设（删除 Redis，回退代码默认） */
export const resetBazelPresets = async (): Promise<BazelPresetsVo> => {
  const response = await api.delete("/system-config/bazel-presets");
  return response.data?.data || response.data;
};

/**
 * admin 配置页三元组，用于 bazel 模块注册表（sysconfig:bazel:module_registry）。
 * currentValue/defaultValue 都是 JSON 字符串，顶层三个字段：
 *   - paths        模块短名 → GitLab 完整 project path 映射
 *   - branchDpInfo daily/PE/PP 共用的 `模块:分支` 逗号串
 *   - changeModule 参与编译的模块名逗号串
 * Redis 优先 + 代码内置默认兜底。
 */
export interface ModuleRegistryVo {
  currentValue: string;
  source: SystemConfigSource;
  defaultValue: string;
}

/** module_registry JSON 反序列化后的形状（三个字段后端保证存在且类型正确） */
export interface ModuleRegistry {
  paths: Record<string, string>;
  branchDpInfo: string;
  changeModule: string;
}

/** 获取 module_registry 当前生效 JSON 与来源（admin） */
export const getModuleRegistry = async (): Promise<ModuleRegistryVo> => {
  const response = await api.get("/system-config/module-registry");
  return response.data?.data || response.data;
};

/**
 * 更新 module_registry JSON（admin）。
 * 后端除语法/结构校验外还强制 changeModule ⊆ branchDpInfo ⊆ paths，
 * 违反时返回 400 并点名具体模块。
 */
export const updateModuleRegistry = async (
  registry: string,
): Promise<ModuleRegistryVo> => {
  const response = await api.put("/system-config/module-registry", { registry });
  return response.data?.data || response.data;
};

/** 重置 module_registry（删除 Redis，下游回退到代码内置默认注册表） */
export const resetModuleRegistry = async (): Promise<ModuleRegistryVo> => {
  const response = await api.delete("/system-config/module-registry");
  return response.data?.data || response.data;
};

/**
 * admin 配置页三元组，用于评测器白名单
 * （sysconfig:evaluator:whitelist）。currentValue/defaultValue 都是
 * JSON 字符串，形如 {"<name>": "<cn_name>", ...}；
 * Redis 优先 + 代码内置默认兜底（30+ 条与迁移前 EVALUATOR_NAME_CN 等价）。
 */
export interface EvaluatorWhitelistVo {
  currentValue: string;
  source: SystemConfigSource;
  defaultValue: string;
}

/** 获取评测器白名单当前生效 JSON 与来源（admin） */
export const getEvaluatorWhitelist = async (): Promise<EvaluatorWhitelistVo> => {
  const response = await api.get("/system-config/evaluator-whitelist");
  return response.data?.data || response.data;
};

/** 更新评测器白名单 JSON（admin；后端做语法 + 结构校验，失败抛 400） */
export const updateEvaluatorWhitelist = async (
  whitelist: string,
): Promise<EvaluatorWhitelistVo> => {
  const response = await api.put("/system-config/evaluator-whitelist", { whitelist });
  return response.data?.data || response.data;
};

/** 重置评测器白名单（删除 Redis，下游回退到代码内置默认 30+ 条） */
export const resetEvaluatorWhitelist = async (): Promise<EvaluatorWhitelistVo> => {
  const response = await api.delete("/system-config/evaluator-whitelist");
  return response.data?.data || response.data;
};

/**
 * admin 配置页三元组，用于统计型评估器·指标聚合声明
 * （sysconfig:evaluator:metric_agg）。currentValue/defaultValue 都是 JSON 字符串，
 * 声明哪些评估器要聚合、哪些 key 直接求和、哪些是「先 ∑ 分子分母再相除」的派生率、
 * 哪些是分布型只逐场景展示。这是「评估器参数」聚合卡片的唯一配置来源。
 * Redis 优先 + 代码内置默认兜底（内置 iLQR / 换道触发检测 / 误绕行检测 三份 spec）。
 */
export interface EvaluatorMetricAggVo {
  currentValue: string;
  source: SystemConfigSource;
  defaultValue: string;
}

/**
 * admin 配置页三元组 + 原桶名，用于产物桶分片路由表
 * （sysconfig:bos:output_bucket_routing）。currentValue 形如
 * {"buckets": [...], "writable": [...]}；writable 为空时新 task 写 defaultBucket。
 */
export interface OutputBucketRoutingVo {
  currentValue: string;
  source: SystemConfigSource;
  defaultValue: string;
  defaultBucket: string;
}

/** 获取产物桶分片路由表（admin） */
export const getOutputBucketRouting = async (): Promise<OutputBucketRoutingVo> => {
  const response = await api.get("/system-config/output-bucket-routing");
  return response.data?.data || response.data;
};

/** 更新产物桶分片路由表（admin；后端校验桶名、writable ⊆ buckets、buckets 只增不减） */
export const updateOutputBucketRouting = async (
  routing: string,
): Promise<OutputBucketRoutingVo> => {
  const response = await api.put("/system-config/output-bucket-routing", { routing });
  return response.data?.data || response.data;
};

/** 回滚产物桶路由：清空 writable（新 task 回原桶），buckets 保留 */
export const resetOutputBucketRouting = async (): Promise<OutputBucketRoutingVo> => {
  const response = await api.delete("/system-config/output-bucket-routing");
  return response.data?.data || response.data;
};

/**
 * admin 配置页三元组，用于 Case-Flow 查询全局并发上限
 * （sysconfig:caseflow:query_concurrency）。currentValue/defaultValue 是整数文本，
 * 形如 "30"；所有用户 / 所有批次 / 三个 region 共用这一个上限（1–200）。
 */
export interface CaseflowQueryConcurrencyVo {
  currentValue: string;
  source: SystemConfigSource;
  defaultValue: string;
}

/** 获取 Case-Flow 查询并发上限当前生效值与来源（admin） */
export const getCaseflowQueryConcurrency = async (): Promise<CaseflowQueryConcurrencyVo> => {
  const response = await api.get("/system-config/caseflow/query-concurrency");
  return response.data?.data || response.data;
};

/** 更新 Case-Flow 查询并发上限（admin；后端校验整数且 1–200，失败抛 400） */
export const updateCaseflowQueryConcurrency = async (
  concurrency: string,
): Promise<CaseflowQueryConcurrencyVo> => {
  const response = await api.put("/system-config/caseflow/query-concurrency", { concurrency });
  return response.data?.data || response.data;
};

/** 恢复 Case-Flow 查询并发上限出厂默认（value := seed_value，≤5s 生效） */
export const resetCaseflowQueryConcurrency = async (): Promise<CaseflowQueryConcurrencyVo> => {
  const response = await api.delete("/system-config/caseflow/query-concurrency");
  return response.data?.data || response.data;
};

/**
 * admin 配置页三元组，用于预测数据开放开关（sysconfig:prediction:open_enabled）。
 * currentValue/defaultValue 是布尔文本 "true" / "false"。
 *
 * 关闭后引擎侧 M2M 接口 /prediction-open/latest-version 直接返回 hasData=false，
 * 仿真回退用 record 自带的预测节点跑完（不中断）；只停「发放」，不影响平台内部
 * 页面的预测版本列表。读不到配置项时按「开放」处理（fail-open）。
 */
export interface PredictionOpenEnabledVo {
  currentValue: string;
  source: SystemConfigSource;
  defaultValue: string;
}

/** 获取预测数据开放开关当前生效值与来源（admin） */
export const getPredictionOpenEnabled = async (): Promise<PredictionOpenEnabledVo> => {
  const response = await api.get("/system-config/prediction-open/enabled");
  return response.data?.data || response.data;
};

/** 更新预测数据开放开关（admin；后端只接受 true / false，大小写不敏感，其余抛 400） */
export const updatePredictionOpenEnabled = async (
  enabled: string,
): Promise<PredictionOpenEnabledVo> => {
  const response = await api.put("/system-config/prediction-open/enabled", { enabled });
  return response.data?.data || response.data;
};

/** 恢复预测数据开放开关出厂默认（种子为 true = 开放；value := seed_value） */
export const resetPredictionOpenEnabled = async (): Promise<PredictionOpenEnabledVo> => {
  const response = await api.delete("/system-config/prediction-open/enabled");
  return response.data?.data || response.data;
};

/**
 * admin 配置页三元组：创建 LPL 任务「预测数据版本」输入框留空时的默认值
 * （sysconfig:prediction:job_default_version）。
 * currentValue/defaultValue 只能是 ``default`` / ``latest``。
 */
export interface PredictionJobDefaultVersionVo {
  currentValue: string;
  source: SystemConfigSource;
  defaultValue: string;
}

/** 获取「创建任务默认预测数据版本」当前生效值与来源（admin） */
export const getPredictionJobDefaultVersion = async (): Promise<PredictionJobDefaultVersionVo> => {
  const response = await api.get("/system-config/prediction-job/default-version");
  return response.data?.data || response.data;
};

/**
 * 建任务弹窗用：读取「预测数据版本」默认值（普通用户可读）。
 * 与 admin 接口同一份配置，只是免去 SYS_CONFIG 权限；返回值只会是 ``default`` / ``latest``。
 */
export const getJobPredictionDefaultVersion = async (): Promise<string> => {
  const response = await api.get("/job/prediction-default-version");
  const value = response.data?.data?.value;
  return value === "default" || value === "latest" ? value : "latest";
};

/** 建任务弹窗「预测数据版本」下拉候选（普通用户可读）。
 * 数据源 = 「预测数据版本列表」中打开「创建任务展示」开关的版本号。
 */
export interface JobPredictionDataVersionOption {
  /** 完整预测数据版本串（prediction.AD3.1.3 / prediction.orin_dev.{时间戳}）——下拉值。 */
  predictionVersion: string;
  /** 数据版本元信息（AD3.1.3 / release.10.12.34 / orin_dev），默认值匹配与展示用。 */
  dataVersion?: string | null;
  description?: string | null;
  /** 代表批所属分组（新口径 = dataVersion）。 */
  groupTag?: string | null;
  /** 该版本下是否有当前生效批（默认模式 latest 折算默认值用）。 */
  isCurrent?: boolean;
}

/** 建任务「预测数据版本」候选（fail-soft：接口异常回 []，弹窗仍可选 latest / default）。 */
export const getJobPredictionDataVersions = async (): Promise<
  JobPredictionDataVersionOption[]
> => {
  try {
    const response = await api.get("/job/prediction-data-versions");
    const data = response.data?.data;
    return Array.isArray(data) ? (data as JobPredictionDataVersionOption[]) : [];
  } catch {
    return [];
  }
};

/** 更新「创建任务默认预测数据版本」（admin；只接受 default / latest，大小写不敏感） */
export const updatePredictionJobDefaultVersion = async (
  value: string,
): Promise<PredictionJobDefaultVersionVo> => {
  const response = await api.put("/system-config/prediction-job/default-version", { value });
  return response.data?.data || response.data;
};

/** 恢复「创建任务默认预测数据版本」出厂默认（种子为 latest） */
export const resetPredictionJobDefaultVersion = async (): Promise<PredictionJobDefaultVersionVo> => {
  const response = await api.delete("/system-config/prediction-job/default-version");
  return response.data?.data || response.data;
};

/** 获取指标聚合声明当前生效 JSON 与来源（admin） */
export const getEvaluatorMetricAgg = async (): Promise<EvaluatorMetricAggVo> => {
  const response = await api.get("/system-config/evaluator-metric-agg");
  return response.data?.data || response.data;
};

/** 更新指标聚合声明 JSON（admin；后端做语法 + 结构校验，失败抛 400） */
export const updateEvaluatorMetricAgg = async (
  agg: string,
): Promise<EvaluatorMetricAggVo> => {
  const response = await api.put("/system-config/evaluator-metric-agg", { agg });
  return response.data?.data || response.data;
};

/** 重置指标聚合声明（删除 Redis，下游回退到代码内置默认 spec） */
export const resetEvaluatorMetricAgg = async (): Promise<EvaluatorMetricAggVo> => {
  const response = await api.delete("/system-config/evaluator-metric-agg");
  return response.data?.data || response.data;
};

/**
 * 普通用户读：返回解析后的预设字典（不是 JSON 字符串）。
 * 拉取失败或返回非对象时返回 {}，调用方据此 disable 一键填下拉（不兜底）。
 */
export const getJobBazelPresets = async (): Promise<BazelPresets> => {
  const response = await api.get("/job/bazel-presets");
  const data = response.data?.data ?? response.data;
  return data && typeof data === "object" ? (data as BazelPresets) : {};
};

/** 探测算法包在 BOS 上是否存在（创建 Job 弹窗字段实时反馈用）。 */
export interface AlgorithmPackageExistsVo {
  /** 用户输入的版本号或 bos:// 全路径（服务端 trim 后回显） */
  version: string;
  /** 请求时携带的 buildType（原样回显） */
  buildType: string;
  /** 服务端拼装完成的 bos:// 全路径；空串表示无法解析 */
  resolvedUrl: string;
  /** true=BOS 存在；false=不存在 或 探测异常（此时 error 有值） */
  exists: boolean;
  /**
   * 探测异常（BOS 401 / 网络 / URL 模板未配等）说明；非空时前端应显示"未知"
   * 而非"不存在"，避免误导用户以为包真的不在。
   */
  error?: string;
}

/**
 * 探测算法包是否存在。用于「版本号」/「基线版本」失焦后实时反馈；
 * 前端应 debounce ~500ms 避免每次按键都发请求。返回 exists=false 但
 * 带 error 时表示服务端无法完成探测（不代表包真的不存在），前端应
 * 显示"未知"而非"不存在"，且不阻塞提交。
 */
export const checkAlgorithmPackageExists = async (
  version: string,
  buildType: "package_neodata" | "package_bazel" = "package_neodata",
): Promise<AlgorithmPackageExistsVo> => {
  const response = await api.get("/job/algorithm-package/exists", {
    params: { version, buildType },
  });
  const data = response.data?.data;
  return (data as AlgorithmPackageExistsVo) ?? {
    version, buildType, resolvedUrl: "", exists: false,
  };
};

/** Daily 仿真手动触发返回值（与 CicdJobVo 对齐） */
export interface DailyJobTriggerVo {
  jobId?: number;
  dispatchJobId?: string;
}

/** 手动触发一次 daily 仿真（等价于每天 01:00 cron 的一次性下发） */
export const triggerDailyJob = async (): Promise<DailyJobTriggerVo> => {
  const response = await api.post("/system-config/trigger/daily-job");
  return response.data?.data || response.data;
};

/** 批量刷新未完成 Job 状态返回结构（与 ``job_service.refresh_running_jobs_bulk`` 对齐） */
export interface BulkRefreshRunningJobsVo {
  /** 全局锁被占用，整轮跳过；其余字段为 0/[] */
  lock_busy: boolean;
  /** 本轮实际枚举到的 job 数 */
  scanned: number;
  /** 真正完成 dispatch 同步的 job 数 */
  refreshed: number;
  /** 已被 per-job 锁占用 / 期间状态变化 / 已终态 等 */
  skipped: number;
  /** 异常的 job 数（失败但不中断扫描） */
  failed: number;
  /** 异常 job_id 列表（最多 50 个） */
  failed_job_ids: number[];
  /** 未完成 job 总数 > batch_limit，需要分批触发 */
  has_more: boolean;
  /** 单次扫描上限 */
  batch_limit: number;
  /** 触发前 DB 中未完成 job 总数 */
  total_running_before: number;
}

/** 触发批量刷新所有未完成 Job 的状态（仅后台管理用） */
export const triggerBulkRefreshRunningJobs = async (): Promise<BulkRefreshRunningJobsVo> => {
  const response = await api.post("/system-config/trigger/refresh-running-jobs");
  return response.data?.data || response.data;
};

/**
 * 触发「最近 6 小时地图查询并生成」（仅后台管理用，admin-only）。
 * 固定 months=6 / storage=bos / 最近 6 小时时间窗；后端 Redis 锁防重复触发。
 * 返回后端提示文案（下发结果 JSON，或已在运行时的忙提示）。
 */
export const triggerMapGenerateRecent = async (): Promise<string> => {
  const response = await api.get("/map/recent/generate");
  const data = response.data?.data;
  return typeof data === 'string' ? data : JSON.stringify(data ?? '');
};

/** 分区清理进度（与 ``PartitionCleanupProgress`` 对齐，全部驼峰）。 */
export interface PartitionCleanupProgress {
  /** 命中的过期分区「日期组」数（4 张表按日期归组） */
  partitionsTotal: number;
  /** 已处理（真删=已 DROP；dry-run=已统计）的日期组数 */
  partitionsProcessed: number;
  /** 已 GC 的 evaluation_result 行数 */
  rowsGced: number;
  /** 已删除的产物前缀数（= 已删产物的行数） */
  bosPrefixesDeleted: number;
  /** 已删除的 BOS 对象数 */
  bosObjectsDeleted: number;
  /** 已 DROP 的子分区物理表数（4 张各算 1） */
  partitionsDropped: number;
  /** 无法解析 address / 语义不明被跳过、未删的行数 */
  skipped: number;
}

/** 分区清理任务状态（与 ``PartitionCleanupJobStatus`` 对齐，存 Redis 供轮询）。 */
export interface PartitionCleanupJobStatus {
  status: 'idle' | 'running' | 'completed' | 'failed';
  sessionId?: string | null;
  dryRun?: boolean | null;
  retentionMonths?: number | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  progress?: PartitionCleanupProgress | null;
  /** 完成后的汇总；dry-run 时含各日期组估算行数 groups[] */
  result?: Record<string, unknown> | null;
  message?: string | null;
}

/** 分区清理触发返回（已提交后台，或已有任务在运行时返回当前状态）。 */
export interface CleanupExpiredPartitionsQueued {
  queued?: boolean;
  sessionId?: string;
  dryRun?: boolean;
  message?: string;
  /** 已有任务在运行时后端返回的是 PartitionCleanupJobStatus，这里合并成宽类型 */
  status?: string;
}

/**
 * 触发「清理超过保留期的分区数据 + 其产物 BOS」（冷热分离 v2 二期，admin-only）。
 * 异步执行、Redis 持久进度；全平台同时仅允许一个清理任务在跑。
 * @param dryRun true=只统计不删（首上线务必先跑）；false=真删（DETACH→GC→DROP）
 * @param retentionMonths 保留期（月），默认 3
 */
export const triggerCleanupExpiredPartitions = async (
  dryRun: boolean,
  retentionMonths = 3,
): Promise<CleanupExpiredPartitionsQueued & Partial<PartitionCleanupJobStatus>> => {
  const response = await api.post(
    "/system-config/trigger/cleanup-expired-partitions",
    null,
    { params: { dryRun, retentionMonths } },
  );
  return response.data?.data || response.data;
};

/** 查询全局分区清理任务状态及进度。 */
export const getCleanupExpiredPartitionsStatus = async (): Promise<PartitionCleanupJobStatus> => {
  const response = await api.get("/system-config/trigger/cleanup-expired-partitions/status");
  return response.data?.data || response.data || { status: 'idle' };
};

/**
 * 获取场景树。``includeHidden=true`` 时（仅后台管理）返回隐藏节点及其后代。
 */
export const getSceneTree = async (
  includeHidden = false,
): Promise<SceneTreeNode[]> => {
  const response = await api.get("/scene-tree/tree", {
    params: includeHidden ? { includeHidden: true } : undefined,
  });
  return response.data?.data || response.data || [];
};

/**
 * 场景集下按 scene_group 分组统计。供场景管理左侧树 daily 集合分组展开使用。
 * 返回按 count 降序；NULL/空串归到 sentinel `__uncategorized__`。
 */
export interface SceneGroupStat {
  sceneGroup: string;
  count: number;
}

export const getSceneGroupStats = async (
  parentIds: number[],
  includeHidden = false,
): Promise<SceneGroupStat[]> => {
  if (!parentIds?.length) return [];
  const response = await api.get("/scene-tree/scene-groups", {
    params: { parentIds: parentIds.join(','), ...(includeHidden ? { includeHidden: true } : {}) },
  });
  return response.data?.data || [];
};

/**
 * 后台 admin：设置场景树节点锁定标记。
 */
export const setSceneTreeNodeLocked = async (
  nodeId: number,
  flag: boolean,
): Promise<void> => {
  await api.post("/scene-tree/lock", { nodeId, flag });
};

/**
 * 后台 admin：设置场景树节点隐藏标记。
 */
export const setSceneTreeNodeHidden = async (
  nodeId: number,
  flag: boolean,
): Promise<void> => {
  await api.post("/scene-tree/hide", { nodeId, flag });
};

export interface PurgeAnonSetsResult {
  deletedNodes: number;
  deletedLinks: number;
  cutoff: string;
  dryRun: boolean;
}

/**
 * 仅 admin：清理「按任务查询」产生的匿名场景集（``__by_task_container__`` 下的
 * ``bytaskJ*`` 叶子）。删除不可逆——被删叶子正被历史 Job 的 sceneSetIds 引用，
 * 删掉后那些 Job 无法重跑，所以后端用的是 ``require_role("admin")`` 而不是
 * 本页的 ``menu:scene-collection``。
 *
 * ``dryRun=true`` 只统计不删，用于弹确认框前先告诉用户要删多少。
 */
export const purgeAnonymousSceneSets = async (
  params: { retentionDays?: number; dryRun?: boolean } = {},
): Promise<PurgeAnonSetsResult> => {
  const response = await api.post("/scene-tree/anonymous-sets/purge", null, {
    params: {
      retentionDays: params.retentionDays ?? 90,
      dryRun: params.dryRun ?? false,
    },
  });
  return (response.data?.data ?? response.data) as PurgeAnonSetsResult;
};

/**
 * 创建场景目录/叶子节点
 */
export const createSceneDir = async (params: {
  parentId: number; name: string; nodeFlag: number;
}): Promise<void> => {
  await api.post("/scene-tree/create/dir", params);
};

/**
 * 重命名场景树节点
 */
export const renameSceneTreeNode = async (id: number, name: string): Promise<void> => {
  await api.post("/scene-tree/rename", { id, name });
};

/**
 * 删除场景树节点
 */
export const deleteSceneTreeNode = async (nodeId: number): Promise<void> => {
  await api.delete(`/scene-tree/delete/${nodeId}`);
};

/**
 * 复制场景树节点（叶子或目录）到目标父节点下。
 * 浅拷贝：只复制节点结构 + scene_tree_node 关联，不复制 scene_inspection 实体。
 */
export interface CopySceneTreeNodeParams {
  sourceNodeId: number;
  targetParentId: number; // -1 表示根目录
  newName: string;
}

export interface CopySceneTreeNodeResult {
  newRootId: number;
  copiedNodes: number;
  copiedSceneRefs: number;
}

export const copySceneTreeNode = async (
  params: CopySceneTreeNodeParams,
): Promise<CopySceneTreeNodeResult> => {
  const response = await api.post("/scene-tree/copy", params);
  return response.data?.data || response.data;
};

/**
 * 查询可用场景列表（状态5-8）
 */
export interface AvailableSceneItem {
  id: number;
  name: string;
  /** 对应 scene_inspection.api_code（Jira key / 回收 task_id 等） */
  apiCode?: string | null;
  /** logsim 标签，对应 scene_inspection.label */
  label?: string | null;
  recordAddress: string;
  vin: string;
  creator: string;
  createTime: string;
  mapName: string;
  mapVersion: string;
  sceneType: string;
  statusName: string;
  /** 场景来源（jira / data_platform / collect_task），由 SceneInspectionVo 直接透传 */
  source?: SceneSource | string | null;
  /** 区域标记: 'cn' (国内) / 'ae' (阿联酋) / 'sea' (新加坡). 由 case_flow_service 落库时派生, 供筛选 + waypulse 跳转路由 */
  region?: string | null;
  /** 是否已禁用（逻辑删除）：1=禁用，前端按此置灰；0/undefined=正常 */
  isDeleted?: number | null;
}

// 可用场景查询参数（所有过滤项支持多值；改用 JSON body 直传数组，避免 URL 过长触发 414）
export interface AvailableSceneParams {
  current?: number;
  pageSize?: number;
  /** 多个场景 ID，按 IN 精确匹配 */
  ids?: number[];
  /** 多个场景名称，每个独立子串 ilike，OR 合并 */
  names?: string[];
  /** 场景状态(SceneStatusEnum.id):可选单值,仅接受 5/7/8(质检中/已完成-瑕疵品/已完成),
   *  越权值后端会降级到白名单 IN */
  status?: number;
  /** 多个 Jira / api_code，每个独立子串 ilike，OR 合并 */
  jiraIssueCodes?: string[];
  /** 多个 label，按 IN 精确匹配 */
  labels?: string[];
  /** 场景来源（jira / data_platform / collect_task），按 IN 精确匹配 */
  sources?: SceneSource[];
  /** case 来源区域: 'cn' (国内) / 'ae' (阿联酋) / 'sea' (新加坡), 按 IN 精确匹配 */
  regions?: string[];
  /** 按创建时间筛选：起始时间（含），ISO 8601 字符串 `YYYY-MM-DDTHH:mm:ss` */
  createTimeStart?: string;
  /** 按创建时间筛选：结束时间（含），ISO 8601 字符串 `YYYY-MM-DDTHH:mm:ss` */
  createTimeEnd?: string;
  /** 是否包含已禁用场景（展示页应传 true，前端按 isDeleted 置灰） */
  includeDisabled?: boolean;
}

export const getAvailableScenes = async (
  params: AvailableSceneParams,
): Promise<{ total: number; list: AvailableSceneItem[]; page?: PageInfo }> => {
  const response = await api.post("/scene/available/list", cleanParams(params));
  const { total, list, page } = parseListResponse(response.data);
  return { total, list, page };
};

/**
 * 批量添加场景到场景集
 */
/** 批量入集被拒场景：不存在 / 已禁用 / 状态不合格（仅已完成、已完成-瑕疵品可入集）。 */
export interface BatchAddRejectedScene {
  sceneId: number;
  reason: string;
}

export interface BatchAddScenesResult {
  added: number;
  /** 已在集合内，跳过 */
  skipped: number;
  /** 不合格，跳过 */
  rejected?: BatchAddRejectedScene[];
}

export const batchAddScenesToTree = async (
  treeNodeId: number, sceneIds: number[],
): Promise<BatchAddScenesResult> => {
  const response = await api.post("/scene-tree/batch-add", { treeNodeId, sceneIds });
  return response.data?.data || response.data;
};

// ─── 跨集复制 case 关联 ───────────────────────────────────────────────
/**
 * 跨场景集复制 case（多对多关系）—— 同一 case 同时属于多个场景集。
 * 源可以是叶子或目录（目录时递归取子孙叶子 case 去重）；目标必须都是叶子。
 * 可选 filter 支持按 status / labels / sources / scene_group / 创建时间过滤。
 */
export interface CopySetContentFilter {
  status?: number[];
  labels?: string[];
  sources?: string[];
  sceneGroups?: string[];
  createTimeStart?: string;
  createTimeEnd?: string;
}

export interface CopySetContentRequest {
  sourceNodeId: number;
  targetNodeIds: number[];
  filter?: CopySetContentFilter;
}

export interface CopySetContentTargetResult {
  nodeId: number;
  name?: string | null;
  added: number;
  skipped: number;
}

export interface CopySetContentResponse {
  sourceNodeId: number;
  sourceTotal: number;
  targets: CopySetContentTargetResult[];
}

export const copySceneSetContent = async (
  req: CopySetContentRequest,
): Promise<CopySetContentResponse> => {
  const response = await api.post("/scene-tree/copy-set-content", req);
  return response.data?.data || response.data;
};

/** 只算 sourceTotal 用于 Modal 实时预览"预计复制 N 个"。 */
export const previewCopySceneSetContent = async (
  req: { sourceNodeId: number; filter?: CopySetContentFilter },
): Promise<{ sourceTotal: number }> => {
  const response = await api.post("/scene-tree/copy-set-content/preview", req);
  return response.data?.data || response.data;
};
// ─────────────────────────────────────────────────────────────────────

/**
 * 批量移除场景集中的场景
 */
export const batchDeleteScenesFromTree = async (
  treeNodeId: number, sceneIds: number[],
): Promise<{ deleted: number }> => {
  const response = await api.post("/scene-tree/batch-delete", { treeNodeId, sceneIds });
  return response.data?.data || response.data;
};

/**
 * 批量禁用 / 恢复场景的接口返回，三桶互不相交、合集 == 入参 sceneIds。
 * - affected：本次实际改变了状态的 id
 * - notFound：库中不存在的 id
 * - alreadyInTargetState：目标状态已是当前状态，未做改动
 */
export interface SceneToggleResult {
  affected: number[];
  notFound: number[];
  alreadyInTargetState: number[];
  /** 硬删专用：存在但非派生场景（无 derived_from），被拒绝删除 */
  notDerived?: number[];
}

/**
 * 批量禁用场景（逻辑删除）。禁用后场景对所有面向用户的查询不可见——
 * 唯一例外是「场景生成 Job 的场景列表」（本页所在）仍展示历史关联，
 * 方便恢复误删。上限 200 个。
 */
export const disableScenes = async (sceneIds: number[]): Promise<SceneToggleResult> => {
  const response = await api.post("/scene-creation/scenes/disable", { sceneIds });
  return response.data?.data || response.data;
};

/**
 * 批量恢复被禁用的场景，回到正常可见状态。上限 200 个。
 */
export const restoreScenes = async (sceneIds: number[]): Promise<SceneToggleResult> => {
  const response = await api.post("/scene-creation/scenes/restore", { sceneIds });
  return response.data?.data || response.data;
};

/**
 * 硬删除「另存为」派生场景（物理删 DB 行 + 树关联 + BOS 文件，不可恢复）。
 * 仅删派生场景（external.derived_from 存在）；非派生 id 归入 notDerived 被拒绝。
 * 区别于 disableScenes 的逻辑软删（可 restore）。
 */
export const deleteDerivedScenes = async (sceneIds: number[]): Promise<SceneToggleResult> => {
  const response = await api.post("/scene-creation/scenes/delete-derived", { sceneIds });
  return response.data?.data || response.data;
};

/**
 * 数据密级（保密）——批量设置场景密级。
 * level=0 取消保密（公开），>0 受控（仅 data:confidential:view 可见）。
 * 需 data:confidential:manage 权限。
 */
export const setScenesSecurityLevel = async (
  sceneIds: number[], level: number,
): Promise<{ affected: number[] }> => {
  const response = await api.post("/scene-creation/scenes/security-level", { sceneIds, level });
  return response.data?.data || response.data;
};

/**
 * 数据密级（保密）——设置场景集/目录节点密级（默认级联 stamp 子树全部场景）。
 * 需 data:confidential:manage 权限。
 */
export const setTreeNodeSecurityLevel = async (
  nodeId: number, level: number, stampScenes = true,
): Promise<{ nodeId: number; level: number; stampedScenes: number }> => {
  const response = await api.post("/scene-tree/security-level", { nodeId, level, stampScenes });
  return response.data?.data || response.data;
};

/**
 * 数据密级（保密）——设置 Job 密级（同步 stamp 其全部 task）。
 * 需 data:confidential:manage 权限。
 */
export const setJobSecurityLevel = async (
  jobId: number, level: number,
): Promise<{ jobId: number; level: number; stampedTasks: number }> => {
  const response = await api.post("/job/security-level", { jobId, level });
  return response.data?.data || response.data;
};

/**
 * 清空场景集（叶子节点）下的全部场景关联。
 * 不删除节点本身，也不删除 scene_inspection 实体。
 */
export const clearSceneTreeNode = async (
  treeNodeId: number,
): Promise<{ deleted: number }> => {
  const response = await api.post(`/scene-tree/clear/${treeNodeId}`);
  return response.data?.data || response.data;
};

// ============================================================
// 场景标签（标签树）
// ============================================================

export const clearSceneTag = async (
  tagId: number,
): Promise<{ deleted: number }> => {
  const response = await api.post(`/scene-tag/clear/${tagId}`);
  return response.data?.data || response.data;
};

/**
 * 标签树节点。
 */
export interface SceneTagNode {
  id: number;
  parentId: number;
  name: string;
  nodeFlag: number; // 0=目录, 1=标签叶子
  tagPath: string;
  tagKey?: string | null; // 英文唯一键
  alias?: string | null; // 中文别名
  description?: string | null; // 标签描述
  creator?: string | null;
  createTime?: string | null;
  children?: SceneTagNode[];
}

/** 标签展示名:中文别名优先,没有别名退回英文名(标签名以英文唯一键为准)。 */
export const tagDisplayName = (t: { name: string; alias?: string | null }): string =>
  (t.alias && t.alias.trim()) ? t.alias : t.name;

/**
 * 获取整棵标签树。
 */
export const getSceneTagTree = async (): Promise<SceneTagNode[]> => {
  const response = await api.get("/scene-tag/tree");
  return response.data?.data || [];
};

/**
 * 标签详情（元信息 + 场景数）。
 */
export interface SceneTagDetail {
  id: number;
  parentId: number;
  name: string;
  nodeFlag: number;
  tagPath: string;
  tagKey?: string | null;
  alias?: string | null;
  description?: string | null;
  creator?: string | null;
  createTime?: string | null;
  sceneCount: number;
}

export const getSceneTagDetail = async (tagId: number): Promise<SceneTagDetail> => {
  const response = await api.get(`/scene-tag/detail/${tagId}`);
  return response.data?.data || response.data;
};

/**
 * 标签下的场景条目（并集）。
 */
export interface SceneTagSceneItem {
  id: number;
  name?: string | null;
  apiCode?: string | null;
  label?: string | null;
  source?: string | null;
  status?: number | null;
  statusName?: string | null;
  createTime?: string | null;
  creator?: string | null;
}

/**
 * 分页查询标签下的场景列表（并集）。
 */
export const getSceneTagScenes = async (
  tagIds: number[],
  current = 1,
  pageSize = 50,
): Promise<{ total: number; list: SceneTagSceneItem[] }> => {
  if (!tagIds?.length) return { total: 0, list: [] };
  const response = await api.get("/scene-tag/list", {
    params: { tagIds: tagIds.join(','), current, pageSize },
  });
  const page = response.data?.page || {};
  return {
    total: page.totalElements ?? 0,
    list: response.data?.data || [],
  };
};

/**
 * 场景详情页：该场景已挂的全部叶子标签（含英文键/别名/创建信息）。
 */
export const getSceneTagsOfScene = async (sceneId: number): Promise<SceneTagNode[]> => {
  const response = await api.get(`/scene-tag/of-scene/${sceneId}`);
  return response.data?.data || [];
};

/**
 * 创建目录或标签叶子。
 */
export const createSceneTag = async (params: {
  parentId: number; name?: string; nodeFlag: number;
  tagKey?: string; alias?: string; description?: string;
}): Promise<void> => {
  await api.post("/scene-tag/create", params);
};

/**
 * 编辑标签（标签名以英文唯一键为准，name 一般省略由后端取 tag_key）。
 */
export const renameSceneTag = async (
  id: number, name?: string, opts?: { tagKey?: string; alias?: string; description?: string },
): Promise<void> => {
  await api.post("/scene-tag/rename", { id, name, ...opts });
};

/**
 * 删除标签节点（含子树；有关联时后端拒绝）。
 */
export const deleteSceneTag = async (tagId: number): Promise<void> => {
  await api.delete(`/scene-tag/delete/${tagId}`);
};

/**
 * 批量添加场景到标签（已存在自动 skip）。
 */
export const batchAddScenesToTag = async (
  tagId: number,
  sceneIds: number[],
): Promise<BatchAddScenesResult> => {
  const response = await api.post("/scene-tag/batch-add", { tagId, sceneIds });
  return response.data?.data || response.data;
};

/**
 * 批量移除标签下的场景关联。
 */
export const batchDeleteScenesFromTag = async (
  tagId: number,
  sceneIds: number[],
): Promise<{ deleted: number }> => {
  const response = await api.post("/scene-tag/batch-delete", { tagId, sceneIds });
  return response.data?.data || response.data;
};

/**
 * 创建任务
 */
export const createJob = async (params: CreateJobParams): Promise<string> => {
  const response = await api.post("/job/create", params);
  return response.data?.message || response.data?.msg || "任务创建成功";
};

/** 创建 Job → bazel 编译 入参。version 由后端自动生成，前端不传。 */
export interface CreateBazelJobParams {
  taskTypeId: number;
  jobName?: string;
  priority?: number;
  // 场景来源二选一：sceneSetIds=按场景集发起；sceneIds=按场景发起（上限 100，
  // 「按场景·输入场景名」由前端反查为 id 后走此字段）
  sceneSetIds?: string;
  sceneIds?: number[];
  /** 「按任务查询/勾选」入口：后端反查或直接用勾选场景，建匿名场景集后下发 */
  byTaskQuery?: {
    jobId: number;
    filters: Record<string, unknown>;
    sceneIds?: number[];
  };
  /** 基线版本（必填） */
  baseVersion: string;
  /** 改动描述（默认 test） */
  versionDescription?: string;
  /** 编译模块列表，逗号分隔；无改动留空 */
  changeModule?: string;
  /** module:branch，逗号分隔；仓库不变留空 */
  branchDpInfo?: string;
  /** 高级选项：重复运行次数 1-10，默认 1；N>1 时后端循环创建并给 jobName 加 _#i/N 后缀 */
  repeatTimes?: number;
  /** 顶层 params：透传到每个 dataset.params 供下游 DAG 分流用（如场景质检的 mode 开关）。系统固定字段（simulation_task_id/scene_name/map_*）不可覆盖。 */
  params?: Record<string, string>;
  /** 用户选择的自定义参数；模型值为完整发布版本名。 */
  customParams?: JobCustomParams;
  /** 「一键填入」选择的预设 key（如 10.10 / 10.9）。选择自定义版本时**不传**；
   *  传了则后端把它作为 version 号最前面一段前缀，形如
   *  ``<presetKey>.<YYYYMMDD>.simulation.<env>.<userId>.<seq>``。 */
  presetKey?: string;
  /** 数据更新（PR_DATA）页专用：完整预测数据版本串（口径同 CreateJobParams.predictionVersion）。 */
  predictionVersion?: string;
  /** 数据更新（PR_DATA）页专用：兼容字段，短版本号（口径同 CreateJobParams.dataVersion）。 */
  dataVersion?: string;
  /** 数据更新（PR_DATA）页专用：已有场景处理策略（口径同 CreateJobParams.fillMode）。 */
  fillMode?: 'skip' | 'overwrite';
  /** LPL（PNC 仿真）页专用：预测数据版本（非必填，口径同 CreateJobParams）。 */
  predictionDataVersion?: string;
  /** 感知数据刷写（PE_DATA）页专用：版本 tag（非必填，口径同 CreateJobParams.perceptionTag）。 */
  perceptionTag?: string;
}

/** GET /job/daily-tags-existing 返回:已实际使用过的 daily tag 列表,倒序 */
export const getDailyTagsExisting = async (): Promise<string[]> => {
  const response = await api.get<{ data: string[] }>('/job/daily-tags-existing');
  return response.data.data || [];
};

/** GET /job/daily/versions?tag=&limit= 单条返回 */
export interface DailyVersionItem {
  jobId: number;
  versionNo: string;
  base: string | null;
  baseVersion: string | null;
  createTime: string | null;
  /** Job 当前状态(JobStatusEnum.id):7=编译中,35=编译失败,其余 ≥ pending 视为编译成功 */
  status: number | null;
  /** Job 当前状态中文名(JobStatusEnum.desc)*/
  statusName: string | null;
}

/** GET /job/daily/versions:按 tag 拉历史 daily 版本,createTime DESC。
 *
 *  用途:CreateJobModal「基于 daily 版本」radio 下的 Tag 下拉 + 历史版本列表联动;
 *  用户选 tag → 拉近 N 条历史 daily job → 点一行 → 回填 baseVersion,提交时仍走
 *  `createBazelJob` (POST /job/bazel/create) 发起 bazel 编译。
 *
 *  详见 docs/daily-tag-and-build-option-refactor.md §4.1 / §4.5。
 */
export const getDailyVersionsByTag = async (
  tag: string,
  limit: number = 50,
): Promise<DailyVersionItem[]> => {
  const response = await api.get<{ data: DailyVersionItem[] }>(
    '/job/daily/versions', { params: { tag, limit } },
  );
  return response.data.data || [];
};

/** 创建 Job → bazel 编译 返回。 */
export interface BazelJobResult {
  jobId: number;
  version: string;
  status: number;
  workflowName: string;
  workflowUrl: string;
  workflowKey: string;
  imageVersion: string;
}

/**
 * 创建 Job 并发起 bazel 编译（编译优先）。
 * 后端自动生成 version、入库置「编译中」、不下发；编译回调成功后才下发调度。
 */
export const createBazelJob = async (
  params: CreateBazelJobParams,
): Promise<BazelJobResult> => {
  const response = await api.post("/job/bazel/create", params);
  return response.data?.data as BazelJobResult;
};

/** 使用已保存的编译输入重试原 Job；不重新提交 commit 或编译参数。 */
export const retryBazelJob = async (jobId: number): Promise<BazelJobResult> => {
  const response = await api.post("/job/bazel/retry", null, { params: { jobId } });
  return response.data?.data as BazelJobResult;
};

/**
 * 查 bazel 编译 job 最近一次的 Argo workflow 信息（用于「查看 Argo Workflow」按钮）。
 * 非 bazel 编译 job / 无记录时后端报错，由全局拦截器提示。
 */
export const getBazelWorkflow = async (
  jobId: number,
): Promise<BazelJobResult> => {
  const response = await api.get("/job/bazel/workflow", { params: { jobId } });
  return response.data?.data as BazelJobResult;
};

/**
 * 算法版本「基准版本」递归下钻：从 version 逐层追溯各仓 commit / 分支 / 基线。
 * 单次最多返回 3 层；末层 hasMore 为真时以其 baseVersionNorm 续查（「每屏 3 层、可续翻」）。
 */
export const getVersionTrace = async (version: string): Promise<VersionTrace> => {
  const response = await api.get("/job/version-trace", { params: { version } });
  return response.data?.data as VersionTrace;
};

/**
 * 算法版本「追到底」汇总：从 version 一路追到最原始平台版本 + 全部仓库变化项。
 * 用于弹窗顶部「最原始版本 + 变化项」概览，与逐层下钻互补。
 */
export const getVersionTraceRoot = async (version: string): Promise<VersionTraceRoot> => {
  const response = await api.get("/job/version-trace/root", { params: { version } });
  return response.data?.data as VersionTraceRoot;
};

/**
 * 停止任务
 */
export const cancelJob = async (jobId: number): Promise<string> => {
  const response = await api.delete("/job/cancel", { params: { jobId } });
  return response.data?.message || response.data?.msg || "任务停止成功";
};

/**
 * 取消任务
 */
export const deleteJob = async (jobId: number): Promise<string> => {
  const response = await api.delete("/job/delete", { params: { jobId } });
  return response.data?.message || response.data?.msg || "任务取消成功";
};

/**
 * 软删除 Job：仅创建者、仅终态。
 * 与 deleteJob（=取消，通知调度平台）互补：只做展示层隐藏，不动状态机。
 */
export const softDeleteJob = async (jobId: number): Promise<void> => {
  await api.post(`/job/${jobId}/soft-delete`);
};

/**
 * 收藏 / 取消收藏 Job（幂等）。
 */
export const setJobFavorite = async (jobId: number, favorite: boolean): Promise<void> => {
  if (favorite) {
    await api.post(`/job/${jobId}/favorite`);
  } else {
    await api.delete(`/job/${jobId}/favorite`);
  }
};

/**
 * 隔离任务：调度器保留任务现场与日志，可通过解除隔离恢复。
 */
export const isolateJob = async (jobId: number): Promise<string> => {
  const response = await api.post("/job/isolate", null, { params: { jobId } });
  return response.data?.message || response.data?.msg || "任务已隔离";
};

/**
 * 解除任务隔离
 */
export const unisolateJob = async (jobId: number): Promise<string> => {
  const response = await api.delete("/job/isolate", { params: { jobId } });
  return response.data?.message || response.data?.msg || "已解除隔离";
};

/**
 * 手动刷新单个 Job 的状态。
 * 后端会拉取调度平台最新结果同步本地 Job/Task 状态，
 * 受 per-job Redis 锁保护可放心重复点击。
 */
export interface RefreshJobStatusResult {
  refreshed: boolean;
  reason?: string;
  job_status?: number;
  task_summary?: {
    total: number;
    completed_tasks: number;
    failed_tasks: number;
  };
}

export const refreshJobStatus = async (
  jobId: number
): Promise<RefreshJobStatusResult> => {
  const response = await api.post("/job/refresh-status", null, {
    params: { jobId },
  });
  return response.data?.data ?? {};
};

/**
 * 获取任务列表
 */
export const getJobList = async (
  params: JobListParams
): Promise<JobListResponse> => {
  const response = await api.get("/job/list", {
    params: cleanParams(params),
    // jobIds 是数组：axios 默认会序列化成 jobIds[]=1&jobIds[]=2（带方括号），
    // FastAPI List[int] 只认重复 key jobIds=1&jobIds=2，故这里显式用
    // URLSearchParams 逐个 append（数组展开成重复 key，标量原样）。
    paramsSerializer: (p: Record<string, unknown>) => {
      const sp = new URLSearchParams();
      Object.entries(p).forEach(([k, v]) => {
        if (Array.isArray(v)) {
          v.forEach((item) => sp.append(k, String(item)));
        } else if (v !== undefined && v !== null) {
          sp.append(k, String(v));
        }
      });
      return sp.toString();
    },
  });
  const { total, list, page } = parseListResponse(response.data);
  return {
    total,
    tasks: list,
    page,
  };
};

/**
 * 获取「场景质检」子链路 Job 列表（task_type_name='SCENE_QC'）。
 *
 * 主 /job/list 会强制 enable_flag=1 过滤，SCENE_QC 的 enable_flag=0 拿不到，
 * 因此走独立后端接口 GET /scene-qc/jobs 反查。返回结构与 /job/list 对齐，
 * 复用 parseListResponse。
 */
export const getSceneQcJobs = async (
  params: JobListParams
): Promise<JobListResponse> => {
  const response = await api.get("/scene-qc/jobs", {
    params: cleanParams(params),
  });
  const { total, list, page } = parseListResponse(response.data);
  return {
    total,
    tasks: list,
    page,
  };
};

/**
 * 拿 SCENE_QC 任务类型详情（含 id，用于创建 Job 时填 taskTypeId）。
 *
 * 场景质检子页面面向所有登录用户，走独立后端接口 `GET /scene-qc/task-type`
 * （不复用 `/task-type/list`，那条挂 require_admin，普通用户 403）。
 * 后端未 seed 时抛业务错误，前端捕获后返回 null，据此禁用「创建任务」提交按钮。
 */
export const getSceneQcTaskType = async (): Promise<TaskTypeDetail | null> => {
  try {
    const response = await api.get("/scene-qc/task-type");
    const raw = response.data?.data;
    if (!raw) return null;
    // 后端 alias 出 camelCase 字段；沿用 TaskTypeDetail 结构直接返回
    return raw as TaskTypeDetail;
  } catch (e) {
    // 未 seed / 权限异常等 → 返回 null，Modal 会 disable 提交按钮
    return null;
  }
};

/**
 * 只取 SCENE_QC 的 id（宽松版）：seed 未跑时返回 null，不抛错。
 * 用于「场景质检 · 创建任务」Modal 拿隐藏 taskTypeId；页面标题永远硬编码
 * "场景质检 (SCENE_QC)"，不依赖此接口。
 */
export const getSceneQcTaskTypeId = async (): Promise<number | null> => {
  try {
    const response = await api.get("/scene-qc/task-type-id");
    const id = response.data?.data?.id;
    return typeof id === "number" ? id : null;
  } catch (e) {
    return null;
  }
};

/**
 * 场景质检创建 Modal 的「版本号」默认值 = 后台配置的 generate-scene package_url。
 * 走独立接口 `/scene-qc/default-package-url`（不挂 admin），
 * 后端从 system_config 透传 currentValue。失败时返回空串，不阻塞打开表单。
 */
export const getSceneQcDefaultPackageUrl = async (): Promise<string> => {
  try {
    const response = await api.get("/scene-qc/default-package-url");
    const v = response.data?.data?.currentValue;
    return typeof v === "string" ? v : "";
  } catch (e) {
    return "";
  }
};

// ================= 数据更新（PR_DATA） =================
// 照场景质检整套 API 复制为独立一套（不复用），走 /prediction-update/* 后端接口。

/**
 * 拉取数据更新（PR_DATA）Job 列表。
 *
 * 主 /job/list 会强制 enable_flag=1 过滤，PR_DATA 的 enable_flag=0 拿不到，
 * 因此走独立后端接口 GET /prediction-update/jobs 反查。返回结构与 /job/list
 * 对齐，复用 parseListResponse。
 */
export const getPredictionUpdateJobs = async (
  params: JobListParams
): Promise<JobListResponse> => {
  const response = await api.get("/prediction-update/jobs", {
    params: cleanParams(params),
  });
  const { total, list, page } = parseListResponse(response.data);
  return {
    total,
    tasks: list,
    page,
  };
};

/**
 * 只取 PR_DATA 的 id + name（宽松版）：seed 未跑时返回 {id:null, name:null}，不抛错。
 * 用于「数据更新 · 创建任务」Modal 拿隐藏 taskTypeId，并用 name（task_type 中文名，
 * 如"数据更新"）作为默认 jobName 前缀；页面标题永远硬编码 "数据更新 (PR_DATA)"，
 * 不依赖此接口。
 */
export const getPredictionUpdateTaskTypeId = async (): Promise<{
  id: number | null;
  name: string | null;
}> => {
  try {
    const response = await api.get("/prediction-update/task-type-id");
    const data = response.data?.data ?? {};
    return {
      id: typeof data.id === "number" ? data.id : null,
      name: typeof data.name === "string" ? data.name : null,
    };
  } catch (e) {
    return { id: null, name: null };
  }
};

/**
 * 数据更新创建 Modal 的「版本号」默认值 = 后台配置的 generate-scene package_url。
 * 走独立接口 `/prediction-update/default-package-url`（不挂 admin），
 * 后端从 system_config 透传 currentValue。失败时返回空串，不阻塞打开表单。
 */
export const getPredictionUpdateDefaultPackageUrl = async (): Promise<string> => {
  try {
    const response = await api.get("/prediction-update/default-package-url");
    const v = response.data?.data?.currentValue;
    return typeof v === "string" ? v : "";
  } catch (e) {
    return "";
  }
};

/**
 * 数据更新任务列表某 task 的迭代状态。
 * - status: 'succeeded'（有产出版本行）/ 'failed'（迭代失败，见 reason）；
 * - version: 成功时的完整预测版本号串，失败为 null；
 * - reason: 失败原因（status==='failed' 时非空），前端 hover 展示。
 */
export interface PredictionIterState {
  version?: string | null;
  status: "succeeded" | "failed";
  reason?: string | null;
}

/**
 * 数据更新任务列表「迭代状态 / 版本号」：取某 PR_DATA Job 下 {taskId: 迭代状态对象}。
 *
 * **独立旁挂接口**，不改通用 /task/list —— 数据更新任务列表页仅当当前 job 是 PR_DATA 时
 * 才额外调本接口，取每个 task 的成功/失败态 + 版本号 + 失败原因；非 PR_DATA job 不调，
 * 通用任务列表零变化。返回对象键为字符串 taskId，前端按 String(taskId) 取。结果里没有的
 * taskId 表示「既没成功也没失败记录」，前端据 task.status 推「进行中 / 未迭代」。
 * 失败返回空对象，不阻塞任务列表加载。
 */
export const getPredictionUpdateTaskIterations = async (
  jobId: number
): Promise<Record<string, PredictionIterState>> => {
  try {
    const response = await api.get("/prediction-update/task-iterations", {
      params: { jobId },
    });
    const data = response.data?.data;
    return data && typeof data === "object"
      ? (data as Record<string, PredictionIterState>)
      : {};
  } catch (e) {
    return {};
  }
};

/**
 * Job 列表页顶部覆盖率卡片：一个迭代版本（tag 下的一条 header 批）的双覆盖率。
 * - daily 覆盖率 = dailyCovered / dailyTotal（该版本覆盖场景 / daily 全量回归集）。
 * - 来源覆盖率 = sourceCovered / sourceTotal（该版本覆盖场景里 case_flow+jira /
 *   daily 全集里 case_flow+jira，合计一个百分比）。
 */
export interface TagVersionCoverage {
  predictionVersion: string;
  /** 是否该 tag 当前生效版本（只由人工「设为当前版本」设定，批终态不自动滚动）。 */
  isCurrent: boolean;
  /** 该版本已覆盖场景数（detail 现算，>0 才可设为当前 / 有数据可补跑）。 */
  coveredScenes: number;
  dailyCovered: number;
  dailyTotal: number;
  sourceCovered: number;
  sourceTotal: number;
  jobId?: number | null;
  createTime?: string | null;
}

/** 一个 tag（daily tag）下的迭代版本覆盖率列表（header.id 倒序）。 */
export interface TagCoverage {
  groupTag: string;
  versions: TagVersionCoverage[];
}

/**
 * Job 列表页顶部覆盖率卡片数据源：按 tag 展示各迭代版本 + 双覆盖率。
 *
 * 只读、fail-soft：后端 service 异常 / 表未建 → 回 []，前端降级不渲染卡片、不影响下方列表。
 */
export const getPredictionTagCoverage = async (): Promise<TagCoverage[]> => {
  try {
    const response = await api.get("/prediction-update/tag-coverage");
    const data = response.data?.data;
    return Array.isArray(data) ? (data as TagCoverage[]) : [];
  } catch (e) {
    return [];
  }
};

/** 删除迭代返回的清理计数。 */
export interface DeleteIterationResult {
  deletedRows: number;
  skippedRows: number;
  bosObjects: number;
}

/**
 * 删除一次迭代（= 该 Job 产出的一个版本）的全部预测数据：清 PG 记录 + BOS 文件。
 * 版本号不回退。仅 PR_DATA 任务、且 Job 终态后可删（后端校验）。
 */
export const deletePredictionIteration = async (
  jobId: number,
): Promise<DeleteIterationResult> => {
  const response = await api.delete("/prediction-update/iteration", {
    params: { jobId },
  });
  return (response.data?.data ?? {
    deletedRows: 0,
    skippedRows: 0,
    bosObjects: 0,
  }) as DeleteIterationResult;
};

// ── 版本管理（按版本号操作）+ 补跑缺失场景 ──────────────────────────────────
// 与上面「按 job 删除迭代」互补：补跑会把新 job 的数据补进**同一个版本号**，
// 按 job 删只删得掉补跑那部分 —— 故「设为当前 / 删除 / 补跑」这一组按版本号收口。

/** 「设为当前版本」结果：changed=false 表示该版本已是当前（幂等返回，未做变更）。 */
export interface SetCurrentVersionResult {
  changed: boolean;
  groupTag: string;
  predictionVersion: string;
  /** 切换前的当前版本；未变更 / 之前无当前版本时为 null。 */
  previousVersion?: string | null;
  coveredScenes: number;
}

/**
 * 把指定版本设为该 tag 的**当前生效版本**（dispatch 基线 / 对外「取最新版本」随之指向它）。
 * 用于补跑补齐后把更完整的版本提为当前，也支持回退到更早的完整版本。
 * 非法（版本不存在 / 仍在迭代 / 无数据）后端抛业务错误，走 api 层统一提示。
 */
export const setPredictionCurrentVersion = async (
  predictionVersion: string,
): Promise<SetCurrentVersionResult> => {
  const response = await api.post("/prediction-update/version/current", {
    predictionVersion,
  });
  return (response.data?.data ?? {
    changed: false,
    groupTag: "",
    predictionVersion,
    previousVersion: null,
    coveredScenes: 0,
  }) as SetCurrentVersionResult;
};

/** 「删除版本」的清理计数。 */
export interface DeleteVersionResult {
  predictionVersion: string;
  /** 已删除的预测数据行数。 */
  deletedRows: number;
  /** BOS 删失败被跳过的行数（>0 时该行 DB 记录保留、避免 BOS 孤儿，可重试删除）。 */
  skippedRows: number;
  /** 已清理的 BOS 对象数。 */
  bosObjects: number;
  /** 被删版本原本是否当前生效版本（删后该 tag 暂无当前版本，需人工重新指定）。 */
  wasCurrent: boolean;
}

/**
 * 删除一个版本：硬删该版本全部预测数据行 + 删 BOS 目录 + 删版本 header，版本号不回退。
 * 与 deletePredictionIteration（按 job 删）的区别：补跑补进同版本的数据也一并清掉。
 * 删掉的若是当前版本，后端**不会**自动回指——该 tag 删后暂无当前版本，需人工重新指定。
 */
export const deletePredictionVersion = async (
  predictionVersion: string,
): Promise<DeleteVersionResult> => {
  const response = await api.delete("/prediction-update/version", {
    params: { predictionVersion },
  });
  return (response.data?.data ?? {
    predictionVersion,
    deletedRows: 0,
    skippedRows: 0,
    bosObjects: 0,
    wasCurrent: false,
  }) as DeleteVersionResult;
};

// ── 预测数据版本列表（数据更新页顶部总览）────────────────────────────────────
// 与上面「按 tag 展开的迭代覆盖率卡片」互补：这里以**数据版本**（算法包版本）为行，
// 一行一个版本的成功率 / 覆盖场景 / 是否在创建任务处展示，并保留按版本号的
// 「设为当前 / 删除版本」操作（复用 /prediction-update/version/current + version）。

/**
 * 数据更新页顶部「预测数据版本」列表的一行。
 *
 * - successRate：成功率 0..1（= succeeded / total，后端算好）；
 * - listed：是否进入默认列表（成功率 ≥90% 自动进入 / 手动加入）；
 *   includeAll=false 时后端只回 listed 版本；
 * - manuallyAdded：来源（自动 ≥90% / 人工手动加入）；
 * - predictionVersion / groupTag / isCurrent / coveredScenes / latestJobId：
 *   关联的迭代版本信息，缺失（老数据 / 尚未产出批）时该行禁用「设为当前 / 删除版本」。
 */
export interface PredictionDataVersionItem {
  /** 完整预测数据版本串——行键（prediction.AD3.1.3 / prediction.orin_dev.{时间戳}）。 */
  predictionVersion: string;
  /** 数据版本元信息（AD3.1.3 / release.10.12.34 / orin_dev）。 */
  dataVersion?: string | null;
  description?: string | null;
  /** 是否在「创建任务」处的版本下拉中展示。 */
  showInCreateJob: boolean;
  /** 是否人工手动加入（false = 成功率 ≥90% 自动进入）。 */
  manuallyAdded: boolean;
  /** 是否进入默认列表（includeAll=false 时的口径）。 */
  listed: boolean;
  /** 成功率 0..1。 */
  successRate: number;
  succeeded: number;
  total: number;
  /** 该版本已覆盖的场景数。 */
  coveredScenes: number;
  jobCount: number;
  latestJobId?: number | null;
  groupTag?: string | null;
  isCurrent?: boolean;
  createTime?: string | null;
}

/**
 * 预测数据版本列表。includeAll=false 只回默认列表（成功率 ≥90% 自动进入 + 手动加入），
 * true 额外带出未达 90% 的版本，便于手动加入后看到全量分布。
 */
export const getPredictionDataVersions = async (
  includeAll = false,
): Promise<PredictionDataVersionItem[]> => {
  const response = await api.get("/prediction-update/data-versions", {
    params: { includeAll },
  });
  const data = response.data?.data;
  return Array.isArray(data) ? (data as PredictionDataVersionItem[]) : [];
};

/** 手动加入预测数据版本的请求体。 */
export interface AddPredictionDataVersionPayload {
  /** 完整版本串；只传 dataVersion（AD3.1.3）时后端自动补 prediction. 前缀。 */
  predictionVersion: string;
  description?: string;
}

/** 手动加入一个预测数据版本（版本号必填；成功后返回该行完整信息）。 */
export const addPredictionDataVersion = async (
  payload: AddPredictionDataVersionPayload,
): Promise<PredictionDataVersionItem> => {
  const response = await api.post("/prediction-update/data-versions", payload);
  const data = response.data?.data;
  if (!data || typeof data !== "object") {
    throw new Error("手动加入版本失败：响应缺少数据");
  }
  return data as PredictionDataVersionItem;
};

/** 修改预测数据版本的请求体（描述 / 是否在创建任务处展示，均为可选）。 */
export interface UpdatePredictionDataVersionPayload {
  description?: string;
  showInCreateJob?: boolean;
}

/** 修改版本描述或「创建任务展示」开关，返回更新后的行。 */
export const updatePredictionDataVersion = async (
  predictionVersion: string,
  payload: UpdatePredictionDataVersionPayload,
): Promise<PredictionDataVersionItem> => {
  const response = await api.patch(
    `/prediction-update/data-versions/${encodeURIComponent(predictionVersion)}`,
    payload,
  );
  const data = response.data?.data;
  if (!data || typeof data !== "object") {
    throw new Error("更新版本信息失败：响应缺少数据");
  }
  return data as PredictionDataVersionItem;
};

/** 移除结果：只摘列表展示，预测数据保留。 */
export interface RemovePredictionDataVersionResult {
  predictionVersion: string;
  removed: boolean;
}

/**
 * 把一个版本**从列表移除**：只摘版本管理列表 / 创建任务下拉里的展示，不删任何预测
 * 数据（PG 行 / BOS 目录 / header / 当前生效标记均保留）。之后可在「手动加入」里
 * 重新加入，原有数据随即恢复显示。
 *
 * 与 [[deletePredictionVersion]]（硬删除）相对；自动收录（成功率 ≥90%）也会被压制，
 * 不会下次刷新又自动出现。
 */
export const removePredictionDataVersion = async (
  predictionVersion: string,
): Promise<RemovePredictionDataVersionResult> => {
  const response = await api.delete(
    `/prediction-update/data-versions/${encodeURIComponent(predictionVersion)}`,
  );
  const data = response.data?.data;
  return (data ?? { predictionVersion, removed: true }) as RemovePredictionDataVersionResult;
};

/** 「手动加入」弹窗的版本号候选项（完整版本串 + 默认描述）。 */
export interface PredictionDataVersionCreateOption {
  /** 完整预测数据版本串（prediction.AD3.1.3 / prediction.orin_dev.20260807123451）。 */
  predictionVersion: string;
  dataVersion?: string | null;
  description?: string | null;
  /** 是否已有产出（header 存在）。false = 仅目录登记，选它将以该串新建版本。 */
  hasHeader?: boolean;
  /** 版本内已覆盖的场景数（去重 scene_id）。 */
  coveredScenes?: number;
  /** 版本 header 记录的场景总数。 */
  sceneTotal?: number;
  /** 是否为「当前生效版本」。 */
  isCurrent?: boolean;
}

/** 「手动加入」弹窗候选版本号（fail-soft：接口异常回 []，弹窗仍可手填）。 */
export const getPredictionDataVersionCreateOptions = async (): Promise<
  PredictionDataVersionCreateOption[]
> => {
  try {
    const response = await api.get("/prediction-update/data-versions/create-options");
    const data = response.data?.data;
    return Array.isArray(data) ? (data as PredictionDataVersionCreateOption[]) : [];
  } catch {
    return [];
  }
};

/** 某 Job 版本的场景缺口（「还缺哪些场景」）。 */
export interface PredictionVersionMissingScenes {
  jobId: number;
  /** 该 Job 产出的版本号；Job 不存在 / 无版本号时为 null。 */
  predictionVersion?: string | null;
  groupTag?: string | null;
  /** 该 Job 的场景总数（task 分区表 distinct scene_id，带 partition_time）。 */
  totalScenes: number;
  /** 该版本已覆盖场景数（prediction_scene_data 现算）。 */
  coveredScenes: number;
  missingCount: number;
  missingSceneIds: number[];
  missingSceneNames: string[];
}

/**
 * 某 Job 的版本「还缺哪些场景」：该 Job 的场景集 − 该版本已覆盖场景。
 *
 * 口径 = 预测数据缺口的唯一权威定义（不看 task.status）：任务失败、任务成功但迭代失败
 * （引擎没传 Prediction 分片 / copy 失败）都表现为「版本里没这个场景」，一并算缺口。
 * 用于「补跑缺失场景」入口的前置判断。
 */
export const getPredictionVersionMissingScenes = async (
  jobId: number,
): Promise<PredictionVersionMissingScenes> => {
  const response = await api.get("/prediction-update/version/missing", {
    params: { jobId },
  });
  const data = response.data?.data;
  // 不兜底造默认值：默认 missingCount=0 会让前端误报「无需补跑」，宁可直接报错。
  if (!data || typeof data !== "object") {
    throw new Error("查询版本缺失场景失败：响应缺少数据");
  }
  return data as PredictionVersionMissingScenes;
};

/** 「补跑缺失场景」结果：created=false 表示判定无缺口（或已被补齐），**不会**新建 Job。 */
export interface RetryMissingScenesResult {
  created: boolean;
  /** 新建的补跑 Job ID；created=false 时为 null。 */
  jobId?: number | null;
  dispatchJobId?: string | null;
  predictionVersion?: string | null;
  groupTag?: string | null;
  missingCount: number;
  totalScenes: number;
  coveredScenes: number;
  /** 结果说明文案（发起成功 / 无需补跑的原因），直接用于 message 展示。 */
  message: string;
}

/**
 * 一键补跑：用源 Job 里「版本缺失的场景」新建一个 Job，产出**补进源 Job 的同一版本**
 * （不重跑全量、不开新版本号）。源 Job 需 PR_DATA + 已终态 + 有产出版本 + 有建单快照；
 * 无缺口时后端不空跑（created=false）。``jobName`` 不传由后端用「源名-补跑」。
 */
export const retryPredictionMissingScenes = async (
  jobId: number,
  jobName?: string,
): Promise<RetryMissingScenesResult> => {
  const response = await api.post("/prediction-update/retry-missing", null, {
    params: { jobId, ...(jobName ? { jobName } : {}) },
  });
  const data = response.data?.data;
  if (!data || typeof data !== "object") {
    throw new Error("补跑缺失场景失败：响应缺少数据");
  }
  return data as RetryMissingScenesResult;
};

/**
 * 获取优先级列表
 */
export const getPriorityList = async (): Promise<PriorityOption[]> => {
  const response = await api.get("/job/priority");
  return mapOptions(response.data?.data || response.data);
};

/**
 * 更新 Job 优先级(调用后端 → 转发 CF)。
 * 仅在 job 状态处于「未开始/排队中/编译中」时可用,前端已按此门禁,后端不校验。
 */
export const updateJobPriority = async (params: {
  jobId: number;
  priority: number;
}): Promise<void> => {
  await api.post("/job/update/priority", params);
};

/**
 * 获取 Job 状态列表（后端 JobStatusEnum：含 compiling/compileFailed，不含 coredump）。
 */
export const getStatusList = async (): Promise<StatusOption[]> => {
  const response = await api.get("/job/status");
  return mapOptions(response.data?.data || response.data);
};

/**
 * 获取 Task 状态列表（后端 TaskStatusEnum：含 coredump，不含 job 专属的编译态）。
 * 任务列表/任务对比的状态筛选用这个，不要复用 Job 的 getStatusList。
 */
export const getTaskStatusList = async (): Promise<StatusOption[]> => {
  const response = await api.get("/task/status");
  return mapOptions(response.data?.data || response.data);
};

/**
 * 获取场景状态列表(后端 SceneStatusEnum:ENTRYING/QUALITY_CHECKING/
 * COMPLETED_DEFECTIVE/COMPLETED/INVALIDATED)。
 *
 * 前端筛选/复制弹窗用这个,不要再硬编码状态列表——新增枚举(如 2026-08-31
 * 补的 COMPLETED_DEFECTIVE=7)时会自动同步,避免多处漏改。
 */
export const getSceneStatusList = async (): Promise<StatusOption[]> => {
  const response = await api.get("/scene/status");
  return mapOptions(response.data?.data || response.data);
};

/**
 * 获取数据源（仿真来源）下拉列表。
 * 后端 JobSourceEnum：1=日常仿真 / 2=daily 仿真 / 3=cicd 仿真。
 */
export const getJobSourceList = async (): Promise<StatusOption[]> => {
  const response = await api.get("/job/sources");
  return mapOptions(response.data?.data || response.data);
};

/**
 * 获取任务日志
 */
export const getJobLogs = async (jobId: number): Promise<string> => {
  const response = await api.get("/job/logs", { params: { jobId } });
  return response.data?.data || "";
};

/** 编译日志返回体（后端 ``BuildLogVo``）：文本 + 可下载链接。 */
export interface JobBuildLog {
  /** 接口拉取的日志文本 */
  logContent: string;
  /** 完整日志下载链接（前端做成「查看完整日志」入口） */
  logDownloadUrl?: string | null;
  /** 日志 S3 路径（排查用） */
  logS3Path?: string | null;
}

/**
 * 获取编译任务的编译日志（build_serial 的 dispatch task 日志）。
 * 非编译任务 / 尚未编译时后端返回空文本 + 空链接。
 */
export const getJobBuildLogs = async (jobId: number): Promise<JobBuildLog> => {
  const response = await api.get("/job/build-logs", { params: { jobId } });
  const data = response.data?.data || {};
  return {
    logContent: data.logContent || "",
    logDownloadUrl: data.logDownloadUrl ?? null,
    logS3Path: data.logS3Path ?? null,
  };
};

/**
 * 获取「创建 job → bazel 编译」任务的编译日志 URL（来自编译平台回调 buildLog 字段）。
 * 找不到记录或字段为空时返回 null。
 */
export const getJobBazelBuildLog = async (jobId: number): Promise<string | null> => {
  const response = await api.get("/job/bazel-build-log", { params: { jobId } });
  const data = response.data?.data || {};
  const url = data.buildLog;
  return typeof url === "string" && url.trim() ? url : null;
};

// ========== Daily 仿真 ==========

/** Daily Job 基础信息（后端 ``DailyJobBriefVo``） */
export interface DailyJobBrief {
  jobId: number;
  jobName: string | null;
  status: number;
  statusName: string;
  createTime: string | null;
  endTime: string | null;
  creator: string | null;
  algorithmVersion: string | null;
  totalTaskNum: number;
  successTaskNum: number;
  failTaskNum: number;
}

/** Daily 最新两个 Job */
export interface DailyLatestJobs {
  today: DailyJobBrief | null;
  compare: DailyJobBrief | null;
}

/** 单 Job 的通过率/异常率指标
 *
 * 2026-05 扩展：携带「任务对比」基本信息（sceneSetIds / algorithmVersion / mileage 等），
 * 兼容旧调用方（DailySummaryCard 仍仅用 passRate/anomalyRate/newSuccessSceneCount 等子集）。
 */
export interface DailyJobMetrics {
  jobId: number;
  jobName: string | null;
  total: number;
  passCount: number;
  failCount: number;
  anomalyCount: number;
  /** 通过率（百分比，0-100） */
  passRate: number;
  /** 异常率（千分比，‰） */
  anomalyRate: number;
  /** Job 累计运行里程（**单位：米**，与数据库 raw 值一致），按 task 去重后再求和；无数据时为 0 */
  mileage: number;
  /** 场景集合（逗号分隔的场景树节点 ID），前端需配合 sceneTree 解析为路径名 */
  sceneSetIds?: string | null;
  algorithmVersion?: string | null;
  buildType?: string | null;
  creator?: string | null;
  createTime?: string | null;
  endTime?: string | null;
  /** 任务类型名（Job.task_type_name 透传） */
  taskTypeName?: string | null;
  /** 归属仿真类型（关联 task_type.sim_type）；对比双方必然一致（后端前置校验保证） */
  simType?: SimType | null;
  /** 「忽略 check 不通过场景」开关下被剔除的 task 数（已不计入 pass/fail/anomaly/total）；
   *  0 = 开关关或本 Job 没有 check 失败 task。前端按此显示提示。 */
  excludedCheckFailedCount?: number;
  /** 「忽略 check 不通过场景」开关当前状态（true=已打开剔除）。count 为 0 时无法区分
   *  「关」与「开但无失败 task」，故单独回传布尔给对比页绑定双方开关。 */
  excludeCheckFailed?: boolean;
}

/** 两个 Job 的对比结果
 *
 * 交叉矩阵字段命名 `<compare>_<today>`（对比/历史在前、基准/今日在后）：
 * - `passPass` = 两侧都通过
 * - `passFail` = 对比通过、基准失败/异常（= 当前新增失败）
 * - `failPass` = 对比失败/异常、基准通过（= 当前新增通过）
 * - `failFail` = 两侧都失败/异常
 * 仅统计两 Job 共有的 scene_id（``intersectionSceneCount``）。
 */
export interface DailyCompare {
  today: DailyJobMetrics;
  compare: DailyJobMetrics;
  newSuccessSceneCount: number;
  newFailSceneCount: number;
  passPass: number;
  passFail: number;
  failPass: number;
  failFail: number;
  intersectionSceneCount: number;
  /**
   * 当前（今日）Job 内存在重复（>1 条 task）的 scene 数。矩阵/对比按「每场景最新
   * task」口径计算，>0 时前端提示「当前 Job 存在重复场景、对比按最新 task 计算」。
   */
  todayDuplicateSceneCount?: number;
}

/** Daily Job 列表查询参数（沿用 JobListParams，不含 source 字段；simType 复用父类型） */
export type DailyJobListParams = Omit<JobListParams, "source">;

/** 查询 daily 仿真最新两个 Job（按 jobId 倒序）。simType 传入时按 task_type.sim_type 过滤 */
export const getDailyLatestJobs = async (
  simType?: SimType,
): Promise<DailyLatestJobs> => {
  const response = await api.get("/daily/latest-jobs", {
    params: simType ? { simType } : undefined,
  });
  return (
    response.data?.data || { today: null, compare: null }
  );
};

/** 对比两个 Job 的通过率/异常率，并计算新增成功/失败场景数 */
export const compareDailyJobs = async (
  todayJobId: number,
  compareJobId: number,
): Promise<DailyCompare | null> => {
  const response = await api.get("/daily/compare", {
    params: { todayJobId, compareJobId },
  });
  return response.data?.data || null;
};

export interface SceneSetCompareMetrics {
  total: number;
  passCount: number;
  passRate: number | null;
}

/** 父级含全部后代，内部按 sceneId 去重；四象限顺序为对比 → 当前。 */
export interface SceneSetCompareNode {
  nodeId: number;
  name: string;
  today: SceneSetCompareMetrics;
  compare: SceneSetCompareMetrics;
  intersectionSceneCount: number;
  todayOnlySceneCount: number;
  compareOnlySceneCount: number;
  unclassifiedSceneCount: number;
  passPass: number;
  passFail: number;
  failPass: number;
  failFail: number;
  children: SceneSetCompareNode[];
}

export const compareDailyJobsSceneSets = async (
  todayJobId: number,
  compareJobId: number,
  signal?: AbortSignal,
): Promise<SceneSetCompareNode[]> => {
  // 此折叠区自行展示失败/重试；取消过期请求时也不弹全局错误提示。
  const config = { params: { todayJobId, compareJobId }, signal, _skipErrorHandler: true };
  const response = await api.get('/daily/compare/scene-sets', config);
  const nodes = response.data?.data?.nodes;
  if (!Array.isArray(nodes)) throw new Error('场景集对比数据加载失败');
  return nodes;
};

export type SceneSetVerdict = 'pass' | 'fail' | 'error' | 'pending';

export interface JobSceneSetNode {
  nodeId: number;
  name: string;
  total: number;
  passCount: number;
  failCount: number;
  errorCount: number;
  pendingCount: number;
  passRate: number | null;
  children: JobSceneSetNode[];
}

/** 单 Job 按场景集逐级统计（口径同对比页场景集）。折叠区自行展示失败/重试，故跳过全局错误提示。 */
export const getJobSceneSets = async (jobId: number, signal?: AbortSignal): Promise<JobSceneSetNode[]> => {
  const config = { signal, _skipErrorHandler: true };
  const response = await api.get(`/job/${jobId}/scene-sets`, config);
  const nodes = response.data?.data?.nodes;
  if (!Array.isArray(nodes)) throw new Error('场景集统计数据加载失败');
  return nodes;
};

/** 单个评测器在两 Job 间的 2×2 pass/fail 矩阵行
 *
 * 字段命名 `<对比>_<基准>`（对比/历史在前、基准/今日在后），与 `DailyCompare`
 * 整体矩阵一致：
 * - `passPass` = 两侧该评测器都 Success
 * - `passFail` = 对比 Success、基准 Failed（= 当前新增失败）
 * - `failPass` = 对比 Failed、基准 Success（= 当前新增通过）
 * - `sceneCount` = 四象限之和（双方都给出可判定结果的共有场景数），前端按横向 100% 算占比
 */
export interface EvaluatorMatrixRow {
  evaluatorName: string;
  sceneCount: number;
  passPass: number;
  passFail: number;
  failPass: number;
  failFail: number;
}

/** 某侧独有（对比侧改名/缺失/新增）的评测器及其本侧失败场景数 */
export interface EvaluatorCoverageItem {
  evaluatorName: string;
  /** 该评测器在本侧、双方共有场景上按矩阵口径判为 fail 的场景数 */
  failCount: number;
}

/** 两 Job 评测器集覆盖情况（仅 type=1，已按「忽略 check」开关剔除场景）。
 *  base_only/compare_only = 本侧有、对侧无同名项的评测器（改名/拆分/新增），
 *  这些在矩阵里因对侧 _side_verdict 返回 None 被整格丢弃，故其本侧失败不进任何象限——
 *  正是「场景级 Pass·Fail N、评测器 Σ Pass·Fail <N」的差额来源。 */
export interface EvaluatorMatrixCoverage {
  baseEvaluatorCount: number;
  compareEvaluatorCount: number;
  commonEvaluatorCount: number;
  baseOnly: EvaluatorCoverageItem[];
  compareOnly: EvaluatorCoverageItem[];
}

/** 评测器矩阵每象限「可匹配」的**去重场景数**（cell_map 各象限 scene_id 并集长度）。
 *  区别于行内 passFail（评测器×场景对数）。前端据此与场景级矩阵对账：
 *  场景级 passFail = matchedSceneCounts.passFail + 无可比场景数。 */
export interface EvaluatorMatrixMatchedCounts {
  passPass: number;
  passFail: number;
  failPass: number;
  failFail: number;
}

/** 两 Job「按评测器聚合」的 2×2 矩阵
 *
 * `intersectionSceneCount` 与 `DailyCompare.intersectionSceneCount` 同义
 * （两 Job 共有 scene_id 总数），作前端「该评测器场景数占总场景比例」的分母。
 * `coverage` / `matchedSceneCounts` 用于解释「评测器矩阵数 < 场景级矩阵数」的差额。
 */
export interface DailyCompareEvaluatorMatrix {
  intersectionSceneCount: number;
  evaluators: EvaluatorMatrixRow[];
  coverage?: EvaluatorMatrixCoverage | null;
  matchedSceneCounts?: EvaluatorMatrixMatchedCounts | null;
}

/** 按 evaluator_name 聚合两 Job 的 2×2 pass/fail 矩阵 */
export const compareDailyJobsEvaluatorMatrix = async (
  todayJobId: number,
  compareJobId: number,
): Promise<DailyCompareEvaluatorMatrix | null> => {
  const response = await api.get("/daily/compare/evaluator-matrix", {
    params: { todayJobId, compareJobId },
  });
  return response.data?.data || null;
};

/** 评测器量化指标定义（后端动态汇总，来自评估器上报的 evaluate_info.metrics）。 */
export interface EvaluatorMetricDef {
  key: string;
  label: string;
  unit?: string;
  higherIsWorse?: boolean | null;
}

/**
 * 动态拉取某评测器在两 Job 上实际上报过的量化指标清单（key + 中文 label + 方向）。
 * 纯结构化：评估器新增/改名指标即自动反映，前端零改动。该评测器未上报 metrics 时返回 []。
 */
export const compareDailyJobsEvaluatorMetrics = async (
  todayJobId: number,
  compareJobId: number,
  evaluatorName: string,
): Promise<EvaluatorMetricDef[]> => {
  const response = await api.get("/daily/compare/evaluator-metrics", {
    params: { todayJobId, compareJobId, evaluatorName },
  });
  return response.data?.data || [];
};

/** 某指标在两 Job 共有场景全集上的方向分布计数（后端 metric_direction_counts）。 */
export interface MetricDirectionCounts {
  /** 当前任务 > 对比任务的场景数 */
  increase: number;
  /** 当前任务 < 对比任务的场景数 */
  decrease: number;
  /** 两侧相等（持平）的场景数 */
  equal: number;
  /** 两侧都取到数值的共有场景数（= increase + decrease + equal） */
  total: number;
}

/**
 * 拉取某评测器某量化指标在两 Job 共有场景上的「增大/缩小/持平」计数。
 * 供指标下钻「选中即出计数、点计数即按方向筛选」。口径为两 Job 共有场景全集，
 * 与列表的 metricKey/metricCompare 筛选一致（未叠加其它筛选时，点某计数筛出行数 == 该计数）。
 */
export const compareDailyJobsMetricDirectionCounts = async (
  todayJobId: number,
  compareJobId: number,
  evaluatorName: string,
  metricKey: string,
): Promise<MetricDirectionCounts> => {
  const response = await api.get("/daily/compare/metric-direction-counts", {
    params: { todayJobId, compareJobId, evaluatorName, metricKey },
  });
  return response.data?.data || { increase: 0, decrease: 0, equal: 0, total: 0 };
};

/**
 * 单 Job 版 ``/daily/compare/evaluator-metrics``：某评测器在本 Job 上实际上报过的
 * 量化指标清单（纯结构化 metrics，无前置注册表）。
 *
 * 供 ``?mode=stats``（不通过统计）页的「指标」下拉。因为指标与评估器**自描述**，
 * 普通通过型评估器一样能出指标 —— 不限于已登记 agg-spec 的评估器参数。
 * 该评测器未上报任何结构化 metrics 时返回空数组（前端应据此不渲染指标列）。
 */
export const getJobEvaluatorMetrics = async (
  jobId: number,
  evaluatorName: string,
): Promise<EvaluatorMetricDef[]> => {
  const response = await api.get("/daily/job/evaluator-metrics", {
    params: { jobId, evaluatorName },
  });
  return response.data?.data || [];
};

/**
 * 单 Job 上某评测器**实际上报过**的原因清单 ``[{kind, code, label}]``。
 *
 * 供 ``?mode=stats`` 页「失败原因 / 豁免原因」两个下拉 —— 前端按 ``kind`` 分成两组。
 * 评估器自描述、无前置注册表：算法侧新增一类原因自动出现。未上报任何原因时返回空数组
 * （前端据此把两个下拉置灰，按普通场景处理、不筛选）。
 */
export const getJobEvaluatorReasons = async (
  jobId: number,
  evaluatorName: string,
): Promise<JobEvaluatorReason[]> => {
  const response = await api.get("/daily/job/evaluator-reasons", {
    params: { jobId, evaluatorName },
  });
  return response.data?.data || [];
};

/**
 * 单 Job 上某评测器某指标的逐场景取值：``{sceneId: value}``。
 *
 * 供 ``?mode=stats`` 页「指标」列渲染 —— 一次取回整 Job 全部场景，前端按 ``sceneId``
 * 查表，**翻页不再发请求**。取不到数值的场景不出现在返回里（渲染 ``—``）。
 */
export const getJobEvaluatorMetricValues = async (
  jobId: number,
  evaluatorName: string,
  metricKey: string,
): Promise<Record<number, number>> => {
  const response = await api.get("/daily/job/evaluator-metric-values", {
    params: { jobId, evaluatorName, metricKey },
  });
  // 后端 JSON 序列化后 key 是**字符串**（`{"160808": 2.0}`）。这里显式归一成 number，
  // 与 getReasonDiff 同款 —— 消费侧 state 声明的是 Record<number, ...>，靠 JS 属性名
  // 隐式转串虽然当下能查中，但一旦有人改成 Map / 用 Object.keys 与 number 比 ===，
  // 就会静默查不到（列上一片 —）。归一化把这条隐患掐在入口。
  const raw = (response.data?.data || {}) as Record<string, number>;
  const out: Record<number, number> = {};
  Object.entries(raw).forEach(([sid, val]) => {
    const n = Number(sid);
    if (!Number.isNaN(n)) out[n] = val;
  });
  return out;
};

/** 单 Job 内单个评测器的通过/不通过/异常统计行（后端 ``JobEvaluatorStatRowVo``） */
export interface JobEvaluatorStatRow {
  evaluatorName: string;
  /** 场景数（= passCount + failCount + errorCount） */
  sceneCount: number;
  passCount: number;
  /** 真实回传失败的场景数（不含异常终态） */
  failCount: number;
  /** 异常终态（执行异常/系统异常/coredump）场景数 */
  errorCount: number;
}

/** 单 Job「按评测器聚合」明细（后端 ``JobEvaluatorStatVo``） */
export interface JobEvaluatorStatData {
  /** 本 Job 场景数（占比分母） */
  totalSceneCount: number;
  evaluators: JobEvaluatorStatRow[];
}

/**
 * 单 Job「按评测器聚合」的通过/不通过/异常明细（单侧版评测器矩阵）。
 * pass/fail 口径与 `/daily/compare/evaluator-matrix` 单侧一致，唯独把异常终态
 * （执行异常/系统异常/coredump）从 fail 拆出、单列为 errorCount。
 */
export const getJobEvaluatorStats = async (
  jobId: number,
): Promise<JobEvaluatorStatData | null> => {
  const response = await api.get("/daily/job/evaluator-stats", {
    params: { jobId },
  });
  return response.data?.data || null;
};

// ---- 统计型评估器·指标聚合（评估器参数卡片）----

/** 已登记聚合声明的评估器摘要（卡片点选器候选）。 */
export interface EvaluatorMetricAggSpec {
  /** 评测器英文 name（= 请求聚合时的 evaluator 参数） */
  evaluator: string;
  /** 卡片展示名（spec.label 优先，回退中文名映射） */
  label: string;
  /** 是否声明了派生率（有则卡片总体区展示率行） */
  hasRates: boolean;
  /** 求和计数键数量 */
  sumKeyCount: number;
}

/** 聚合 KPI 单项（∑计数键 或 派生率）。 */
export interface EvaluatorMetricAggSummaryItem {
  key: string;
  label: string;
  unit?: string;
  /** ∑den==0 的派生率 → null（前端渲染 —） */
  value: number | null;
  higherIsWorse?: boolean | null;
  /** spec.hide_in_table 命中：只在 KPI 卡里隐藏，图表与派生率仍用它 */
  hidden?: boolean;
}

/** 逐场景明细行：3B 分布键缺席则不在 metrics 里出现（前端渲染 —）。 */
export interface EvaluatorMetricAggSceneRow {
  sceneId: number;
  sceneName: string;
  /** 是否触发（trigger_key>0）；false = 未触发空场（如 iLQR valid_frames==0） */
  triggered: boolean;
  metrics: Record<string, number>;
}

/** spec.charts 声明（prefix 已由后端展开成 seriesKeys；数据取同响应的 summary）。 */
export interface EvaluatorMetricAggChart {
  id: string;
  title: string;
  /** donut 环形占比 / bar 分类柱状 */
  type: "donut" | "bar";
  /** 要画的 summary key，顺序 = spec 书写顺序 */
  seriesKeys: string[];
  /** donut 占比分母："sum"（各项合计，恒 100%）或 "key:<sum_key>" */
  percentBase: string;
}

/** 单 Job 聚合结果（后端 single_job_metric_agg）。 */
export interface JobEvaluatorMetricAgg {
  evaluator: string;
  label: string;
  summary: EvaluatorMetricAggSummaryItem[];
  scenes: EvaluatorMetricAggSceneRow[];
  sceneCount: number;
  triggeredCount: number;
  /** spec 未声明 charts 时为空数组 */
  charts?: EvaluatorMetricAggChart[];
}

/** 两 Job 聚合对比单项。 */
export interface CompareMetricAggSummaryItem {
  key: string;
  label: string;
  unit?: string;
  value: number | null;
  baseValue: number | null;
  /** value - baseValue；任一侧缺失 → null */
  delta: number | null;
  /** increase / decrease / equal；任一侧缺失 → null */
  direction: "increase" | "decrease" | "equal" | null;
  higherIsWorse?: boolean | null;
  /** spec.hide_in_table 命中：只在对比表格里隐藏，对比图仍用它 */
  hidden?: boolean;
}

/** 两 Job 聚合结果（后端 compare_metric_agg）。 */
export interface CompareEvaluatorMetricAgg {
  evaluator: string;
  label: string;
  summary: CompareMetricAggSummaryItem[];
  /** spec 未声明 charts 时为空数组；同单 Job 的 charts */
  charts?: EvaluatorMetricAggChart[];
}

/**
 * 已登记聚合声明的评估器列表（评估器参数卡片点选器候选）。
 * 未登记的评估器不出现在此列表 = 不进卡片、也不从 pass/fail 表拆出。
 */
export const getEvaluatorMetricAggSpecs = async (): Promise<EvaluatorMetricAggSpec[]> => {
  const response = await api.get("/daily/evaluator/metric-agg-specs");
  return response.data?.data || [];
};

/**
 * 单 Job：某统计型评估器的跨场景聚合 KPI + 逐场景明细。
 *
 * ``withScenes=false`` 时后端 `scenes` 恒为空数组（sum_keys 多时逐场景明细是
 * payload 大头）：卡片首屏只要 summary + charts，用户展开「逐场景明细」再带
 * ``withScenes=true`` 重取。默认 true 保持向后兼容。
 */
export const getJobEvaluatorMetricAgg = async (
  jobId: number,
  evaluator: string,
  withScenes: boolean = true,
): Promise<JobEvaluatorMetricAgg | null> => {
  const response = await api.get("/daily/job/evaluator-metric-agg", {
    params: { jobId, evaluator, withScenes },
  });
  return response.data?.data || null;
};

/** 两 Job：某统计型评估器聚合 KPI 的当前/对比/差值/方向。 */
export const compareJobEvaluatorMetricAgg = async (
  jobId: number,
  baseJobId: number,
  evaluator: string,
): Promise<CompareEvaluatorMetricAgg | null> => {
  const response = await api.get("/daily/compare/evaluator-metric-agg", {
    params: { jobId, baseJobId, evaluator },
  });
  return response.data?.data || null;
};

/**
 * 评测器英文 name → 中文展示名映射（单一数据源在后端）。
 * 前端启动时拉取一次缓存，展示/筛选 label 用；未登记的 name 回退展示英文。
 */
export const getEvaluatorNameMap = async (): Promise<Record<string, string>> => {
  const response = await api.get("/evaluation/evaluator/name-map");
  return response.data?.data || {};
};

/** 评测器英文 name → 飞书文档链接（仅含已配置项；后台「EVALUATOR · 白名单」维护）。 */
export const getEvaluatorDocMap = async (): Promise<Record<string, string>> => {
  const response = await api.get("/evaluation/evaluator/doc-map");
  return response.data?.data || {};
};

/**
 * VRU 责任碰撞评估器失败场景的相对速度分档统计(单 job)。
 * 供「不通过统计」页在评测器聚合明细与任务明细中间嵌入的饼图。
 * 5 桶: 0-5 / 5-10 / 10-15 / 15-20 / 20+ (m/s,左闭右开)。
 * summary 提供均值/最大值/样本数,饼图侧栏 KPI 展示。
 */
export interface VruSpeedBucket {
  bucket: string;
  minSpeed: number;
  maxSpeed: number | null;
  count: number;
}
export interface VruSpeedSummary {
  total: number;
  avgSpeed: number | null;
  maxSpeed: number | null;
}
export interface VruSpeedDistResp {
  buckets: VruSpeedBucket[];
  summary: VruSpeedSummary;
}
export const getVruRelativeSpeedDist = async (jobId: number): Promise<VruSpeedDistResp> => {
  const response = await api.get("/evaluation/evaluator/vru-relative-speed-dist", {
    params: { jobId },
  });
  return response.data?.data || { buckets: [], summary: { total: 0, avgSpeed: null, maxSpeed: null } };
};

/**
 * 整场景一致性判定 `{sceneId: 'same'|'diff'|'only_current'}`，供对比明细「差异」列展示。
 * 与 `/task/list?diffConsistency=` 的筛选同源（后端共用一份判定逻辑），一次性拉取后缓存，
 * 列直接读取，避免列与筛选口径漂移。JSON key 为字符串，这里转回 number 索引。
 */
export const getCompareConsistency = async (
  todayJobId: number,
  compareJobId: number,
): Promise<Record<number, CompareConsistencyVerdict>> => {
  const response = await api.get("/daily/compare/consistency", {
    params: { todayJobId, compareJobId },
  });
  const raw = (response.data?.data || {}) as Record<string, CompareConsistencyVerdict>;
  const out: Record<number, CompareConsistencyVerdict> = {};
  Object.entries(raw).forEach(([sid, verdict]) => {
    const n = Number(sid);
    if (!Number.isNaN(n)) out[n] = verdict;
  });
  return out;
};

/**
 * 对比页「原因」列判定 + 两侧原始原因：`{sceneId: {verdict, today, compare}}`。
 *
 * 比的是**指定评测器**上报的 fail 失败原因 / pass 豁免原因（`evaluate_info.reason`）。
 * 判定与 `/task/list?reasonDiff=` 的筛选同源（后端共用 `_reason_scene_map`），
 * 故「列上标着的判定」与「筛出来的行」恒等，不可能漂移。
 *
 * 一次请求覆盖整 Job 场景（翻页不再发请求）；`today`/`compare` 一并返回，故 label /
 * kind 直接可得，不必再去逐 task 的评测明细缓存里捞。
 *
 * 算法侧尚未上报 `reason` 时 today/compare 两侧均为 null，后端判双方键集相等（空 dict）→ `same`。
 * v3 起 reason 可为元素数组、``kind`` 参与键、次数相同且 >1 时做二分图时空匹配。
 * 调用方依据 reasonColumnHasData（见 index.tsx）决定整列是否走空态，不会出现一列空白。
 */
export const getReasonDiff = async (
  todayJobId: number,
  compareJobId: number,
  evaluatorName: string,
): Promise<Record<number, ReasonDiffCell>> => {
  const response = await api.get("/daily/reason-diff", {
    params: { todayJobId, compareJobId, evaluatorName },
  });
  const raw = (response.data?.data || {}) as Record<string, ReasonDiffCell | null>;
  const out: Record<number, ReasonDiffCell> = {};
  Object.entries(raw).forEach(([sid, cell]) => {
    const n = Number(sid);
    // 空 cell 直接丢：下游按「有 cell 就有 verdict」读，不为数据未到位的脏值兜底。
    if (!Number.isNaN(n) && cell) out[n] = cell;
  });
  return out;
};

/** 对比页首屏一次拉全:指标 + 评测器矩阵 + 一致性判定
 *
 * 合并 `/daily/compare` + `/daily/compare/evaluator-matrix` +
 * `/daily/compare/consistency` 为一次请求。前端首屏 3 次 RTT 变 1 次,
 * 后端共享 request-scoped reader 缓存,3× 相同 SQL 触发压到 1×。
 *
 * 老 3 个端点保留兼容。前端主流程用本接口;单点场景(仅需其中之一)保留老 API。
 */
export interface DailyCompareBundle {
  compare: DailyCompare;
  evaluatorMatrix: DailyCompareEvaluatorMatrix;
  consistency: Record<number, CompareConsistencyVerdict>;
}

export const getCompareBundle = async (
  todayJobId: number,
  compareJobId: number,
): Promise<DailyCompareBundle | null> => {
  const response = await api.get("/daily/compare/bundle", {
    params: { todayJobId, compareJobId },
  });
  const data = response.data?.data;
  if (!data) return null;
  const rawCons = (data.consistency || {}) as Record<string, CompareConsistencyVerdict>;
  const consistency: Record<number, CompareConsistencyVerdict> = {};
  Object.entries(rawCons).forEach(([sid, verdict]) => {
    const n = Number(sid);
    if (!Number.isNaN(n)) consistency[n] = verdict;
  });
  return {
    compare: data.compare,
    evaluatorMatrix: data.evaluatorMatrix,
    consistency,
  };
};

/** 查询 daily 仿真 Job 列表（强制 source=daily 仿真） */
export const getDailyJobList = async (
  params: DailyJobListParams,
): Promise<JobListResponse> => {
  const response = await api.get("/daily/job/list", {
    params: cleanParams(params),
  });
  const { total, list, page } = parseListResponse(response.data);
  return { total, tasks: list, page };
};

/**
 * 查询 daily Job 下拉候选项（按 jobId 倒序）。
 * - ``limit``：返回条数上限（后端默认 100，最大 500）
 * - ``excludeJobId``：可选，从候选项中排除某个 Job（如「今日 Job」自身）
 * - ``simType``：可选，按 task_type.sim_type 过滤
 */
export const getDailyJobOptions = async (
  params: { limit?: number; excludeJobId?: number; simType?: SimType } = {},
): Promise<DailyJobBrief[]> => {
  const response = await api.get("/daily/job/options", {
    params: cleanParams(params),
  });
  const data = response.data?.data;
  return Array.isArray(data) ? data : [];
};

/** 查询 ``jobId`` 之前最近一条 daily Job（用于展开行「对比 Job」默认值） */
export const getDailyPreviousJob = async (
  jobId: number,
  simType?: SimType,
): Promise<DailyJobBrief | null> => {
  const response = await api.get("/daily/job/previous", {
    params: simType ? { jobId, simType } : { jobId },
  });
  return response.data?.data || null;
};

// ─────────────── 感知仿真评估报表（/perception/report/*） ───────────────
//
// 与 /daily/* 的关键差异：不挂 source 闸门。/daily/* 硬过滤 source=daily，而感知
// job 的 source 是 planning_perception(4)，套那个闸门恒返回空——这正是「感知的
// Daily 后端未接入」占位的根因。这组接口只按 jobId 取数。
//
// 两条贯穿全部字段的口径，改前端时务必先读：
// 1. **所有指标字段可为 null**。分类别 precision 为配对后的类别准确率；聚合侧
//    「任一 case 缺某计数 → 依赖它的比率整体 null」的传染语义原样传到前端。
//    null 必须显示 "—" 而不是 "0.00%"——「没算」与「算出来是 0」是两件事。
// 2. **单位**：precision/recall/f1 是 0~1 小数（展示需 ×100）；所有 delta* 字段
//    已经是百分点（pp），直接加 "pp" 后缀，不要再乘 100。

/** 一组感知指标（计数 + 比率）。用于总体 / 分类别 / 单 case 单类别三种粒度。 */
export interface PerceptionMetrics {
  /** 真值目标数 */
  gt: number | null;
  /** 检测目标数 */
  det: number | null;
  tp: number | null;
  /** 第一版不算，恒 null */
  fp: number | null;
  fn: number | null;
  /** 匹配对 IoU 之和（原始累计值，供跨 case 重算 mean_iou） */
  iouSum: number | null;
  /** 0~1；分类别为配对后的类别准确率，ALL/总体为原检测精确率 */
  precision: number | null;
  /** 召回率 0~1 */
  recall: number | null;
  /** 原检测 F1，不使用类别准确率重算 */
  f1: number | null;
  /** 匹配对平均 IoU（iouSum/tp）；tp=0 时 null。计数单位 box 用这一列 */
  meanIou: number | null;
  /** 格子 IoU = tp/(tp+fp+fn)；仅计数单位 cell 有值 */
  iou?: number | null;
  /** 扩展计数（如 OCC 的 fp_unlabeled / tp_occupied）；任一 case 缺失则 null */
  extraCounts?: Record<string, number | null>;
  /** 贡献该组指标的 case 数 */
  caseCount: number | null;
  /** 该类别发生 checker 失败的 case 数；旧数据未上报时为 null */
  failedCaseCount?: number | null;
  /** 新 checker 独立统计，不等同于旧 TP；旧平台响应可缺失这些字段 */
  categoryMatched?: number | null;
  categoryCorrect?: number | null;
  categoryState?: string | null;
  categoryRuleVersion?: string | null;
  categoryRuleSignature?: string | null;
}

/** 计数单位：box = 目标框（OD），cell = BEV 格子（OCC）；其他业务原样透传 */
export type PerceptionUnit = 'box' | 'cell' | string;

/** 指定报表取哪个「业务 / 检查项」的指标块；不传 = od / perception_recall_check */
export interface PerceptionTarget {
  task: string;
  checker: string;
}

/** 分类别指标行（表 2）。``category`` 是真值里的原始类型字符串，``ALL`` 为合计行。
 *  ``categoryCn`` 由后端随行下发（单一数据源 utils.evaluator_names.PERCEPTION_CATEGORY_CN），
 *  未登记的类别回退英文原文——标注侧随时可能出新类型，前端不再另存一份映射。 */
export interface PerceptionCategoryMetrics extends PerceptionMetrics {
  category: string;
  categoryCn: string | null;
}

/** 有效 case 漏斗，逐级单调不增。 */
export interface PerceptionFunnel {
  /** job 中放入的场景数 */
  submitted: number;
  /** 回传了 perception_metrics 的 */
  withMetrics: number;
  /** 其中真值确实加载成功的（gt_coverage.loaded） */
  gtLoaded: number;
  /** 进入两 job 交集的；单 job 视图为 null */
  compared: number | null;
}

/** 报表表头的「对比方基本信息」。 */
export interface PerceptionJobHeader {
  jobId: number;
  jobName: string | null;
  creator: string | null;
  createTime: string | null;
  /** 复用 job.algorithm_version 归一后的裸版本号——平台无独立的「感知模型版本」字段 */
  modelVersion: string | null;
  algorithmVersionRaw: string | null;
  /** 真值标注版本，从 gt_coverage.scene_id 派生 */
  gtVersion: string | null;
  /** 判据配置版本；库中无此字段，本期恒 null */
  criteriaVersion: string | null;
}

/** GET /perception/report/single 出参 */
export interface PerceptionSingleReport {
  task?: string;
  checker?: string;
  unit?: PerceptionUnit | null;
  header: PerceptionJobHeader;
  funnel: PerceptionFunnel;
  overall: PerceptionMetrics;
  byCategory: PerceptionCategoryMetrics[];
}

/** 对比视图里的单侧：表头 + 该侧漏斗 + 该侧（交集上的）总体指标 */
export interface PerceptionSideReport extends PerceptionJobHeader {
  funnel: PerceptionFunnel;
  overall: PerceptionMetrics;
}

/** Δ 行：计数为差值，比率为**百分点差**（pp）。任一侧缺失则为 null。 */
export interface PerceptionDeltaMetrics {
  gt: number | null;
  det: number | null;
  tp: number | null;
  fp: number | null;
  fn: number | null;
  /** 单位 pp */
  precision: number | null;
  /** 单位 pp */
  recall: number | null;
  /** 单位 pp */
  f1: number | null;
  /** 单位 pp */
  meanIou: number | null;
  /** 单位 pp（cell） */
  iou?: number | null;
}

/** 表 2 的分类别对比行。单侧缺该类别时对应字段为 null（不是 0）。 */
export interface PerceptionCategoryCompareRow {
  category: string;
  categoryCn: string | null;
  base: PerceptionMetrics | null;
  current: PerceptionMetrics | null;
  delta: PerceptionDeltaMetrics | null;
}

/** case 级 Δ 召回的四档档位键 */
export type PerceptionDeltaBucket =
  | 'improved'
  | 'slightlyImproved'
  | 'slightlyDegraded'
  | 'degraded';

/** case 级 Δ 召回四档计数。
 *  档位边界（左闭右开）：improved ≥+2pp、slightlyImproved [0,+2)、
 *  slightlyDegraded (-2,0)、degraded ≤-2pp。 */
export interface PerceptionDeltaBuckets {
  improved: number;
  slightlyImproved: number;
  slightlyDegraded: number;
  degraded: number;
}

/** 按 scene_group 聚合的 Δ 分档矩阵行。
 *  分组用 scene_group 而非 label：后者是逗号拼接的多值串，按整串分组不构成分区。
 *  ``__uncategorized__`` 为 NULL/空的哨兵。 */
export interface PerceptionSceneGroupDeltaRow extends PerceptionDeltaBuckets {
  sceneGroup: string;
  total: number;
}

/** 逐 case 的 Δ 召回。两侧任一算不出召回时 deltaPp / bucket 为 null。 */
export interface PerceptionCaseDelta {
  sceneId: number;
  /** 如 LPL0000292689 */
  caseId: string | null;
  /** 如 AIP-414194 */
  apiCode: string | null;
  /** 场景标签原文（逗号拼接） */
  label: string | null;
  sceneGroup: string | null;
  source: string | null;
  baseRecall: number | null;
  currentRecall: number | null;
  /** 单位 pp */
  deltaPp: number | null;
  bucket: PerceptionDeltaBucket | null;
}

/** GET /perception/report/compare 出参。
 *  所有对比数值**只在两侧 case 的交集上计算**；单侧缺失的 case 反映在各自
 *  funnel.compared 与 withMetrics 的落差里。 */
export interface PerceptionCompareReport {
  task?: string;
  checker?: string;
  unit?: PerceptionUnit | null;
  base: PerceptionSideReport;
  current: PerceptionSideReport;
  delta: PerceptionDeltaMetrics;
  byCategory: PerceptionCategoryCompareRow[];
  deltaBuckets: PerceptionDeltaBuckets;
  bySceneGroup: PerceptionSceneGroupDeltaRow[];
  caseDeltas: PerceptionCaseDelta[];
}

/** 表 3：单 case × 类别明细行。 */
export interface PerceptionCaseDetailRow extends PerceptionMetrics {
  sceneId: number;
  /** FOXGLOVE / mcap / 日志的对象 key 都是 ``<taskId>/...`` 前缀，缺了无法下钻 */
  taskId: number | null;
  caseId: string | null;
  apiCode: string | null;
  label: string | null;
  sceneGroup: string | null;
  source: string | null;
  category: string;
  categoryCn: string | null;
  /** 该 case 指标块的计数单位 */
  unit?: PerceptionUnit | null;
  /** 相对基线同 case 同类别的 Δ 召回率，单位 pp */
  deltaRecall: number | null;
}

/** 检查项类型：metrics = 指标型（有分类别指标块），judgement = 判定型（只有通过与否） */
export type PerceptionCheckerKind = 'metrics' | 'judgement';

/** 检查项总览的「主要指标」：ALL 行跨 case 重算的比率（0~1 小数） */
export interface PerceptionCheckerHeadline {
  /** 判定型检查项的展示文案（如“不通过的 case 见详情”/“全部 case 通过”） */
  message?: string | null;
  colorConsistency?: number | null;
  alignedFrameRatio?: number | null;
  recall: number | null;
  precision: number | null;
  f1: number | null;
  /** cell 单位的 IoU */
  iou: number | null;
  /** box 单位的平均 IoU */
  meanIou: number | null;
  gt: number | null;
  tp: number | null;
  /**
   * 仅 ``occ / occ_benchmark_check`` 且有 ``extras.halo`` 时有值：带 halo 数据的 case 数，
   * 以及下面 halo 口径的主要指标（见 ``OccHaloHeadline``）。其余检查项为 null / 缺省。
   */
  haloCases?: number | null;
  miou?: number | null;
  occupancyIou?: number | null;
  macroRayiou?: number | null;
  letrayF1lFront?: number | null;
  godF1?: number | null;
  godMf1?: number | null;
  // OCC FP checker（人工 ROI）总览口径：Σ失败帧 / Σ有效帧
  occFailFrames?: number | null;
  occEligibleFrames?: number | null;
  /** Σ失败帧 / Σ有效帧；全部 case 均无有效帧时为 null */
  occFailRate?: number | null;
  /** Job 内单 case 最长连续失败帧数的最大值 */
  occMaxConsecutiveFail?: number | null;
  /** OCC FP checker 各状态 case 数（通过率 = ok / (ok + error)） */
  occOkCases?: number | null;
  occWarningCases?: number | null;
  occErrorCases?: number | null;
  occSkipCases?: number | null;
}

/** 基线 Job 上同一检查项的同口径汇总 */
export interface PerceptionCheckerBaseSummary {
  runCases: number;
  passCases: number;
  failCases: number;
  passRate: number | null;
  headline: PerceptionCheckerHeadline | null;
}

/** 检查项总览的一行。运行 = 名单里有 / 有结果；通过 = 运行 − 上报失败 */
export interface PerceptionCheckerSummary extends PerceptionCheckerBaseSummary {
  task: string;
  checker: string;
  checkerCn: string | null;
  kind: PerceptionCheckerKind;
  unit: PerceptionUnit | null;
  /** 回传了该检查项指标块的场景数 */
  withMetrics: number;
  /** 其中该业务真值加载成功的 */
  gtLoaded: number;
  /** 选了基线 Job 时有值；基线没跑该检查项为 null */
  base: PerceptionCheckerBaseSummary | null;
}

/** 一个业务页签 */
export interface PerceptionTaskOverview {
  task: string;
  taskCn: string | null;
  submitted: number;
  /** 该业务任一检查项运行过的场景数 */
  runCases: number;
  withMetrics: number;
  gtLoaded: number;
  checkers: PerceptionCheckerSummary[];
}

/** GET /perception/report/overview 出参：业务 → 检查项总览 */
export interface PerceptionOverview {
  jobId: number;
  baseJobId: number | null;
  submitted: number;
  tasks: PerceptionTaskOverview[];
}

/** GET /perception/report/checker-cases 的一行 */

export interface OdFpfnMetrics {
  annotationFrameCount: number | null;
  eligibleFrameCount: number | null;
  alignedFrameCount: number | null;
  eligibleAlignedFrameCount: number | null;
  matchedFrameCount: number | null;
  missedFrameCount: number | null;
  targetCount: number | null;
  eligibleTargetCount: number | null;
  problemTargetCount: number | null;
  frameAlignmentRatio: number | null;
  matchedFrameRatio: number | null;
  missedFrameRatio: number | null;
}

export interface OdFpfnCase {
  sceneId: number;
  taskId: number;
  caseId: string | null;
  apiCode: string | null;
  source: string | null;
  sourceKind: string | null;
  mode: string | null;
  topic: string | null;
  metrics: OdFpfnMetrics;
  failReasonCounts: Record<string, number>;
  /** checker 结果：false = 该 case 判定不通过 */
  passed: boolean;
}

export interface OdFpfnReport {
  available: boolean;
  task: string;
  checker: string;
  header: PerceptionJobHeader;
  metrics: OdFpfnMetrics;
  withMetrics: number;
  fpfnCases: number;
  validCases: number | null;
  runCases: number | null;
  failCases: number | null;
  failReasonCounts: Record<string, number>;
  cases: OdFpfnCase[];
}

export const getPerceptionOdFpfn = async (
  jobId: number,
  target: { task: string; checker: string },
): Promise<OdFpfnReport | null> => {
  const response = await api.get('/perception/report/od-fpfn', {
    params: { jobId, task: target.task, checker: target.checker },
  });
  return response.data?.data || null;
};

/** OCC FP checker 一个失败帧的俯视证据图 */
export interface OccEvidenceImage {
  gtFrameId: number | null;
  /** 该帧 lidar 时间戳（Unix 秒） */
  gtTs: number | null;
  /** 相对 task 输出目录，如 occ_fp/<checker>/frame_303.svg */
  path: string;
  summary: string | null;
}

export interface PerceptionCheckerCaseRow {
  sceneId: number | null;
  taskId: number | null;
  caseId: string | null;
  apiCode: string | null;
  label: string | null;
  sceneGroup: string | null;
  source: string | null;
  /** false = 有结果行判定不通过 */
  passed: boolean;
  /** false = 只在名单里、没有结果行，按「名单 − 失败」视为通过 */
  reported: boolean;
  msg: string | null;
  // ── OCC FP checker（人工 ROI）结构化指标；其他 checker 为 null ──
  /** checker 状态：ok / warning / error / skip */
  status?: string | null;
  /** false=误检 */
  direction?: string | null;
  /** 参与评测的 ROI 类别 */
  roiClasses?: string[] | null;
  /** 有效对齐帧数 */
  eligibleFrames?: number | null;
  /** 失败（误检）帧数 */
  failedFrames?: number | null;
  /** 失败帧率，0~1 小数，展示 ×100 */
  failRate?: number | null;
  /** 最长连续失败帧数 */
  maxConsecutiveFail?: number | null;
  /** 连续失败阈值 */
  consecutiveThreshold?: number | null;
  /** 失败时间段（halo 格式字符串） */
  failTimeRanges?: string[] | null;
  /** 失败帧证据图（task 输出目录内的相对路径），最多 20 张 */
  evidenceImages?: OccEvidenceImage[] | null;
  /** 该 case GT 的起始时间戳（Unix 秒）；前端把失败时间段换算成相对秒 */
  gtTsMin?: number | null;
  /** halo 的场景 ID（GT source 目录名），区别于平台 sceneId */
  occSceneId?: string | null;
}

/** 业务 → 检查项总览（报表页签 + 检查项总览表）。传 baseJobId 时每个检查项附带基线汇总。 */
export const getPerceptionOverview = async (
  jobId: number,
  baseJobId?: number,
): Promise<PerceptionOverview | null> => {
  const response = await api.get("/perception/report/overview", {
    params: cleanParams({ jobId, baseJobId }),
  });
  return response.data?.data || null;
};

/** 某检查项在各运行场景上的逐 case 结果（不通过的排前面）。 */
export const getPerceptionCheckerCases = async (
  jobId: number,
  target: PerceptionTarget,
): Promise<PerceptionCheckerCaseRow[]> => {
  const response = await api.get("/perception/report/checker-cases", {
    params: { jobId, task: target.task, checker: target.checker },
  });
  const data = response.data?.data;
  return Array.isArray(data) ? data : [];
};

/** 单 job 的感知指标汇总（表 1 单行 + 表 2 分类别 + 漏斗）。 */
export const getPerceptionSingleReport = async (
  jobId: number,
  target?: PerceptionTarget,
): Promise<PerceptionSingleReport | null> => {
  const response = await api.get("/perception/report/single", {
    params: cleanParams({ jobId, task: target?.task, checker: target?.checker }),
  });
  return response.data?.data || null;
};

/** 两 job 的感知指标对比报表（只在两侧 case 交集上计算）。 */
export const getPerceptionCompareReport = async (
  jobId: number,
  baseJobId: number,
  target?: PerceptionTarget,
): Promise<PerceptionCompareReport | null> => {
  const response = await api.get("/perception/report/compare", {
    params: cleanParams({ jobId, baseJobId, task: target?.task, checker: target?.checker }),
  });
  return response.data?.data || null;
};

/** 表 3：单 case × 类别明细。传 baseJobId 时每行附带 deltaRecall。 */
export const getPerceptionCaseDetail = async (
  jobId: number,
  baseJobId?: number,
  target?: PerceptionTarget,
): Promise<PerceptionCaseDetailRow[]> => {
  const response = await api.get("/perception/report/case-detail", {
    params: cleanParams({ jobId, baseJobId, task: target?.task, checker: target?.checker }),
  });
  const data = response.data?.data;
  return Array.isArray(data) ? data : [];
};

// ─────────────── OCC Benchmark（/perception/report/occ-benchmark） ───────────────
// halo occ_benchmark 口径：各 case extras.halo 的计数先求和、再算比率（EPS=1e-6，分母 0 → 0）；
// 只有「求平均的类别集合为空」时为 null。delta 系列为 pp。

/** OCC Benchmark 主要指标（0~1 小数） */
export interface OccHaloHeadline {
  miou: number | null;
  occupancyIou: number | null;
  statusMiou: number | null;
  macroRayiou: number | null;
  /** LET-Ray F1L：前向扇区 class_agnostic */
  letrayF1lFront: number | null;
  letrayRayIouFront: number | null;
  /** LET-Ray mF1L：前向扇区 class_aware，gt>0 的类 F1L 平均 */
  letrayMf1l: number | null;
  /** Instance GOD F1@0.20：global class_aware */
  godF1: number | null;
  godRecall: number | null;
  godPrecision: number | null;
  godMf1: number | null;
}

export interface OccHaloSummary {
  cases: number;
  frames: number;
  validCellCount: number;
  evalRegion: string | null;
  standard: string | null;
  haloReference: string | null;
  timeAlignToleranceSec: number | null;
  grid: Record<string, unknown> | null;
}

export interface OccHaloHeader extends PerceptionJobHeader {
  withMetrics: number;
  haloCases: number;
}

/** I/U/G/P 计数 + 比率（语义类用 className，status 类用 status） */
export interface OccIouRow {
  className: string | null;
  status: string | null;
  iou: number;
  precision: number;
  recall: number;
  i: number;
  u: number;
  g: number;
  p: number;
  /** 是否计入 mIoU（ΣU>0） */
  inMean: boolean | null;
}

export interface OccOccupancy {
  iou: number;
  precision: number;
  recall: number;
  f1: number;
  gt: number;
  pred: number;
  i: number;
  u: number;
}

export interface OccGodRow {
  mode: string | null;
  iouThreshold: string | null;
  className: string | null;
  /** instance_iou / curb_distance */
  matchRule: string | null;
  tp: number;
  gt: number;
  pred: number;
  recall: number;
  precision: number;
  f1: number;
}

export interface OccGod {
  primaryIou: string | null;
  overview: OccGodRow[];
  thresholds: OccGodRow[];
  classes: OccGodRow[];
  mf1: number | null;
}

export interface OccRayIouClass {
  className: string;
  rayIou: number;
  gt: number;
  pred: number;
  matched: number;
  inMean: boolean;
}

export interface OccLetRayRow {
  mode: string | null;
  className: string | null;
  sector: string | null;
  validRayCount: number;
  gt: number;
  pred: number;
  matched: number;
  recall: number;
  precision: number;
  precisionL: number;
  rayIou: number;
  f1L: number;
  meanLa: number;
  falseBlockRate: number;
  missedBlockRate: number;
  meanAbsErrorM: number;
  meanSignedErrorM: number;
  falseBlock: number;
  missedBlock: number;
  depthMismatch: number;
  classMismatch: number;
  trueFree: number;
  errorHist: number[];
  errorP50Bucket: string | null;
  errorP90Bucket: string | null;
  inMean: boolean | null;
}

export interface OccSpatialCell {
  /** 1 起的 xBins 下标（base_link x，前向） */
  x: number;
  /** 1 起的 yBins 下标 */
  y: number;
  valid: number;
  occupancyIou: number | null;
  semanticMiou: number | null;
  rayiou: number | null;
  godF1Agnostic: number | null;
  godF1Aware: number | null;
}

export interface OccBenchmarkCase {
  sceneId: number;
  taskId: number | null;
  caseId: string | null;
  apiCode: string | null;
  label: string | null;
  sceneGroup: string | null;
  source: string | null;
  frames: number;
  validCellCount: number;
  metrics: OccHaloHeadline;
  baseMetrics: OccHaloHeadline | null;
}

export interface OccBenchmarkCompare {
  base: OccHaloHeader;
  comparedCases: number;
  currentHeadline: OccHaloHeadline | null;
  baseHeadline: OccHaloHeadline | null;
  /** 单位 pp */
  delta: OccHaloHeadline | null;
}

/** GET /perception/report/occ-benchmark 出参。available=false 表示没有 halo 数据 */
export interface OccBenchmarkReport {
  available: boolean;
  task: string | null;
  checker: string | null;
  header: OccHaloHeader;
  summary: OccHaloSummary;
  headline: OccHaloHeadline;
  occupancy: OccOccupancy;
  status: { miou: number | null; classes: OccIouRow[] };
  fusion: { miou: number | null; semanticClasses: OccIouRow[]; god: OccGod };
  vision: {
    macroRayiou: number | null;
    rayiouClasses: OccRayIouClass[];
    letraySector: string | null;
    letrayOverview: OccLetRayRow[];
    letrayClasses: OccLetRayRow[];
    mf1l: number | null;
    sectors: OccLetRayRow[];
    errorBuckets: string[];
  };
  spatial: { xBins: string[]; yBins: string[]; cells: OccSpatialCell[] };
  cases: OccBenchmarkCase[];
  compare: OccBenchmarkCompare | null;
}

/** OCC Benchmark 报表（halo occ_benchmark 口径）。传 baseJobId 时附 compare。 */
export const getPerceptionOccBenchmark = async (
  jobId: number,
  baseJobId?: number,
  target?: PerceptionTarget,
): Promise<OccBenchmarkReport | null> => {
  const response = await api.get("/perception/report/occ-benchmark", {
    params: cleanParams({ jobId, baseJobId, task: target?.task, checker: target?.checker }),
  });
  return response.data?.data || null;
};

/**
 * 获取Task列表
 */
export interface TldMetrics {
  colorConsistency: number | null;
  alignedFrameRatio: number | null;
  blinkAgreement: number | null;
  countdownAgreement: number | null;
  blinkSame: number | null;
  blinkTotal: number | null;
  countdownSame: number | null;
  countdownTotal: number | null;
  colorMatchLights: number | null;
  comparedLights: number | null;
  matchedMessages: number | null;
  keptMessages: number | null;
  tldMessages: number | null;
  emptyMessages: number | null;
  filteredOutMessages: number | null;
  gtFrames: number | null;
  matchedGtFrames: number | null;
}

export interface TldCriteria {
  minColorAccuracy: number;
  minMatchedFrameRatio: number;
  timeToleranceSec: number;
  countdownToleranceSec: number;
}

export interface TldCase {
  sceneId: number;
  taskId: number;
  caseId: string | null;
  apiCode: string | null;
  source: string | null;
  passed: boolean;
  advisory: boolean | null;
  criteria: TldCriteria | null;
  legacy: boolean;
  messages: string[];
  metrics: TldMetrics;
  baseMetrics: TldMetrics | null;
  delta: TldMetrics;
}

export interface TldStateReport {
  available: boolean;
  advisory: boolean | null;
  criteria: TldCriteria | null;
  header: PerceptionJobHeader;
  metrics: TldMetrics;
  withMetrics: number;
  validCases: number | null;
  legacyCases: number;
  byDirection: { direction: string; metrics: TldMetrics }[];
  mismatches: Record<string, number | null> | null;
  cases: TldCase[];
  compare: {
    base: PerceptionJobHeader;
    comparedCases: number;
    baseCases: number;
    currentMetrics: TldMetrics;
    baseMetrics: TldMetrics;
    delta: TldMetrics;
  } | null;
}

export const getPerceptionTldState = async (
  jobId: number, baseJobId?: number,
): Promise<TldStateReport | null> => {
  const response = await api.get('/perception/report/tld-state', {
    params: cleanParams({ jobId, baseJobId }),
  });
  return response.data?.data || null;
};

export interface LaneMetricRow {
  category: string; threshold_m: number;
  gt: number | null; predictions: number | null; tp: number | null; fp: number | null; fn: number | null;
  precision: number | null; recall: number | null; ap: number | null;
}
export interface LaneMetricGroup {
  profile_signature: string; profile: Record<string, unknown>; complete: boolean;
  metrics_scope: string; contributing_scenes: number; rows: LaneMetricRow[];
  map: number | null; map_contributing_category_thresholds: number;
  fact_errors: { scene_id: number | null; reasons: string[] }[];
}
export interface LaneSummary {
  submitted: number; with_metrics: number; complete_scenes: number; business_status: string;
  groups: LaneMetricGroup[]; excluded: { scene_id: number; reasons: string[] }[];
}
export interface LanePolylineReport {
  task: string; checker: string; jobId: number; summary: LaneSummary;
  cases: { scene_id: number; task_id: number; fact_error?: string; runtime_error?: string; payload_validation_errors?: string[]; execution_state?: string; payload: {
    source_scene_id?: string; execution_state: string; business_status: string;
    complete: boolean; errors: string[]; frames?: Record<string, number>;
    provenance?: Record<string, unknown>; profile?: Record<string, unknown>;
    profile_signature?: string; gt_metadata_sha256?: string;
    facts?: Record<string, unknown>; rows: LaneMetricRow[];
  } | null }[];
  compare: {
    available: boolean; common_source_scenes: string[];
    excluded: { source_scene_id: string; reason: string }[];
    current: LaneSummary; base: LaneSummary;
    deltas: { category: string; threshold_m: number; profile_signature: string;
      precision_pp: number | null; recall_pp: number | null; ap_pp: number | null }[];
  } | null;
}
export const getPerceptionLanePolyline = async (
  jobId: number, baseJobId?: number,
): Promise<LanePolylineReport | null> => {
  const response = await api.get('/perception/report/lane-polyline', { params: cleanParams({ jobId, baseJobId }) });
  return response.data?.data || null;
};

export const getTaskList = async (params: TaskListParams): Promise<TaskListResponse> => {
  const response = await api.post("/task/list", params);
  const { total, list, page } = parseListResponse(response.data);
  return {
    total,
    tasks: list,
    page,
  };
};

/** 导出任务报表（POST /task/export）。
 *
 *  报表**整份由后端生成**（CSV，UTF-8 BOM，流式返回）：行集与 ``getTaskList``
 *  共用后端同一条过滤链，故传同一份 params 即可；分页字段（current/pageSize）
 *  后端忽略，导出的是全部命中行而非当前页。
 *
 *  返回 blob + 文件名。文件名优先取响应头 ``Content-Disposition`` 里的
 *  RFC 5987 ``filename*=``（后端给的带中文名），同源请求才读得到该头；
 *  取不到时前端按同样规则兜底拼一个。
 */
export const exportTaskReport = async (
  params: TaskListParams,
): Promise<{ blob: Blob; filename: string }> => {
  // responseType=blob 之后，错误体（JSON）也会被当成 blob 收下：拦截器读不到
  // data.msg，默认会弹出 "Request failed with status code 500" 之类。故这里
  // 关掉统一错误处理，自己把 blob 读成文本解析出后端文案。
  // 注意 401 续期重试不受影响——拦截器对 401 的处理在 _skipErrorHandler 判断之前。
  const readErrMsg = async (b: unknown, fallback: string): Promise<string> => {
    if (!(b instanceof Blob)) return fallback;
    try {
      const parsed = JSON.parse(await b.text());
      return parsed?.msg || parsed?.message || parsed?.detail || fallback;
    } catch {
      return fallback;
    }
  };

  let response;
  try {
    response = await api.post("/task/export", params, {
      responseType: "blob",
      _skipErrorHandler: true,
    } as any);
  } catch (err: any) {
    throw new Error(
      await readErrMsg(err?.response?.data, err?.message || "导出失败"),
    );
  }

  const blob: Blob = response.data;
  // 流式响应的 200 也可能夹带业务错误：CSV 的 content-type 是 text/csv，
  // 收到 JSON 就说明后端在开流前失败了。
  if (blob?.type?.includes("application/json")) {
    throw new Error(await readErrMsg(blob, "导出失败"));
  }

  const disposition: string = response.headers?.["content-disposition"] || "";
  let filename = "";
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star) {
    try {
      filename = decodeURIComponent(star[1]);
    } catch {
      filename = "";
    }
  }
  if (!filename) {
    const plain = /filename="?([^";]+)"?/i.exec(disposition);
    if (plain) filename = plain[1];
  }
  if (!filename) {
    filename = `Job_${params.jobId}_报表_${new Date().toISOString().slice(0, 10)}.csv`;
  }
  return { blob, filename };
};

/** 「筛选后统计」数据（POST /task/list/count 返回的 data）：跟随当前筛选条件实时统计。
 *  口径与顶部瓷砖一致（Task.status 分桶）；sceneCount 为 distinct 场景数，故不一定等于
 *  passCount+failCount+errorCount。 */
export interface FilteredTaskCountData {
  sceneCount: number;
  passCount: number;
  failCount: number;
  errorCount: number;
  runningCount: number;
  readyCount: number;
  totalCount: number;
}

/** 按当前筛选条件统计场景数 / pass / fail / error。与 getTaskList 传同一份 params，
 *  分页字段（current/pageSize）后端忽略、统计全量筛选结果。 */
export const getFilteredTaskCount = async (
  params: TaskListParams,
): Promise<FilteredTaskCountData> => {
  const response = await api.post("/task/list/count", params);
  const d = response.data?.data;
  return {
    sceneCount: d?.sceneCount ?? 0,
    passCount: d?.passCount ?? 0,
    failCount: d?.failCount ?? 0,
    errorCount: d?.errorCount ?? 0,
    runningCount: d?.runningCount ?? 0,
    readyCount: d?.readyCount ?? 0,
    totalCount: d?.totalCount ?? 0,
  };
};

/** Job 下 Task 数量统计（/job/tasks/count 返回的 data） */
export interface JobTaskCountData {
  exceptionCount: number;
  failCount: number;
  passCount: number;
  readyCount: number;
  runningCount: number;
  totalCount: number;
  /** 搭车字段：Job 的 task_type_id；前端用于按 task type 做渐进式 UI 开关
   *  （如「按场景分类」抽屉仅在 PNC 类型 taskTypeId=2 时显示）。
   *  Job 不存在时为 null，未升级的后端可能省略，故 optional */
  taskTypeId?: number | null;
  /** 「忽略 check 不通过场景」开关当前值；为 true 时本对象的 pass/fail/total
   *  已剔除被忽略 task。前端按此切换 Switch 状态、显示提示文案 */
  excludeCheckFailed: boolean;
  /** 被剔除条数；开关关或本 Job 没有 check 失败 task 时为 0 */
  excludedCheckFailedCount: number;
}

/** 按 logsim label 分组的 Job Task 统计单行（GET /job/tasks/count/by-label） */
export interface JobTaskCountByLabelItem {
  /** scene_inspection.label 原文；NULL / 空归 "未分类" */
  label: string;
  totalCount: number;
  passCount: number;
  failCount: number;
  exceptionCount: number;
  /** 有责数（task 下存在判定为有责的 evaluator.type==2 行，pass=false）；无责数 = total - respWith */
  respWithCount: number;
}

export interface JobTaskCountByLabelData {
  /** 按 totalCount 降序 */
  items: JobTaskCountByLabelItem[];
  /** 全 Job task 总数，用于前端做完整性校验 */
  totalCount: number;
}

/**
 * 按 logsim label 分组的 Job Task 统计；列表页统计条侧边「按场景分类」抽屉懒加载。
 */
export const getJobTaskCountByLabel = async (
  jobId: number,
): Promise<JobTaskCountByLabelData> => {
  const response = await api.get("/job/tasks/count/by-label", { params: { jobId } });
  const d = response.data?.data;
  const items: JobTaskCountByLabelItem[] = Array.isArray(d?.items)
    ? d.items.map((it: Record<string, unknown>) => ({
        label: String(it.label ?? "未分类"),
        totalCount: Number(it.totalCount ?? 0),
        passCount: Number(it.passCount ?? 0),
        failCount: Number(it.failCount ?? 0),
        exceptionCount: Number(it.exceptionCount ?? 0),
        respWithCount: Number(it.respWithCount ?? 0),
      }))
    : [];
  return {
    items,
    totalCount: Number(d?.totalCount ?? 0),
  };
};

/**
 * 获取 Job 下 Task 数量统计
 */
export const getJobTaskCount = async (jobId: number): Promise<JobTaskCountData> => {
  const response = await api.get("/job/tasks/count", { params: { jobId } });
  const d = response.data?.data;
  return {
    exceptionCount: d?.exceptionCount ?? 0,
    failCount: d?.failCount ?? 0,
    passCount: d?.passCount ?? 0,
    readyCount: d?.readyCount ?? 0,
    runningCount: d?.runningCount ?? 0,
    totalCount: d?.totalCount ?? 0,
    taskTypeId: d?.taskTypeId ?? null,
    excludeCheckFailed: Boolean(d?.excludeCheckFailed),
    excludedCheckFailedCount: Number(d?.excludedCheckFailedCount ?? 0),
  };
};

/** 读取 Job 的「忽略 check 不通过场景」开关。 */
export const getExcludeCheckFailed = async (jobId: number): Promise<boolean> => {
  const response = await api.get("/job/exclude-check-failed", { params: { jobId } });
  return Boolean(response.data?.data?.value);
};

/** 切换 Job 的「忽略 check 不通过场景」开关；返回写入后值。 */
export const setExcludeCheckFailed = async (
  jobId: number,
  value: boolean,
): Promise<boolean> => {
  const response = await api.put(
    "/job/exclude-check-failed",
    { value },
    { params: { jobId } },
  );
  return Boolean(response.data?.data?.value);
};

// Task日志响应
export interface TaskLogResponse {
  logS3Path: string;
  logDownloadUrl: string;
  logContent: string;
  totalLines: number;
  tailLines: number;
}

/**
 * 获取Task日志
 */
export const getTaskLogs = async (taskId: number): Promise<TaskLogResponse> => {
  const response = await api.get("/task/logs", { params: { taskId } });
  return response.data?.data || {
    logS3Path: '',
    logDownloadUrl: '',
    logContent: '',
    totalLines: 0,
    tailLines: 0,
  };
};

/** GET /task/{taskId}/scene-info 响应:场景生成任务详情抽屉专用,
 *  轻量返回场景备注 + 状态,让前端判断「豁免」按钮是否可点。 */
export interface TaskSceneInfo {
  taskId: number;
  sceneId: number;
  sceneName: string | null;
  sceneInfo: string;
  sceneStatus: number;
  sceneStatusName: string | null;
}

/** 拉 task 关联场景的备注 + 状态。 */
export const getTaskSceneInfo = async (
  taskId: number,
): Promise<TaskSceneInfo | null> => {
  const response = await api.get(`/task/${taskId}/scene-info`);
  return response.data?.data ?? null;
};

/** POST /scene-inspection/{sceneId}/waive 响应:豁免结果 + 自动入集(如果配了)。 */
export interface WaiveSceneResult {
  sceneId: number;
  status: number;
  statusName: string;
  autoAddedToNodeId: number | null;
  autoAddedToNodeName: string | null;
  /** 本次豁免是否顺带解除了「重新生成失败」造成的禁用（R3）。 */
  releasedDisable?: boolean;
}

/** 豁免不合格场景(INVALIDATED→COMPLETED_DEFECTIVE),自动尝试入集(依赖 job 配置)。 */
export const waiveScene = async (
  sceneId: number,
  reason?: string,
): Promise<WaiveSceneResult> => {
  const response = await api.post(
    `/scene-inspection/${sceneId}/waive`,
    { reason: reason ?? undefined },
  );
  return response.data?.data as WaiveSceneResult;
};

// Coredump 抽栈响应:与后端 task_service.query_task_coredump_tail 对齐。
// 命中行的 lineNo 是**扫描窗口内**相对行号,不是全量日志绝对行号。
export interface CoredumpHit {
  lineNo: number;
  line: string;
}
export interface CoredumpTailResponse {
  hits: CoredumpHit[];
  scannedLines: number;
  scannedBytes: number;
  fromTail: boolean;
}

/**
 * 获取 Task 日志尾部的 coredump 堆栈行(服务端 grep,前端不直连 BOS)。
 */
export const getTaskCoredumpTail = async (
  taskId: number,
): Promise<CoredumpTailResponse> => {
  const response = await api.get("/task/logs/coredump-tail", { params: { taskId } });
  return response.data?.data || {
    hits: [], scannedLines: 0, scannedBytes: 0, fromTail: false,
  };
};

// 仿真评估项（evaluator/list 返回的 data 项）
// type=1 评测器（evaluator），type=2 有无责任（respEvent），type=3 检查项（_check）；
// type=2 行只保证 evaluatorName 必有，其余字段（responsibility 等）按后端 evaluate_info
// 原样透传，与算法侧任意扩展同步，故声明为索引签名 + 已知字段并存。
export interface EvaluatorItem {
  id?: number;
  evaluatorName: string;
  result?: string | null;
  msg?: string | null;
  time?: string | null;
  createTime?: string | null;
  type?: 1 | 2 | 3;
  responsibility?: string | null;
  /** 布尔判定（后端读路径由 pass/success 或 result 派生）：
   *  对 type=2（有无责任）行,true=无责、false=有责;无法判定时为 null。 */
  passed?: boolean | null;
  /** 所属 evaluation_result.id（同一 task 多次评测时区分批次） */
  evaluationResultId?: number | null;
  [key: string]: unknown;
}

/**
 * 获取仿真评估结果列表。
 *
 * @param taskId 任务 ID
 * @param includeRespEvent 同时返回有无责任结果（type=2 行）；默认 false 保持向后兼容
 */
export const getEvaluatorList = async (
  taskId: number,
  includeRespEvent: boolean = false,
): Promise<EvaluatorItem[]> => {
  const response = await api.get("/evaluation/evaluator/list", {
    params: { taskId, includeRespEvent },
  });
  const list = response.data?.data;
  return Array.isArray(list) ? list : [];
};

/**
 * 获取 Job 下所有 evaluator_name（去重、升序；终结态 job 走 2h 缓存）
 */
export const getEvaluatorNamesByJob = async (jobId: number): Promise<string[]> => {
  const response = await api.get("/evaluation/evaluator/job/names", { params: { jobId } });
  const list = response.data?.data;
  return Array.isArray(list) ? list.filter((x) => typeof x === "string" && x) : [];
};

/**
 * 获取 Job 下所有「有无责事件」(type=2) 的 evaluator_name（原样、去重升序；终结态 job 走 2h 缓存）。
 * 供 task 列表「有无责事件」筛选下拉用，与 respEventName 精确匹配同口径。
 */
export const getRespEventNamesByJob = async (jobId: number): Promise<string[]> => {
  const response = await api.get("/evaluation/evaluator/job/resp-event-names", { params: { jobId } });
  const list = response.data?.data;
  return Array.isArray(list) ? list.filter((x) => typeof x === "string" && x) : [];
};

/**
 * 获取 Job 下所有「检查项」(type=3) 的 evaluator_name（原样、去重升序；终结态 job 走 2h 缓存）。
 * 供 task 列表「检查项」筛选下拉用，与 checkName 精确匹配同口径。
 */
export const getCheckNamesByJob = async (jobId: number): Promise<string[]> => {
  const response = await api.get("/evaluation/evaluator/job/check-names", { params: { jobId } });
  const list = response.data?.data;
  return Array.isArray(list) ? list.filter((x) => typeof x === "string" && x) : [];
};

/**
 * 获取 Job 下所有 scene_inspection.label（logsim label，去重升序）
 *
 * 返回串末尾若出现 `"__unlabeled__"`，表示该 Job 含 NULL/空 label 的 task，
 * 前端下拉应展示为「未分类」。
 */
export const getJobSceneLabels = async (jobId: number): Promise<string[]> => {
  const response = await api.get("/job/scene-labels", { params: { jobId } });
  const list = response.data?.data;
  return Array.isArray(list) ? list.filter((x) => typeof x === "string" && x) : [];
};

export interface JobSceneTreeNodeOption {
  id: number;
  parentId: number;
  name: string;
  nodeFlag: number;
  sceneType: string;
  children: JobSceneTreeNodeOption[];
}

export const getJobSceneTreeNodes = async (
  jobId: number,
): Promise<JobSceneTreeNodeOption[]> => {
  const response = await api.get("/job/scene-tree-nodes", { params: { jobId } });
  const list = response.data?.data;
  return Array.isArray(list) ? list : [];
};

// 评估文件信息
export interface EvaluationFileItem {
  filename: string;
  path: string;
  url: string;
}

/** 文件用途：output=评测结果输出桶 / scene_info=场景信息桶。具体桶名由后端按环境决定。 */
export type EvaluationFileScope = 'output' | 'scene_info';

/**
 * 根据 keys 列表获取评估文件访问地址（桶由后端按 scope + 环境决定，前端不再传桶名）
 */
export const getEvaluationFileUrls = async (
  keys: string[],
  scope: EvaluationFileScope = 'output',
  /** skipErrorHandler：调用方自行汇总提示时关掉拦截器的全局 message.error（分批请求避免刷屏）；
   *  download：链接签为附件，点击即下载（而非新标签页打开） */
  options: { skipErrorHandler?: boolean; download?: boolean } = {}
): Promise<EvaluationFileItem[]> => {
  if (!keys.length) return [];
  const response = await api.get("/evaluation/batch/file/url", {
    params: {
      scope,
      keys: keys.join(","),
      ...(options.download ? { download: true } : {}),
    },
    ...(options.skipErrorHandler ? { _skipErrorHandler: true } : {}),
  } as any);

  const list = response.data?.data;
  if (!Array.isArray(list)) return [];

  const result: EvaluationFileItem[] = [];

  list.forEach((item: any) => {
    const fileDetails = Array.isArray(item?.fileDetails) ? item.fileDetails : [];
    fileDetails.forEach((fd: any) => {
      result.push({
        filename: fd.filename ?? "",
        path: fd.path ?? item.key ?? "",
        url: fd.url ?? "",
      });
    });
  });

  return result;
};

/**
 * 根据单个 key 获取文件访问地址（桶由后端按 scope + 环境决定，前端不再传桶名）
 */
export const getEvaluationSingleFileUrl = async (
  key: string,
  scope: EvaluationFileScope = 'output'
): Promise<string> => {
  const response = await api.get("/evaluation/file/url", {
    params: { scope, key },
  });
  return response.data?.data?.[0]?.url ?? "";
};

// Job 详情中的评估项
export interface JobDetailEvaluator {
  evaluatorName: string;
  msg: string;
  result: string;
  time: string;
}

// Job 详情中的 Task 项
export interface JobDetailTask {
  taskId: number;
  jobId: number;
  sceneId: number;
  sceneName: string;
  sceneType: string;
  /** 场景 logsim label(scene_inspection.label);NULL/空 = 未分类 */
  label?: string | null;
  taskTypeId: number;
  taskTypeName: string;
  status: number;
  statusName: string;
  createTime: string;
  endTime: string | null;
  creator: string;
  taskDispatchId: string;
  evaluationResult?: {
    address?: string;
    createTime?: string;
    evaluators?: JobDetailEvaluator[];
    id?: number;
    result?: boolean;
    taskId?: number;
  } | null;
  evaluationStatic?: {
    evaluationResult?: boolean;
    failedCount?: number;
    successCount?: number;
    /** type=1（评测器）总行数，不分 result —— 列表 chip「评测 n」直接消费 */
    evaluatorCount?: number;
    /** type=2（respEvent / 有无责）行数，列表 chip「有无责 m」直接消费 */
    respEventCount?: number;
    /** 是否存在碰撞：type=1 中任一「*collision*」评测器判 Failed。 */
    hasCollision?: boolean;
    /** VRU 责任碰撞相对速度(m/s)：仅 vru_responsibility_collision_evaluator 上报。 */
    collisionRelativeSpeed?: number | null;
    /** 真实(record 侧)碰撞相对速度(m/s)：从碰撞评估器 msg "record relative speed X m/s" 提取。 */
    collisionRecordSpeed?: number | null;
  } | null;
}

// Job 详情响应数据
export interface JobDetailData {
  customParams?: JobCustomParams | null;
  jobId: number;
  jobName: string;
  algorithmVersion: string | null;
  createTime: string;
  endTime: string | null;
  creator: string | null;
  dispatchJobId: string;
  priority: number;
  priorityName: string;
  sceneSetIds: string;
  status: number;
  statusName: string;
  taskTypeId: number;
  taskTypeName: string;
  /** 归属仿真类型（后端 task_type.sim_type），口径同 JobItem.simType。 */
  simType?: string;
  totalTaskNum: number;
  successTaskNum: number;
  failTaskNum: number;
  tasks: JobDetailTask[];
}

/**
 * 获取 Job 详情（单页）
 */
const getJobDetailPage = async (
  jobId: number, current: number, pageSize: number
): Promise<{ data: JobDetailData; totalElements: number }> => {
  const response = await api.get("/job/detail", {
    params: { jobId, current, pageSize },
  });
  const data = response.data?.data;
  const totalElements = response.data?.page?.totalElements
    ?? data?.totalTaskNum
    ?? data?.tasks?.length
    ?? 0;
  return { data, totalElements };
};

/**
 * 获取 Job 基本信息（轻量：只取第 1 页第 1 条，拿到 jobName / 任务类型 / 创建人 /
 * 时间 / 算法版本 / 状态等元数据，不拉全量 tasks）。供「不通过统计」独立页基本信息卡使用。
 */
export const getJobBasicInfo = async (jobId: number): Promise<JobDetailData> => {
  const { data } = await getJobDetailPage(jobId, 1, 1);
  return data;
};

/**
 * 获取 Job 详情（自动分批拉取全量 tasks，单次最多 1000）
 */
export const getJobDetail = async (jobId: number): Promise<JobDetailData> => {
  const PAGE_SIZE = 1000;
  const first = await getJobDetailPage(jobId, 1, PAGE_SIZE);
  const result = first.data;
  const total = first.totalElements;

  if (total > PAGE_SIZE && result.tasks) {
    const totalPages = Math.ceil(total / PAGE_SIZE);
    const remaining = [];
    for (let p = 2; p <= totalPages; p++) {
      remaining.push(getJobDetailPage(jobId, p, PAGE_SIZE));
    }
    const pages = await Promise.all(remaining);
    for (const page of pages) {
      if (page.data?.tasks) {
        result.tasks.push(...page.data.tasks);
      }
    }
  }

  return result;
};

// 场景列表项
export interface SceneListItem {
  id: number;
  name: string;
  /** 对应 scene_inspection.api_code（Jira key 等），列表接口与详情字段名一致为 apiCode */
  apiCode?: string | null;
  /** logsim 标签，对应 scene_inspection.label */
  label?: string | null;
  recordAddress: string;
  params: string;
  vin: string;
  vinId: string;
  creator: string;
  createTime: string;
  /** 更新时间：新增场景时与 createTime 同值；场景更新接口命中实际改动时刷新为 now() */
  updateTime?: string | null;
  sceneStartTime: string;
  sceneEndTime: string;
  mapName: string;
  mapVersion: string;
  /** 场景版本号：整型计数器，每次场景内容改动 +1 */
  sceneVersion: number;
  /** Jira 算法版本号（customfield_10508 -> jira.version），非 jira 来源为 null */
  algorithmVersion?: string | null;
  sceneInfo: string;
  sceneType: string;
  /** 场景描述：人工填写的说明文字，供「基础信息」区块展示与编辑；未填为 null / undefined */
  description?: string | null;
  treeNodeId: number;
  /** 场景来源（jira / data_platform / collect_task），由 SceneInspectionVo 直接透传 */
  source?: SceneSource | string | null;
  /** 区域标记: 'cn' (国内) / 'ae' (阿联酋) / 'sea' (新加坡). 由 case_flow_service 落库时派生, 供筛选 + waypulse 跳转路由 */
  region?: string | null;
  /**
   * 缩略图 BOS 签名 URL（6h 有效）；后端按「与 scene_info.json 同目录」的 prefix
   * 拼 ``preview_image/preview.webp``（前缀实为 ``bag_type + function_tag``，
   * 例 "L"+"PL"="LPL"），与 list_bos_config_files 接口的 prefix 一致。
   * 缺 sceneInfoAddress 或 BOS 凭据未配置时为 null；preview 文件不存在时浏览器 404,
   * 前端用 antd Image 的 fallback 占位（不必先 head 确认）。
   */
  previewUrl?: string | null;
  /** 是否已禁用（逻辑删除）：1=禁用，前端按此置灰；0/undefined=正常 */
  isDeleted?: number | null;
  /** 数据密级：0=公开（默认），>0=受控（仅 data:confidential:view 可见） */
  securityLevel?: number;
  /** 场景状态 (scene_inspection.status);运行时 SceneInspectionVo 里也有,前端按需展示。 */
  status?: number | null;
  /** 场景状态中文名 (由后端 SceneInspectionVo.status_name computed_field 派生)。 */
  statusName?: string | null;
  /** JSONB 扩展:external.jira.issue_time(故障时刻)、external.open_switch_closed_loop_sec 等。
   *  详细结构见后端 SceneInspection.external;这里以 Record 兜底供前端按需读取。 */
  external?: Record<string, any> | null;
}

// 场景列表查询参数（所有过滤项支持多值，逗号/空格分隔）
export interface SceneListParams {
  current?: number;
  pageSize?: number;
  parentIds?: string;
  /** 多个场景 ID，逗号分隔；按 IN 精确匹配 */
  sceneIds?: string;
  /** 多个场景名称，逗号分隔；任一子串命中即返回（OR LIKE） */
  sceneNames?: string;
  /** 多个 Jira / AIP 等 issue 编码（api_code），逗号分隔；任一子串命中即返回 */
  jiraIssueCodes?: string;
  /** 多个 label，逗号分隔；按 IN 精确匹配（来自下拉去重列表） */
  labels?: string;
  /** 场景来源（jira / data_platform / collect_task），逗号分隔；按 IN 精确匹配 */
  sources?: string;
  /**
   * 场景大类分组过滤（scene_inspection.scene_group），逗号分隔多值；
   * sentinel `__uncategorized__` 命中 NULL/空串；供左侧树 daily 分组子节点使用。
   */
  sceneGroups?: string;
  /** 按 scene_inspection.region 精确匹配: 'cn' (国内) / 'ae' (阿联酋) / 'sea' (新加坡)。逗号分隔多值 */
  regions?: string;
  /** 按创建时间筛选：起始时间（含），ISO 8601 字符串 `YYYY-MM-DDTHH:mm:ss` */
  createTimeStart?: string;
  /** 按创建时间筛选：结束时间（含），ISO 8601 字符串 `YYYY-MM-DDTHH:mm:ss` */
  createTimeEnd?: string;
  /** 是否包含已禁用场景（展示页应传 true，前端按 isDeleted 置灰） */
  includeDisabled?: boolean;
}

// 场景列表响应
export interface SceneListResponse {
  total: number;
  scenes: SceneListItem[];
  page?: PageInfo;
}

// ---- 场景生成 (scene-creation) ----

/** 场景生成模式（下发 dataset.params.mode，场景表字段 gen_mode）。同一 apiCode 的事故 / 云控是两条独立场景 */
export type SceneGenMode = 'generate_and_qc_check' | 'cloud_cmd';

export interface CreateSceneByJiraParams {
  jiraIssueKeys: string;
  /** 可选：创建后是否立即调度 CF 构建。后端默认 True(不传即构建)；传 false 只建不构建。 */
  autoGenerate?: boolean;
  /** 是否跳过 logsim 字段判断；默认 false */
  skipLogsimCheck?: boolean;
  /** 可选：目标场景集（场景树叶子节点 id）。生成完成进入「质检中」后自动入集。 */
  sceneSetNodeId?: number;
  /** 可选：新建场景的 security_level（0=公开、1=受控）。仅 data:confidential:manage 权限用户可传；后端无权限时忽略。 */
  securityLevel?: number;
  /** 场景生成模式：generate_and_qc_check=事故场景（默认）/ cloud_cmd=云控场景 */
  genMode?: SceneGenMode;
}

// ─── v3 场景标注 · preview ─────────────────────────────
//
// 输入 1~N 个 Jira/CaseFlow code,后端**只读**拉 draft 参数供前端标注表单展示,
// 不落库、不 dispatch。详见 [docs/scene-annotation-backend-design.md] §2.1。
// 用户在标注页改完 → 前端存 localStorage → 全部完成后走 annotate-submit 一次
// 性入库(annotate-submit 后端待定,前端联调时再补)。

import type { CloudCmdPolicy } from '@/utils/cloudCmdPolicy';

// 云控策略的取值与守卫放在无依赖的独立模块，供 node --test 直接加载。
export {
  CLOUD_CMD_POLICY_OPTIONS,
  CLOUD_CMD_POLICY_HINT,
  isCloudCmdPolicy,
} from '@/utils/cloudCmdPolicy';
export type { CloudCmdPolicy } from '@/utils/cloudCmdPolicy';

/** preview 单条待渲染的字段;与 SceneEditFields 对齐;字段值可为 null(用户未填/外源未抽到)。 */
export interface ScenePreviewFields {
  sceneStartTime: string | null;
  sceneEndTime: string | null;
  issueTime: string | null;
  mapName: string | null;
  mapVersion: string | null;
  openSwitchClosedLoopSec: number | null;
  /** 是否屏蔽 finish 信号（bool）；来自 external.mask_finish_signal，缺省 null（不屏蔽=正常透传）。 */
  maskFinishSignal: boolean | null;
  /** 云控指令合并策略；来自 external.cloud_cmd_policy，缺省 null（按 passthrough 一起发渲染）。 */
  cloudCmdPolicy: CloudCmdPolicy | null;
  label: string | null;
  /** 场景描述：可在标注面板编辑，与 scene_inspection.description 直连 */
  description: string | null;
  /** 云端控制事件列表;来自 scene_inspection.external.events(JSONB)。
   *  null=preview 未拉到(code mode);空数组=普通场景无事件;非空=已有事件。 */
  events: SceneEvent[] | null;
}

/** 命中已存在 api_code 时的短摘要,前端可提示用户"跳去编辑那条已有场景"。 */
export interface ExistingScene {
  sceneId: number;
  sceneName: string;
  createdAt: string | null;
}

/** preview 单条结果;单条失败 resolved=false + error,不影响其他条。 */
export interface ScenePreviewItem {
  code: string;
  source: 'jira' | 'case_flow';
  resolved: boolean;
  error: string | null;
  fields: ScenePreviewFields | null;
  external: Record<string, any>;
  algorithmVersion: string | null;
  /** draft 阶段地图不存在时的软警告(annotate-submit 时才严格阻断) */
  mapMissingReason: string | null;
  existing: ExistingScene | null;
}

export interface ScenePreviewResponse {
  items: ScenePreviewItem[];
}

/** POST /scene-creation/scenes/preview
 *
 *  - source='auto'(默认): 后端按 code 字面 pattern 判定(``XX-\d+`` → jira,其他 → case_flow)。
 *  - source='jira' / 'case_flow': 强制走某条通路。
 *  - codes: 支持 1-200 条,前端不需要在客户端去重(后端去重 + strip),但仍建议前端展示前先 trim。
 */
export const previewScenes = async (
  codes: string[],
  source: 'jira' | 'case_flow' | 'auto' = 'auto',
): Promise<ScenePreviewResponse> => {
  const response = await api.post("/scene-creation/scenes/preview", { codes, source });
  return response.data?.data ?? { items: [] };
};

/** 批量生成提交前检查命中的已生成完成场景（已完成 / 已完成-瑕疵品）。 */
export interface ExistingCompletedScene {
  /** 输入的 code（= api_code：Jira Issue Key / CaseFlow Case ID） */
  code: string;
  sceneId: number;
  sceneName: string;
  /** 8 已完成 / 7 已完成-瑕疵品 */
  status: number;
  statusName: string;
}

/** POST /scene-creation/scenes/existing-completed
 *
 *  只读查库：返回 codes 中在 genMode 下已有合格场景的项（顺序同输入，未命中不返回）。
 *  事故 / 云控是两条独立场景，只看所选模式。批量生成提交前调用，命中则弹框让用户选择处理方式。
 */
export const checkExistingCompletedScenes = async (
  codes: string[],
  genMode: SceneGenMode = 'generate_and_qc_check',
  functionTag: 'PL' | 'PE' = 'PL',
): Promise<ExistingCompletedScene[]> => {
  const response = await api.post("/scene-creation/scenes/existing-completed", {
    codes, genMode, functionTag,
  });
  return response.data?.data?.items ?? [];
};

/** ``POST /scene-creation/from-jira`` 异步任务完成后的批量结果（与 batch_create_from_jira 保持一致）。 */
export interface CreateSceneFromJiraResult {
  total: number;
  success: number;
  /** 已存在 api_code 时按 Jira 更新并重写 scene_info.json 的条数 */
  updated: number;
  skipped: number;
  failed: number;
  results?: Array<{
    key: string;
    success: boolean;
    updated?: boolean;
    sceneId?: number;
    error?: string;
    data?: unknown;
  }>;
  /** auto_generate=true 时附带的批量构建结果 */
  generateResult?: { jobId: number; dispatchJobId: string | null; total: number } | null;
  /** true = 本批被取消收口，未处理的条目 results[].error 为「本批已取消，未处理」 */
  cancelled?: boolean;
}

/** 批量建场景批次的一条逐条消息（后端只保留最近 200 条）。 */
export interface SceneBatchMessage {
  time?: string | null;
  /** info / warn / error */
  level?: string | null;
  text?: string | null;
}

/**
 * 批量建场景批次（Jira / 国内 Case-Flow 共用）的台账字段。
 *
 * 2026-10-21 起「发起即建 Job」：Job 立刻出现在任务列表（pending，进度随拉取涨），
 * 不再等全部拉完才建。``jobId`` 就是那个 Job —— 卡片在跑的时候也能点进 job 详情。
 * 海外区域（ae/sea）不走台账，恒为 null。
 */
export interface SceneBatchLedgerFields {
  /** 本批台账 Job（发起时同步创建） */
  jobId?: number | null;
  jobName?: string | null;
  /**
   * true = 状态还在 running 但后台线程已消失（进程重启等）。账本在 PG 里没丢，
   * 可调 ``resumeSceneBatch`` 从断点继续。
   */
  interrupted?: boolean | null;
  /** 逐条进度消息（最近 200 条） */
  messages?: SceneBatchMessage[] | null;
}

/** from-jira 异步任务状态（按用户维度存于 Redis，TTL 6h 兜底）。 */
export type CreateSceneFromJiraStatus = "idle" | "running" | "completed" | "failed";

export interface CreateSceneFromJiraState extends SceneBatchLedgerFields {
  status: CreateSceneFromJiraStatus;
  keysCount?: number;
  /**
   * running 时的当前阶段：creating=建台账 / fetching=拉取数据并创建场景 /
   * done=全部处理完（含自动下发）/ cancelled=已取消
   */
  stage?: "creating" | "fetching" | "done" | "cancelled" | string | null;
  /** running 期间已处理条数（成功 + 失败） */
  processedCount?: number | null;
  /** 本批总条数（进度条分母） */
  totalCount?: number | null;
  okCount?: number | null;
  failedCount?: number | null;
  autoGenerate?: boolean;
  skipLogsimCheck?: boolean;
  startTime?: string | null;
  endTime?: string | null;
  updatedAt?: string | null;
  result?: CreateSceneFromJiraResult | null;
  error?: string | null;
  genMode?: SceneGenMode;
}

export interface CreateSceneFromJiraStartResponse {
  message: string;
  state: CreateSceneFromJiraState;
}

/**
 * 异步启动 Jira 批量建场景任务：若已在运行则 message 提示运行中。
 * 结果通过 ``getCreateSceneFromJiraStatus`` 轮询获取。
 */
export const createSceneFromJira = async (
  params: CreateSceneByJiraParams,
): Promise<CreateSceneFromJiraStartResponse | undefined> => {
  const response = await api.post("/scene-creation/from-jira", params);
  const payload = response.data;
  if (!payload) return undefined;
  return { message: payload.message || "", state: payload.data };
};

/** 查询当前用户的 from-jira 任务状态 */
export const getCreateSceneFromJiraStatus = async (): Promise<CreateSceneFromJiraState | undefined> => {
  const response = await api.get("/scene-creation/from-jira/status");
  return response.data?.data;
};

/** POST /scene-creation/from-caseflow 入参 */
export interface CreateSceneFromCaseFlowParams {
  /** 多个 case_id, 逗号 / 换行分隔 */
  caseIds: string;
  autoGenerate?: boolean;
  sceneSetNodeId?: number;
  securityLevel?: number;
  /** case-flow 区域: 'cn'=国内(默认) / 'ae'=阿联酋 / 'sea'=新加坡。三套 caseflow 独立, 数据不通, 前端显式选 */
  region?: "cn" | "ae" | "sea";
  /** 场景生成模式：generate_and_qc_check=事故场景（默认）/ cloud_cmd=云控场景 */
  genMode?: SceneGenMode;
  /** pnc=规划入库（默认）；perception=感知入库 */
  domain?: "pnc" | "perception";
}

/** POST /scene-creation/from-caseflow 返回的 data 字段(与 CaseFlowSceneCreateResult 对齐, 单条 webhook 用) */
export interface CaseFlowSceneCreateResult {
  sceneId: number;
  sceneName: string;
  sceneInfoAddress: string;
  updated: boolean;
  dispatched: boolean;
}

/** ``/scene-creation/from-caseflow`` 异步任务状态(用户维度, Redis TTL 6h)。 */
export type CreateSceneFromCaseFlowStatus =
  | "idle" | "running" | "completed" | "failed";

export interface CreateSceneFromCaseFlowState extends SceneBatchLedgerFields {
  status: CreateSceneFromCaseFlowStatus;
  caseIdsCount?: number | null;
  /**
   * running 时的当前阶段：creating=建台账 / fetching=拉取数据并创建场景 /
   * done=全部处理完（含自动下发）/ cancelled=已取消
   */
  stage?: "creating" | "fetching" | "done" | "cancelled" | string | null;
  /** running 期间已处理条数（成功 + 失败） */
  processedCount?: number | null;
  /** 本批总条数（进度条分母） */
  totalCount?: number | null;
  okCount?: number | null;
  failedCount?: number | null;
  autoGenerate?: boolean | null;
  startTime?: string | null;
  endTime?: string | null;
  updatedAt?: string | null;
  /** completed 时的批量结果:{total, success, updated, failed, results, generateResult} */
  result?: {
    total: number;
    success: number;
    updated: number;
    failed: number;
    results: {
      caseId: string;
      success: boolean;
      updated?: boolean;
      sceneId?: number;
      sceneName?: string;
      error?: string;
    }[];
    generateResult?: { jobId: number; dispatchJobId: string | null; total: number } | null;
    /** true = 本批被取消收口 */
    cancelled?: boolean;
  } | null;
  error?: string | null;
  genMode?: SceneGenMode;
}

export interface CreateSceneFromCaseFlowStartResponse {
  message: string;
  state: CreateSceneFromCaseFlowState;
}

/** 异步启动 case-flow 批建场景任务;已在运行则 message 提示。轮询 status 端点看结果。 */
export const createSceneFromCaseFlow = async (
  params: CreateSceneFromCaseFlowParams,
): Promise<CreateSceneFromCaseFlowStartResponse | undefined> => {
  const response = await api.post("/scene-creation/from-caseflow", params);
  const payload = response.data;
  if (!payload) return undefined;
  return { message: payload.message || "", state: payload.data };
};

/** 查询当前用户的 from-caseflow 任务状态。perception 与规划进度锁分开。 */
export const getCreateSceneFromCaseFlowStatus = async (
  domain: 'pnc' | 'perception' = 'pnc',
): Promise<CreateSceneFromCaseFlowState | undefined> => {
  const response = await api.get("/scene-creation/from-caseflow/status", { params: { domain } });
  return response.data?.data;
};

/** ``POST /scene-creation/batch/{jobId}/cancel|resume`` 的返回。 */
export interface SceneBatchActionResponse<TState> {
  jobId: number;
  started: boolean;
  message: string;
  state?: TState | null;
}

/** 取消 / 续跑的统一返回：``ok=false`` 时 ``message`` 是后端给的原因（直接弹给用户）。 */
export interface SceneBatchActionResult<TState> {
  ok: boolean;
  message: string;
  /** 后端顺带回的**最新状态**（成功时才有），可直接覆盖本地轮询结果 */
  state?: TState | null;
}

/**
 * 取消一个批量建场景批次（**取消即放弃本批**）。
 *
 * 拉取途中取消：未处理的 Task 置 cancelled、Job 进 cancelled 终态，已建成的场景保留；
 * 已下发构建：等同任务页的取消（删 CF 作业 + 暂停任务）。**不可续跑**。
 */
export const cancelSceneBatch = async (
  jobId: number,
): Promise<SceneBatchActionResult<CreateSceneFromJiraState | CreateSceneFromCaseFlowState>> => {
  const response = await api.post(`/scene-creation/batch/${jobId}/cancel`);
  const payload = response.data;
  if (!payload || payload.code !== 0) {
    return { ok: false, message: payload?.message || "取消失败" };
  }
  const data = payload.data as SceneBatchActionResponse<never> | undefined;
  return { ok: true, message: data?.message || "已取消本批", state: (data?.state as never) ?? null };
};

/**
 * 断点续跑一个**异常中断**（``interrupted``）的批次：只处理仍为 pending 的条目，
 * 已建成的沿用，不重复建 / 不重复下发。已取消 / 已下发的批次会被后端拒绝。
 */
export const resumeSceneBatch = async (
  jobId: number,
): Promise<SceneBatchActionResult<CreateSceneFromJiraState | CreateSceneFromCaseFlowState>> => {
  const response = await api.post(`/scene-creation/batch/${jobId}/resume`);
  const payload = response.data;
  if (!payload || payload.code !== 0) {
    return { ok: false, message: payload?.message || "继续失败" };
  }
  const data = payload.data as SceneBatchActionResponse<never> | undefined;
  return { ok: true, message: data?.message || "已从断点继续", state: (data?.state as never) ?? null };
};

/** 海外 (UAE) case sync 任务列表元素 —— 场景生成前置阶段 (Azure → 国内 BOS) */
export interface ForeignSyncTaskItem {
  sceneId?: number | null;
  sceneName?: string | null;
  apiCode?: string | null;
  creator?: string | null;
  syncSrc?: string | null;
  syncDest?: string | null;
  /** SUBMITTED / COMPLETED / SYNC_FAILED */
  status?: string | null;
  /** UAE CF 上的 job_id, 可点击跳转到 UAE CF UI */
  dispatchJobId?: string | null;
  /** sim 侧 scene.status (1/5/8/9), 让用户看 sync 之后 autogen/qc 阶段进度 */
  sceneStatus?: number | null;
  submittedAt?: string | null;
  callbackAt?: string | null;
}

/** 海外 sync job 列表元素 (job 级聚合, 主列表 paginateByJob=true 返回) */
export interface ForeignSyncJobItem {
  dispatchJobId: string;
  totalCases: number;
  completedCases: number;
  failedCases: number;
  submittedCases: number;
  creator?: string | null;
  /** SUBMITTED / COMPLETED / SYNC_FAILED (job 级聚合状态) */
  syncStatus?: string | null;
  sceneRecorded: number;
  sceneQcRunning: number;
  scenePassed: number;
  sceneFailed: number;
  submittedAt?: string | null;
  callbackAt?: string | null;
}

export interface QueryForeignSyncTaskParams {
  current?: number;
  pageSize?: number;
  apiCode?: string;
  creator?: string;
  status?: string;
  dispatchJobId?: string;
  /** true=主列表按 job 分页, false=子任务页按 scene 分页 */
  paginateByJob?: boolean;
  submittedAfter?: string;
  submittedBefore?: string;
}

export const queryForeignSyncTasks = async (
  params: QueryForeignSyncTaskParams,
): Promise<{ items: ForeignSyncTaskItem[] | ForeignSyncJobItem[]; total: number } | undefined> => {
  const response = await api.post("/scene-creation/foreign-sync/list", {
    current: params.current ?? 1,
    pageSize: params.pageSize ?? 50,
    apiCode: params.apiCode,
    creator: params.creator,
    status: params.status,
    dispatchJobId: params.dispatchJobId,
    paginateByJob: params.paginateByJob,
    submittedAfter: params.submittedAfter,
    submittedBefore: params.submittedBefore,
  });
  const payload = response.data;
  if (!payload || payload.code !== 0) return undefined;
  return {
    items: (payload.data as ForeignSyncTaskItem[]) ?? [],
    total: payload.page?.totalElements ?? 0,
  };
};

/** 海外 sync job 详情里的单个 task (CF dataset, 名字 case-{api_code}, 带 apiCode) */
export interface ForeignSyncTaskInfo {
  task_id?: string;
  task_name?: string;
  status?: string;
  executor_id?: string;
  start_time?: string;
  end_time?: string;
  duration?: number;
  error_message?: string;
}

export interface ForeignSyncTaskPage {
  total: number;
  page: number;
  page_size: number;
  tasks: ForeignSyncTaskInfo[];
}

export interface ForeignSyncJobProgress {
  total_tasks: number;
  completed_tasks: number;
  failed_tasks: number;
}

/** 海外 sync job 详情 (按 dispatch_job_id 反查海外 CF, 字段跟后端 JobDetailVo 一致 snake_case) */
export interface ForeignSyncJobDetail {
  job_id?: string;
  job_name?: string;
  status?: string;
  priority?: number;
  progress?: ForeignSyncJobProgress;
  tasks?: ForeignSyncTaskPage;
}

/** 海外 sync job 详情 + task 列表 —— 按 dispatch_job_id 反查海外 CF */
export const queryForeignSyncJobDetail = async (
  dispatchJobId: string,
  taskPage = 1,
): Promise<ForeignSyncJobDetail | undefined> => {
  const response = await api.post('/scene-creation/foreign-sync/job-detail', {
    dispatchJobId,
    taskPage,
  }, { _skipErrorHandler: true } as any);
  const payload = response.data;
  if (!payload || payload.code !== 0) return undefined;
  return payload.data as ForeignSyncJobDetail;
};

/** 海外 sync task 迁移日志 —— 按 task_id 反查海外 CF (复用 TaskLogResponse) */
export const queryForeignSyncTaskLog = async (
  taskId: string,
): Promise<TaskLogResponse | undefined> => {
  const response = await api.post('/scene-creation/foreign-sync/task-log', { taskId });
  const payload = response.data;
  if (!payload || payload.code !== 0) return undefined;
  return {
    logContent: payload.data?.logContent || '',
    logDownloadUrl: payload.data?.logDownloadUrl ?? null,
    logS3Path: payload.data?.logS3Path ?? null,
  } as TaskLogResponse;
};

export interface ScanJiraDaysParams {
  scanDays: number;
  /**
   * 要扫的 Jira 项目 key 列表（AIP / DC / GC）。
   * 空数组由后端退化到默认 AIP（保持兼容旧客户端）。
   */
  projects?: string[];
}

/** 自动扫描 Jira 后批量建场景并统一发起构建（仅汇总，无逐条明细）。
 *  - success: 新建成功
 *  - existed: 数据库已存在（api_code 命中）
 *  - skipped: logsim != "是"，主动跳过
 *  - failed: 其余异常
 */
export interface ScanJiraDaysResult {
  scanDays: number;
  jiraIssueCount: number;
  success: number;
  existed: number;
  skipped: number;
  failed: number;
  batchBuild?: { jobId: number; dispatchJobId: string | null; total: number } | null;
}

/** scan-jira 异步任务状态（按用户维度存于 Redis，TTL 6h 兜底）。
 *  - idle: 从未发起 / Redis 已过期；
 *  - running: 正在扫描；
 *  - completed: 已完成，result 中含汇总明细；
 *  - failed: 失败，error 中含错误信息；
 *  再次发起扫描会直接覆盖原记录。
 */
export type ScanJiraDaysStatus = "idle" | "running" | "completed" | "failed";

export interface ScanJiraDaysState {
  status: ScanJiraDaysStatus;
  scanDays?: number;
  /**
   * 本次扫描覆盖的 Jira 项目 key 列表；null / 空 兼容旧客户端。
   */
  projects?: string[] | null;
  /**
   * 触发本次扫描的用户 key（email > open_id > unknown）。
   *
   * 扫描已退化为全局单实例锁——任何用户共享同一个 running 状态；
   * 此字段用于在 UI 上提示「由 xxx 触发」，让后来点扫描的人知道
   * 当前不是自己起的、是同事在跑。
   */
  triggeredBy?: string | null;
  /**
   * running 期间已扫描的 Issue 数；每翻完一页 Jira 更新一次。
   * completed 时与 result.jiraIssueCount 一致。
   */
  scannedCount?: number | null;
  /**
   * Jira 服务端报告的总数（``ResultList.total``），可能 null；
   * UI 据此渲染分母「已扫描 scannedCount / totalCount」。
   */
  totalCount?: number | null;
  startTime?: string | null;
  endTime?: string | null;
  updatedAt?: string | null;
  result?: ScanJiraDaysResult | null;
  error?: string | null;
}

export interface ScanJiraDaysStartResponse {
  message: string;
  state: ScanJiraDaysState;
}

/** 启动异步扫描：若已在运行则 message 提示运行中 */
export const scanJiraDaysAndCreateScenes = async (
  params: ScanJiraDaysParams
): Promise<ScanJiraDaysStartResponse | undefined> => {
  const response = await api.post("/scene-creation/scan-jira-days", params);
  const payload = response.data;
  if (!payload) return undefined;
  return { message: payload.message || "", state: payload.data };
};

/**
 * 查询扫描任务状态（**全局**，所有用户共享同一份）。
 *
 * 后端已退化为全局单实例锁；当前用户身份不影响读到的状态，
 * ``triggeredBy`` 会告诉你 running 是谁触发的。
 */
export const getScanJiraDaysStatus = async (): Promise<ScanJiraDaysState | undefined> => {
  const response = await api.get("/scene-creation/scan-jira-days/status");
  return response.data?.data;
};

/**
 * 根据场景 ID 列表批量发起 generate-scene 任务。
 *
 * canonical 路径走 ``/scene-creation/dispatch/by-ids``，会在后端把 Job/Task.creator
 * 落到当前操作人；老的 ``/scene/start/generate/scene/by-ids`` 已 deprecated。
 */
export const batchGenerateSceneByIds = async (sceneIds: number[], force = false) => {
  const response = await api.post("/scene-creation/dispatch/by-ids", { sceneIds, force });
  return response.data?.data;
};

/**
 * 查询场景生成 Job 列表（task_type=SCENE）
 */
/**
 * 场景生成 Job 列表参数。与 JobListParams 区别:后端接口
 * ``/scene-creation/jobs`` 外部参数名仍是 ``createTimeStart``/``createTimeEnd``
 * (前端契约不动),后端内部映射到 partition_time 触发分区 pruning。
 * (主 ``/job/list`` 走的是新命名 ``partitionTimeStart``/``End``。)
 */
export interface SceneGenerationJobsParams
  extends Omit<JobListParams, 'partitionTimeStart' | 'partitionTimeEnd'> {
  createTimeStart?: string;
  createTimeEnd?: string;
  /** 精确匹配 Job.task_type_name。缺省由后端按 SCENE 过滤。 */
  taskTypeName?: 'SCENE' | 'SCENE_PE' | string;
}

export const getSceneGenerationJobs = async (
  params: SceneGenerationJobsParams
): Promise<JobListResponse> => {
  const response = await api.get("/scene-creation/jobs", { params: cleanParams(params) });
  const { total, list, page } = parseListResponse(response.data);
  return { total, tasks: list, page };
};

// 场景巡检列表项
/** 场景来源 enum 值，与后端 ``SceneSourceEnum`` 对齐：
 *  - jira: Jira issue 自动导入 / 手动按 issue key 批量
 *  - data_platform: 数据中台 / kafka 监听
 *  - manual_upload: UI 手工上传
 *  - collect_task: 回收任务直传 task_id
 *  - data_mining: 数据挖掘（预留入口，目前后端尚无写入路径）
 *  - case_flow: Case-Flow 平台推送 (POST /scene-creation/case-flow/scenes)
 *  历史数据按 label 非空回填，可能为 null 或不存在（旧 vo 不带 source 字段时）
 */
export type SceneSource =
  | 'jira'
  | 'data_platform'
  | 'manual_upload'
  | 'collect_task'
  | 'data_mining'
  | 'case_flow';

export interface SceneInspectionItem {
  id: number;
  name: string;
  /** logsim 标签，对应 scene_inspection.label */
  label?: string | null;
  recordAddress: string;
  apiCode: string;
  mapName: string;
  mapVersion: string;
  status: number;
  statusName: string;
  scenMode: number;
  creator: string;
  createTime: string;
  sceneInfoAddress: string;
  /** 备注：对应 scene_inspection.scene_info（Jira 路径会落 jira.summary） */
  sceneInfo?: string | null;
  /** 场景来源；旧数据未回填或 vo 缺失时为 null/undefined */
  source?: SceneSource | string | null;
  /** case 来源区域: 'cn' (国内) / 'ae' (阿联酋) / 'sea' (新加坡); 默认 cn */
  region?: string | null;
  /** 场景生成模式：generate_and_qc_check=事故 / cloud_cmd=云控 */
  genMode?: SceneGenMode | null;
  /** 是否已禁用（逻辑删除）：1=禁用，前端按此置灰；0/undefined=正常 */
  isDeleted?: number | null;
  /** 数据密级：0=公开（默认），>0=受控（仅 data:confidential:view 可见） */
  securityLevel?: number;
}

/**
 * 查询场景巡检列表
 *
 * 改用 POST + JSON body 直传数组，避免 ID 数量较多时 CSV 拼成 URL 触发 414 Request-URI Too Large。
 * 后端 GET 同名接口仍保留兼容外部调用方与浏览器调试。
 */
// 场景巡检列表查询参数（除 status 外，全部支持多值；走 JSON body 数组）
export interface SceneInspectionListParams {
  current?: number;
  pageSize?: number;
  /** 多个场景 ID，按 IN 精确匹配 */
  ids?: number[];
  /** 多个场景名称，每个独立子串 ilike，OR 合并 */
  names?: string[];
  /** 场景状态单值精确匹配 */
  status?: number;
  /** 多个 Jira / api_code，每个独立子串 ilike，OR 合并 */
  jiraIssueCodes?: string[];
  /** 多个 label，按 IN 精确匹配 */
  labels?: string[];
  /** 场景来源（jira / data_platform / manual_upload / collect_task），按 IN 精确匹配 */
  sources?: SceneSource[];
  /** case 来源区域: 'cn' (国内) / 'ae' (阿联酋) / 'sea' (新加坡), 按 IN 精确匹配 */
  regions?: string[];
  /** 按场景生成模式筛选（多值 IN） */
  genModes?: SceneGenMode[];
  /** 场景树节点 id 集：后端递归展开子孙叶子后按关联表 IN 匹配；既可传目录也可传叶子 */
  treeNodeIds?: number[];
  /** 按创建时间筛选：起始时间（含），ISO 8601 字符串 `YYYY-MM-DDTHH:mm:ss` */
  createTimeStart?: string;
  /** 按创建时间筛选：结束时间（含），ISO 8601 字符串 `YYYY-MM-DDTHH:mm:ss` */
  createTimeEnd?: string;
  /** 是否包含已禁用场景（场景生成 scenes tab 应传 true，前端按 isDeleted 置灰） */
  includeDisabled?: boolean;
}

export const getSceneInspectionList = async (
  params: SceneInspectionListParams,
): Promise<{ total: number; list: SceneInspectionItem[]; page?: PageInfo }> => {
  const response = await api.post("/scene/inspection/list", cleanParams(params));
  const { total, list, page } = parseListResponse(response.data);
  return { total, list, page };
};

/**
 * 按场景名精确反查 id，供「按场景·输入场景名」创建 Job 的前置校验用。
 *
 * ``requireCompleted``（默认 true）区分两类创建：
 * - 普通任务：仅接受 status=COMPLETED（质检合格）场景；
 * - 场景质检任务：传 false 放开任意状态（质检本就是去跑质检）。
 *
 * 命中未合格（仅 requireCompleted 时）/ 未找到时后端返 ``code!=0``，axios 拦截器
 * 自动 message.error 提示用户，本函数走 catch 返 null；调用方看到 null 应终止创建流程。
 */
export const resolveSceneIdsByNames = async (
  names: string[],
  requireCompleted: boolean = true,
): Promise<number[] | null> => {
  try {
    const response = await api.post(
      "/scene/inspection/resolve-ids-by-names",
      { names, requireCompleted },
    );
    const ids = response.data?.data?.ids;
    return Array.isArray(ids) ? ids : null;
  } catch {
    // 拦截器已 message.error, 这里静默返 null 阻断
    return null;
  }
};

/**
 * 查询场景表中去重后的 label 列表（后端 Redis 缓存 3h）
 */
export const getSceneLabels = async (): Promise<string[]> => {
  const response = await api.get("/scene/labels");
  const data = response.data?.data;
  return Array.isArray(data) ? data.filter((v) => typeof v === "string" && v) : [];
};

/** 与 scene_info.json 同目录的 BOS 文件项 */
export interface SceneBosConfigFileItem {
  key: string;
  name: string;
  size: number;
}

export interface SceneBosConfigFilesData {
  sceneId: number;
  sceneName: string;
  prefix: string;
  files: SceneBosConfigFileItem[];
  /** 是否为「另存为」派生场景（external.derived_from 存在）；true 才可硬删 */
  isDerived?: boolean;
}

/**
 * 列举场景目录下与 scene_info.json 同级的配置文件
 */
export const getSceneBosConfigFiles = async (
  sceneId: number,
): Promise<SceneBosConfigFilesData> => {
  const response = await api.get(`/scene/inspection/${sceneId}/config-files`);
  return response.data?.data;
};

/**
 * 读取单个配置文件内容。
 * 后端按扩展名分流：
 * - viewType='image'：previewUrl 为 BOS 签名 URL，前端直接 <img> 渲染（webp/gif/apng 动图）
 * - viewType='text'：content 为 UTF-8 文本，前端 <pre> 展示
 */
export interface SceneBosConfigFileContent {
  name: string;
  size: number;
  content: string;
  viewType: 'text' | 'image';
  contentType: string;
  previewUrl: string | null;
  sceneVersion?: number;
  sha256?: string;
  editable?: boolean;
  kind?: SceneFileKind;
}

export const getSceneBosConfigFileContent = async (
  sceneId: number,
  objectKey: string,
  sceneVersion?: number,
): Promise<SceneBosConfigFileContent> => {
  const response = await api.get(`/scene/inspection/${sceneId}/config-file-content`, {
    params: { objectKey, sceneVersion },
  });
  return response.data?.data;
};

export type SceneFileKind = 'managed-json' | 'json' | 'protobuf-text' | 'text';
export type SceneEditMode = 'fields' | 'files';

export interface SceneGenerationTask {
  taskId: number;
  jobId: number;
  jobName: string;
  status: number;
  statusName: string;
  createdAt: string;
  reason: string;
}

/** 编辑页「当前场景异常」口径（R2）。
 *  只报硬失败：kind='invalidated'（质检不合格 status=9）或
 *  kind='task_failed'（最近一条生成任务是 failed/cancelled/coredump 且已提交到 CF）。
 *  rollbackBlocked / canRegenerate 的唯一口径是生成锁（generation_busy）。 */
export interface SceneHealth {
  abnormal: boolean;
  kind: 'invalidated' | 'task_failed' | null;
  sceneStatus: number;
  sceneStatusName: string;
  sceneInfo: string | null;
  task: (SceneGenerationTask & { dispatchJobId?: string | null }) | null;
  rollbackBlocked: boolean;
  canRegenerate: boolean;
}

export interface SceneContentBaseline {
  sceneId: number;
  generationBusy: boolean;
  generationTasks: SceneGenerationTask[];
  generationStatusError: string | null;
  sceneHealth?: SceneHealth | null;
  baseVersion: number;
  fields: SceneFieldPatch;
  files: (SceneBosConfigFileItem & { editable: boolean; kind: SceneFileKind | null; reason?: string })[];
  limits: { fileBytes: number; managedFileBytes: number; totalBytes: number; requestBytes: number; fileCount: number };
}

export interface SceneContentRequest {
  requestId: string;
  editMode: SceneEditMode;
  baseVersion: number;
  fields: SceneFieldPatch;
  files: { name: string; baseSha256: string; content: string }[];
}

export interface SceneContentResult {
  sceneId: number;
  changed: boolean;
  sceneVersion: number;
  logId: number | null;
  requestId: string;
  changedFiles: string[];
  dispatch: SceneBatchUpdateDispatchInfo | null;
}

export const getSceneContentBaseline = async (sceneId: number, silent = false): Promise<SceneContentBaseline> => {
  const response = await api.get(`/scene-creation/scenes/${sceneId}/content-edit`, { _skipErrorHandler: silent } as any);
  return response.data.data;
};

export const saveSceneContent = async (sceneId: number, request: SceneContentRequest): Promise<SceneContentResult> => {
  const response = await api.post(`/scene-creation/scenes/${sceneId}/content`, request);
  return response.data.data;
};

// ── 场景配置文件「编辑锁」流程 ──────────────────────────────────────────────

/** 编辑锁持有人 */
export interface SceneEditor {
  editorKey: string;
  editorName?: string | null;
  lockedAt?: string | null;
}

// ── 云端控制事件（cloud_control events）──────────────────────────────────────
// events 存 scene_inspection.external.events（JSONB，camelCase 与本类型对齐），
// 经 scene_info_writer 写入 BOS scene_info.json，供仿真侧 simulation#38 消费。
// 单位：time 秒 / position UTM / radius 米 / duration 秒（见 plan §8 A-2）。

/** 触发条件类型：time=定时 / position=定区域 */
export type TriggerType = 'time' | 'position';
/** 事件模式：once=触发一次（带 duration）/ while_inside=在区域内持续 */
export type EventMode = 'once' | 'while_inside';
/** 动作类型：一阶段仅 cloud_control */
export type ActionType = 'cloud_control';
/** 云控指令：后端（simulation#38）支持全部 8 个 PlanningInterface.CloudCmd 枚举 */
export type CloudCmd =
  | 'TRAFFIC_LIGHT_PASS'
  | 'DETOUR_LEFT'
  | 'DETOUR_RIGHT'
  | 'LANE_CHANGE_LEFT'
  | 'LANE_CHANGE_RIGHT'
  | 'BACK_TO_ROUTE'
  | 'MOVE_FORWARD'
  | 'MOVE_BACKWARD';

export interface EventTrigger {
  type: TriggerType;
  /** type=time，秒，>=0 */
  time?: number;
  /** type=position，UTM 坐标 */
  position?: { x: number; y: number };
  /** type=position，米，>0 */
  radius?: number;
}

export interface EventAction {
  type: ActionType;
  cloudCmd: CloudCmd;
}

export interface SceneEvent {
  /** 前端 Form.List 行 key；提交前剥除（见 plan §3.1/A-4），后端不依赖前端 id */
  id?: string;
  trigger: EventTrigger;
  mode: EventMode;
  /** once 模式：触发次数，>=1，默认 1（count=1 即真"触发一次"） */
  count?: number;
  /** 频率（秒，>0）：once 且 count>1 时为多次触发间隔；while_inside 时为区域内持续发送间隔 */
  frequency?: number;
  action: EventAction;
}

/** acquire 返回的可改字段现值（时间/地图/故障时刻/标签为字符串，openSwitchClosedLoopSec 为 double） */
export interface SceneEditFields {
  sceneStartTime?: string | null;
  sceneEndTime?: string | null;
  mapName?: string | null;
  mapVersion?: string | null;
  issueTime?: string | null;
  /** 场景标签；submit 时传空串= 清空；不传/undefined = 不改。 */
  label?: string | null;
  /** 开环切闭环秒数（秒，double）；默认 0，负数按 -1（开环仿真）归一。submit 时传表单值，后端 noop-if-same。 */
  openSwitchClosedLoopSec?: number | null;
  /** 是否屏蔽 finish 信号（bool）；默认 false（不屏蔽=正常透传），点"是"才走屏蔽逻辑。submit 时传表单值，后端 noop-if-same。 */
  maskFinishSignal?: boolean | null;
  /** 云控指令合并策略；默认 passthrough（一起发）。submit 时传表单值，后端 noop-if-same。 */
  cloudCmdPolicy?: CloudCmdPolicy | null;
  /**
   * 云端控制事件；undefined/空数组=无事件（普通场景）。
   * 就地编辑（submit）：落 external.events + 写 scene_info.json；acquire 回吐同值。
   * 另存为（save-as）：覆盖到新场景 external.events；undefined=沿用基准 events。
   */
  events?: SceneEvent[];
}

export interface SceneEditStart {
  sceneId: number;
  editor: SceneEditor;
  fields: SceneEditFields;
}

/**
 * 点「编辑」：抢占 12h 编辑锁 + 拉取 5 个可改字段现值。
 * 被他人持有时后端返回业务错误码，axios 拦截器会弹出「xxx 正在修改场景」，
 * 此处 Promise 会 reject（调用方 catch 里不要再重复提示）。
 */
export const acquireSceneEdit = async (sceneId: number): Promise<SceneEditStart> => {
  const response = await api.post(`/scene-creation/scenes/${sceneId}/edit/acquire`);
  return response.data?.data;
};

/** 点「完成」：提交 5 个字段，后端校验持锁人后落库并释放锁 */
export const submitSceneEdit = async (
  sceneId: number,
  fields: SceneEditFields,
  dispatch = true,
): Promise<unknown> => {
  const response = await api.post(`/scene-creation/scenes/${sceneId}/edit/submit`, {
    fields,
    dispatch,
  });
  return response.data?.data;
};

/** 取消 / 关闭编辑：释放自己持有的锁 */
export const cancelSceneEdit = async (sceneId: number): Promise<void> => {
  await api.post(`/scene-creation/scenes/${sceneId}/edit/cancel`);
};

/** 另存为新场景（F1）入参：新名 + 目标树节点 + 事件 */
export interface SaveAsSceneParams {
  /** 选填：留空则后端自动派生「基准名_序号」 */
  newName?: string;
  targetTreeNodeId: number;
  /** undefined=沿用基准场景 events；传数组（含空=清空）=覆盖到新场景 */
  events?: SceneEvent[];
  /** 场景描述：undefined=沿用基准描述；字符串（含空串=清空）=覆盖到新场景 */
  description?: string;
}

/**
 * 另存为新场景（F1）：基于基准场景复制实体 + 叠加 events + 挂目标树节点。
 * 基准场景不变；成功返回新场景实体（含 id / name / sceneInfoAddress）。
 */
export const saveAsScene = async (
  baseSceneId: number,
  params: SaveAsSceneParams,
): Promise<unknown> => {
  const response = await api.post(
    `/scene-creation/scenes/${baseSceneId}/save-as`,
    params,
  );
  return response.data?.data;
};

/** 单字段改动前后值。
 *
 *  时间字段在「改时间触发的重跑」链路上会多两个键：``requested`` = 用户改的值、
 *  ``engineOnly`` = 用户没改、引擎重跑时顺手调整的字段。老消费者只读
 *  before/after，多出来的键可忽略；``after`` 恒等于该版本快照里的真实值。 */
export interface SceneChangeDiff {
  before: string | number | null;
  after: string | number | null;
  /** 用户改的值（起止时间三段的中间那一段） */
  requested?: string | number | null;
  /** 用户没改，引擎重跑时调整的字段 */
  engineOnly?: boolean;
}

/** 时间重跑链路的审计块（R1：沿用编辑那条版本行，不新增占位行）。
 *  status: running（重跑中）/ resolved（已重跑完）/ superseded（被后续构建顶替）。 */
/** 引擎一次回调带回的原因（平台 `_normalize_resolve_reason` 的规整结果）。
 *  `code` 非空 = 命中 `_RESOLVE_REASON_TEXT` 里认识的推导分支码;为空 = 引擎自由文本
 *  （`scene_qc:pass (mileage=…)` 这类审计串）。 */
export interface SceneEngineReason {
  code?: string | null;
  text?: string | null;
  detail?: string | null;
  stage?: string | null;
  /** 留档时间（毫秒 3 位字符串，同 changed_fields 口径） */
  at?: string | null;
  /** 这次回调把场景推到的状态：QUALITY_CHECKING=Step 9，COMPLETED=QC 定稿 */
  sceneStatus?: string | null;
}

export interface SceneTimeRerun {
  status: 'running' | 'resolved' | 'superseded' | string;
  jobId?: number | null;
  dispatchedAt?: string | null;
  dispatchCount?: number;
  resolvedAt?: string | null;
  supersededAt?: string | null;
  final?: Record<string, string | null>;
  /** 用户改过的时间字段最终是否全部回到改动前的值 */
  revertedToOriginal?: boolean;
  reason?: { code?: string | null; text?: string | null; detail?: string | null; stage?: string | null } | null;
  sceneStatus?: string | null;
  sceneInfo?: string | null;
  callbacks?: number;
}

/** 场景版本(RFC scene-version-design 一期 PR-2)。
 *  同一张 scene_change_log 表,前端「更新记录/版本历史」抽屉直接消费。 */
export interface SceneVersion {
  id: number;
  /** scene_change_log.id;回退接口用它定位来源版本 */
  logId: number;
  sceneId: number;
  /** 场景内版本号;老 log 可能 null(PR-1 前的历史) */
  versionNo: number | null;
  isCurrent: boolean;
  /** 'initial' | 'edit' | 'rollback' */
  source: string;
  /** 版本来源及文件快照属性 */
  sourceRef: {
    fromLogId?: number;
    fromVersionNo?: number;
    /** 本版对应的生成 Job id（rebuild / generate 占位行,前端跳任务详情用） */
    jobId?: number | null;
    /** 时间修改后的中间记录，场景文件尚未完成重新生成，不可回退。 */
    requiresRegeneration?: boolean;
    changedFiles?: Record<string, { before: string | null; after: string; size: number }>;
    /** 改时间触发的重跑链路（R1） */
    timeRerun?: SceneTimeRerun | null;
    /** 引擎历次回调回传的原因流水（首建/重构建/QC 定稿/重跑都记,追加式,最新在后）。
     *  比 timeRerun.reason 全:后者只服务重跑链路的展示,这里是「引擎说过什么」的原始审计。 */
    resolveReasons?: SceneEngineReason[] | null;
  } | null;
  /** 版本化 BOS 对象 key;null=老版本未快照/已被 GC 过期,不可预览/回退 */
  versionBosKey: string | null;
  changedFields: Record<string, SceneChangeDiff>;
  creator?: string | null;
  changeTime?: string | null;
}

export interface SceneVersionsPage {
  items: SceneVersion[];
  total: number;
  current: number;
  pageSize: number;
  totalPages: number;
}

/** 查询场景版本历史(分页;默认 pageSize=5,与「更新记录」抽屉一致)。 */
export const getSceneVersions = async (
  sceneId: number,
  params: { current?: number; pageSize?: number } = {},
): Promise<SceneVersionsPage> => {
  const current = params.current ?? 1;
  const pageSize = params.pageSize ?? 5;
  const response = await api.get(
    `/scene-creation/scenes/${sceneId}/versions`,
    { params: { current, pageSize } },
  );
  const body = response.data;
  const items: SceneVersion[] = Array.isArray(body?.data) ? body.data : [];
  const page = body?.page ?? {};
  return {
    items,
    current: page.current ?? current,
    pageSize: page.pageSize ?? pageSize,
    total: page.totalElements ?? items.length,
    totalPages: page.totalPages ?? Math.max(1, Math.ceil(items.length / pageSize)),
  };
};

/** 回退场景到某版本(RFC scene-version-design 一期 PR-3)。
 *  语义:不删后续版本,用旧版内容再建一个新版本(source='rollback')。
 *  回退**只覆盖 scene_info.json 里的字段**(时间/地图/标签/开环秒/故障时刻);
 *  不在 scene_info.json 的字段(如 description)本次回退不动。 */
export const rollbackSceneToVersion = async (
  sceneId: number,
  targetLogId: number,
): Promise<SceneVersion> => {
  const response = await api.post(
    `/scene-creation/scenes/${sceneId}/rollback`,
    { targetLogId },
  );
  return response.data?.data as SceneVersion;
};

/** 场景所属的一个场景集摘要;path 是 "根 / 子目录 / 场景集" 的完整名称链。 */
export interface SceneSetOfScene {
  treeNodeId: number;
  name: string;
  path: string;
}

/** 查询场景所属的所有「场景集」(scene_tree.node_flag=1);目录节点不参与展示。 */
export const getSceneSetsOfScene = async (
  sceneId: number,
): Promise<SceneSetOfScene[]> => {
  const response = await api.get(`/scene/inspection/${sceneId}/scene-sets`);
  const data = response.data?.data;
  return Array.isArray(data) ? data : [];
};

// ────────────────────────────────────────────────────────────
// 场景批量编辑 (POST /scene-creation/scenes/batch-update)
//
// 本期允许改的字段（None/空串/与现值相同均跳过；label / description 语义特殊：
// 空串 = 清空为 NULL）：
//   sceneStartTime / sceneEndTime / issueTime /
//   mapName / mapVersion / openSwitchClosedLoopSec / maskFinishSignal /
//   label / description
// 服务端对未列出字段一律 422 拒收（extra=forbid）。
// 命中真实改动的场景锁定为 scen_mode=MANUAL,后续不被 Jira 自动刷新覆盖。
// ────────────────────────────────────────────────────────────

/** 单条 patch:字段与后端 SceneFieldPatch 对齐,alias 全部走 camelCase。 */
export interface SceneFieldPatch {
  sceneStartTime?: string | null;
  sceneEndTime?: string | null;
  issueTime?: string | null;
  mapName?: string | null;
  mapVersion?: string | null;
  openSwitchClosedLoopSec?: number | null;
  maskFinishSignal?: boolean | null;
  cloudCmdPolicy?: CloudCmdPolicy | null;
  label?: string | null;
  description?: string | null;
  /** 云端控制事件;undefined=不改;空数组=清空;非空数组=覆盖 external.events */
  events?: SceneEvent[];
}

export interface SceneBatchUpdateItem {
  sceneId: number;
  fields: SceneFieldPatch;
}

export interface SceneBatchUpdateOk {
  sceneId: number;
  name?: string | null;
}

export interface SceneBatchUpdateFail {
  sceneId: number;
  /** validate=入参校验/未找到场景;db=ORM 落库;bos=scene_info.json 写入 */
  stage: string;
  error: string;
}

export interface SceneBatchUpdateDispatchInfo {
  /** 生成 Job 的本地主键;点跳转到「场景生成 → 任务详情」页用它。 */
  jobId?: number | null;
  /** compute-flow 侧 job_id(用于日志排查,前端一般不用暴露)。 */
  dispatchJobId?: string | null;
  /** 实际发起了几条场景 → 提示"已提交 N 个场景重跑" */
  total?: number | null;
  /** 因地图不存在被跳过的场景数;非空时前端应提示用户 */
  skippedNoMap?: number | null;
  skippedScenes?: Array<{ id: number; name: string; error: string }> | null;
  /** dispatch 阶段失败(比如 CF 端拒收),但 update 已经落库;前端应提示"生成任务发起失败" */
  error?: string | null;
}

export interface SceneBatchUpdateResult {
  updated: SceneBatchUpdateOk[];
  failed: SceneBatchUpdateFail[];
  /** 命中"起止时间改动 + dispatch=true"时非空;否则 null。参见 SceneBatchUpdateDispatchInfo。 */
  dispatch?: SceneBatchUpdateDispatchInfo | null;
}

/**
 * 批量编辑场景。
 *
 * @param items 每条一份 partial patch（None/空串/与现值相同均跳过）
 * @param dispatch true=命中起止时间改动的场景会触发 generate-scene 重跑;
 *                 单场景编辑常规不需要,传 false 只落库
 */
export const batchUpdateScenes = async (
  items: SceneBatchUpdateItem[],
  dispatch: boolean = false,
): Promise<SceneBatchUpdateResult> => {
  const response = await api.post('/scene-creation/scenes/batch-update', {
    items,
    dispatch,
  });
  return response.data?.data as SceneBatchUpdateResult;
};

/**
 * 获取场景列表
 */
export const getSceneList = async (params: SceneListParams): Promise<SceneListResponse> => {
  const response = await api.get("/scene-tree/list", { params: cleanParams(params) });
  const { total, list, page } = parseListResponse(response.data);
  return {
    total,
    scenes: list,
    page,
  };
};

// ───────────────────────────────────────────────────────────────────────────
// /scene-stats/*  — 场景管理「概览」主页面数据源
//
// 与后端 ``simulation_platform.api.schemas.scene_stats`` 一一对应：
// - summary：3 个统计卡（场景总数 / Jira 扫描 / 数据挖掘）
// - trend：折线图（按 day/month/year 分桶 + 缺桶补 0）
//
// 失败业务错误（粒度/source/区间反向 / 数据量超 50w）走 ``code != 0``，
// 由调用方按 ``response.data.message`` 提示用户。
// ───────────────────────────────────────────────────────────────────────────

export interface SceneSourceSummary {
  total: number;
  /** 已禁用(is_deleted=1)场景数;概览左列独立展示,不参与 bySource / bySimType */
  disabledTotal: number;
  /** 按 SceneSourceEnum 全部值返回;后端保证缺的 source 补 0。 */
  bySource: Record<SceneSource, number> & Record<string, number>;
  /** 按 sim_type 分桶 (planning/perception/map/other/unclassified);后端补 0。 */
  bySimType: Record<string, number>;
}

export type SceneTrendGranularity = "day" | "month" | "year";

export interface SceneTrendBucket {
  /** ISO 桶标签：day=YYYY-MM-DD / month=YYYY-MM / year=YYYY */
  bucket: string;
  /** 每个 source 在该桶的新增场景数；key 顺序与请求 ``sources`` 顺序一致 */
  counts: Record<string, number>;
}

export interface SceneTrendData {
  granularity: SceneTrendGranularity;
  /** delta=每桶新增;cumulative=每桶末尾累计存活 */
  mode: "delta" | "cumulative";
  /** ISO 字符串,按粒度归一化后的实际查询区间起点 */
  startDate: string;
  endDate: string;
  sources: string[];
  buckets: SceneTrendBucket[];
}

export interface SceneSourceSummaryParams {
  /** 限定统计场景为某场景树节点(目录/叶子)下的场景;不传=全库 */
  treeNodeId?: number;
}

export const getSceneSourceSummary = async (
  params: SceneSourceSummaryParams = {},
): Promise<SceneSourceSummary> => {
  const response = await api.get("/scene-stats/summary", {
    params: cleanParams(params),
  });
  const data = response.data?.data;
  return {
    total: typeof data?.total === "number" ? data.total : 0,
    disabledTotal: typeof data?.disabledTotal === "number" ? data.disabledTotal : 0,
    bySource: data?.bySource ?? {},
    bySimType: data?.bySimType ?? {},
  };
};

export interface SceneTrendParams {
  /** ISO 字符串;缺省由后端按粒度回退(day=最近 7 天,month=最近 12 月,year=最近 5 年) */
  startDate?: string;
  endDate?: string;
  granularity?: SceneTrendGranularity;
  /** 只画指定 source 的线,多值用数组;不传则返回所有 enum 值 */
  sources?: string[];
  /** 按 sim_type 过滤(planning/perception/map/other/unclassified);不传=不过滤 */
  simType?: string;
  /** 折线口径:delta(默认,新增)/ cumulative(累计存活) */
  mode?: "delta" | "cumulative";
  /** 限定统计范围到某场景树节点(目录/叶子)下的场景;不传=全库 */
  treeNodeId?: number;
}

export const getSceneTrend = async (
  params: SceneTrendParams,
): Promise<SceneTrendData> => {
  const response = await api.get("/scene-stats/trend", {
    params: cleanParams(params),
  });
  const data = response.data?.data;
  return {
    granularity: data?.granularity ?? "day",
    mode: data?.mode ?? "delta",
    startDate: data?.startDate ?? "",
    endDate: data?.endDate ?? "",
    sources: Array.isArray(data?.sources) ? data.sources : [],
    buckets: Array.isArray(data?.buckets) ? data.buckets : [],
  };
};


// ─── 场景评论（3 入口共享：任务列表 / 场景管理 / 全量场景列表）─────────────────

/** 评论展示 VO（与后端 SceneCommentVo 对齐）。 */
export interface SceneCommentVo {
  id: number;
  sceneId: number;
  jobId: number | null;
  taskId: number | null;
  title: string;
  content: string;
  creator: string;
  creatorName: string | null;
  createTime: string;
  updateTime: string | null;
}

export interface SceneCommentListVo {
  items: SceneCommentVo[];
  total: number;
}

/** 评论摘要：列表入口的"评论"列展示用 → "标题(N)" 或 "评论"。 */
export interface SceneCommentSummary {
  sceneId: number;
  total: number;
  latestTitle: string | null;
}

/** 创建评论；jobId/taskId 必须同时给（任务维度）或同时不给（场景维度）。 */
export const createSceneComment = async (params: {
  sceneId: number;
  jobId?: number | null;
  taskId?: number | null;
  title?: string | null;
  content: string;
}): Promise<SceneCommentVo> => {
  const body: Record<string, unknown> = {
    sceneId: params.sceneId,
    content: params.content,
  };
  if (params.title != null && params.title !== "") {
    body.title = params.title;
  }
  if (params.jobId != null && params.taskId != null) {
    body.jobId = params.jobId;
    body.taskId = params.taskId;
  }
  const resp = await api.post("/scene-comment", body);
  return resp.data?.data;
};

/** 批量摘要：列表入口一次拉一组场景的"标题(N)"。空 sceneIds 直接返回 {}. */
export const getSceneCommentSummaryBatch = async (params: {
  sceneIds: number[];
  jobId?: number;
}): Promise<Record<number, SceneCommentSummary>> => {
  if (!params.sceneIds || params.sceneIds.length === 0) return {};
  const ids = Array.from(new Set(params.sceneIds.filter((x) => x > 0)));
  if (!ids.length) return {};
  const q: Record<string, unknown> = { sceneIds: ids.join(",") };
  if (params.jobId != null) q.jobId = params.jobId;
  const resp = await api.get("/scene-comment/summary", { params: q });
  const items: SceneCommentSummary[] = resp.data?.data?.items ?? [];
  const out: Record<number, SceneCommentSummary> = {};
  for (const it of items) out[it.sceneId] = it;
  return out;
};

/** 列表查询：jobId 给值=任务列表入口；不给=场景管理 / 全量入口（看历史全部）。 */
export const listSceneComments = async (params: {
  sceneId: number;
  jobId?: number;
  keyword?: string;
  titleKeyword?: string;
  current?: number;
  pageSize?: number;
}): Promise<SceneCommentListVo> => {
  const resp = await api.get("/scene-comment", { params: cleanParams(params) });
  const data = resp.data?.data;
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    total: data?.total ?? 0,
  };
};

/** 编辑评论（仅作者本人）。``title=null`` 不动旧标题，``""`` 服务端按新 content 重新派生。 */
export const updateSceneComment = async (
  commentId: number,
  content: string,
  title?: string | null,
): Promise<SceneCommentVo> => {
  const body: Record<string, unknown> = { content };
  if (title !== undefined) body.title = title;
  const resp = await api.put(`/scene-comment/${commentId}`, body);
  return resp.data?.data;
};

/** 物理删除评论（仅作者本人）。 */
export const deleteSceneComment = async (commentId: number): Promise<void> => {
  await api.delete(`/scene-comment/${commentId}`);
};

// ============================ 定时/延迟/循环创建 Job ============================
// 对应 docs/schedule-v3.md §4 的 6 个端点。后端 schema 见
// simulation_platform/api/schemas/schedule.py。
// 「开启定时」不走同步 preflight——分支/包对错到点才会暴露，前端必须自行
// 单次触发确保参数正确。失败时 status 切 failed、job_schedule_log 留完整错误、
// 飞书推 owner。

/** 触发方式：once_at=定时绝对时间；once_after=延时相对 now；cron=循环。 */
export type ScheduleKind = "once_at" | "once_after" | "cron";

/** 排程状态：active=等待触发或循环进行中；done=一次性触发成功终态；
 *  failed=一次性触发失败终态；cancelled=用户手工取消。
 *  cron 失败仍留 active + fail_count++（不阻塞下次）。 */
export type ScheduleStatus = "active" | "done" | "failed" | "cancelled";

/** 触发日志结果：success / failed。 */
export type ScheduleLogStatus = "success" | "failed";

/** 失败原因分类（6 档，与后端 ScheduleErrorTypeEnum 对齐）。 */
export type ScheduleErrorType =
  | "branch_not_found"
  | "repo_access"
  | "compile_failed"
  | "dispatch_failed"
  | "timeout"
  | "unknown";

/** 延时单位（once_after 用）。 */
export type ScheduleDelayUnit = "minutes" | "hours" | "days";

/** 触发目标异步接口：create=/job/create/async；bazel_create=/job/bazel/create/async。 */
export type ScheduleJobEndpoint = "create" | "bazel_create";

/** 创建排程入参（对应后端 ScheduleCreateRequest）。 */
export interface ScheduleCreatePayload {
  name: string;
  job_endpoint: ScheduleJobEndpoint;
  /** 原 create 入参完整快照（service 触发时同进程 await 对应异步 service 方法）。 */
  payload: Record<string, any>;
  kind: ScheduleKind;
  /** once_at 用：ISO 8601 绝对时间。 */
  fire_at?: string;
  /** once_after 用：延时数值（≥1）。 */
  delay_value?: number;
  /** once_after 用：延时单位。 */
  delay_unit?: ScheduleDelayUnit;
  /** cron 用：5 段标准 cron 表达式，最小间隔 5min。 */
  cron_expr?: string;
  /** cron 用：IANA 时区名，默认 Asia/Shanghai。 */
  cron_tz?: string;
}

/** 排程记录响应（对应后端 ScheduleVo）。 */
export interface ScheduleItem {
  id: number;
  name: string;
  /** SSO owner。 */
  owner: string;
  job_endpoint: ScheduleJobEndpoint | string;
  payload: Record<string, any>;
  kind: ScheduleKind | string;
  /** once_at / once_after 入库后都会算成绝对时间存 fire_at；cron 为 null。 */
  fire_at: string | null;
  cron_expr: string | null;
  cron_tz: string | null;
  next_fire_at: string;
  status: ScheduleStatus | string;
  last_fired_at: string | null;
  last_job_id: number | null;
  last_error: string | null;
  fire_count: number;
  fail_count: number;
  created_at: string;
  updated_at: string;
  /** 派生：当前 viewer 是否能编辑（owner 自有或 sim_admin）。 */
  can_edit: boolean;
  /** 派生：当前 viewer 是否能删除。 */
  can_delete: boolean;
  /** 详情接口额外回填最近 5 条触发日志；列表接口为 null。 */
  recent_logs: ScheduleLogItem[] | null;
}

/** 触发日志条目（对应后端 ScheduleLogVo）。 */
export interface ScheduleLogItem {
  id: number;
  schedule_id: number;
  fired_at: string;
  trigger_status: ScheduleLogStatus | string;
  /** 成功时回填的 Job ID。 */
  job_id: number | null;
  /** 失败时分类。 */
  error_type: ScheduleErrorType | string | null;
  error_msg: string | null;
  pod: string | null;
  /** 触发当时的 payload 快照。 */
  payload_snapshot: Record<string, any> | null;
  duration_ms: number | null;
}

/** cron 预演响应（对应后端 SchedulePreviewVo）。 */
export interface SchedulePreviewResult {
  /** 未来 N 次触发时间（ISO 8601 字符串）。 */
  times: string[];
  /** 人话翻译（如「每天 09:00 触发」）。 */
  human_readable: string;
  cron_expr: string;
  cron_tz: string;
}

/** 创建定时记录（不立即建 Job，到点由后台协程触发）。 */
export const createSchedule = async (
  body: ScheduleCreatePayload,
): Promise<ScheduleItem> => {
  const response = await api.post("/schedules", body);
  return response.data?.data as ScheduleItem;
};

/** 定时列表分页（可按 status 过滤）。 */
export const listSchedules = async (params: {
  page?: number;
  pageSize?: number;
  status?: ScheduleStatus | string;
}): Promise<{ items: ScheduleItem[]; total: number; page: number; size: number }> => {
  const response = await api.get("/schedules", {
    params: cleanParams({
      current: params.page,
      pageSize: params.pageSize,
      status: params.status,
    }),
  });
  const { total, list, page } = parseListResponse(response.data);
  return {
    items: list as ScheduleItem[],
    total,
    page: page?.current ?? params.page ?? 1,
    size: page?.pageSize ?? params.pageSize ?? 50,
  };
};

/** 排程详情（含最近 5 条触发日志 recent_logs）。 */
export const getSchedule = async (id: number): Promise<ScheduleItem> => {
  const response = await api.get(`/schedules/${id}`);
  return response.data?.data as ScheduleItem;
};

/** 取消排程（仅 active 可取消，且只能取消自己的）。成功返回 204 无响应体。 */
export const cancelSchedule = async (id: number): Promise<void> => {
  await api.delete(`/schedules/${id}`);
};

/**
 * 物理删除排程（level-connected to log 级联删除）。任何状态都能删，
 * 只能删自己创建的。成功返回 204 无响应体。
 */
export const deleteSchedule = async (id: number): Promise<void> => {
  await api.delete(`/schedules/${id}/hard`);
};

/**
 * 手动触发一次 —— 不影响定时/延时/循环，只往 log 表加一行。
 * 同步跑：接口 200 时返回本次触发的日志（含 job_id 或 error）。
 * 已 done / failed / cancelled 的 schedule 也允许触发（复跑一遍）。
 */
export const manualFireSchedule = async (id: number): Promise<ScheduleLogItem> => {
  const response = await api.post(`/schedules/${id}/fire`);
  return response.data?.data as ScheduleLogItem;
};

/** 触发日志分页（日志抽屉用）。倒序按 fired_at DESC。 */
export const listScheduleLogs = async (
  id: number,
  params: { page?: number; pageSize?: number },
): Promise<{ items: ScheduleLogItem[]; total: number; page: number; size: number }> => {
  const response = await api.get(`/schedules/${id}/logs`, {
    params: cleanParams({
      current: params.page,
      pageSize: params.pageSize,
    }),
  });
  const { total, list, page } = parseListResponse(response.data);
  return {
    items: list as ScheduleLogItem[],
    total,
    page: page?.current ?? params.page ?? 1,
    size: page?.pageSize ?? params.pageSize ?? 50,
  };
};

/** cron 表达式预演未来 N 次（创建表单调）。不入库、不校验最小间隔。 */
export const previewCron = async (
  cronExpr: string,
  cronTz: string = "Asia/Shanghai",
  n: number = 3,
): Promise<SchedulePreviewResult> => {
  const response = await api.post("/schedules/preview", {
    cron_expr: cronExpr,
    cron_tz: cronTz,
    n,
  });
  return response.data?.data as SchedulePreviewResult;
};


// ─────────────────────────────────────────────────────────────
// 打版触发规则(release-trigger-rule)+ 打版事件(release-build)
// 详见 docs/kafka-release-build-onboarding.md
// 后端路由挂在 /admin 前缀下, 权限点 release_trigger_rule:manage
// ─────────────────────────────────────────────────────────────

export interface ReleaseTriggerRuleVo {
  id: number;
  name: string;
  versionRegex: string;
  jobParams: Record<string, unknown>;
  enabled: boolean;
  description?: string | null;
  creator?: string | null;
  updater?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReleaseTriggerRuleRequest {
  name: string;
  versionRegex: string;
  jobParams: Record<string, unknown>;
  enabled?: boolean;
  description?: string | null;
}

export type UpdateReleaseTriggerRuleRequest = Partial<CreateReleaseTriggerRuleRequest>;

export interface ReleaseBuildVo {
  id: number;
  buildId: string;
  version?: string | null;
  rawEvent: Record<string, unknown>;
  receivedAt: string;
}

export const listReleaseTriggerRules = async (): Promise<ReleaseTriggerRuleVo[]> => {
  const response = await api.get("/admin/release-trigger-rules");
  return (response.data?.data ?? []) as ReleaseTriggerRuleVo[];
};

/** 新建规则时前端预填的 Job 参数默认值 (sceneSetIds 由后端从 yaml
 *  neolix.daily.scene-set-ids 取,test=33 / prod=38 由 profile 决定)。 */
export const getReleaseTriggerJobParamsTemplate = async (): Promise<
  Record<string, unknown>
> => {
  const response = await api.get(
    "/admin/release-trigger-rules/job-params-template",
  );
  return (response.data?.data ?? {}) as Record<string, unknown>;
};

export const createReleaseTriggerRule = async (
  req: CreateReleaseTriggerRuleRequest,
): Promise<ReleaseTriggerRuleVo> => {
  const response = await api.post("/admin/release-trigger-rules", req);
  return response.data?.data as ReleaseTriggerRuleVo;
};

export const updateReleaseTriggerRule = async (
  id: number, req: UpdateReleaseTriggerRuleRequest,
): Promise<ReleaseTriggerRuleVo> => {
  const response = await api.put(`/admin/release-trigger-rules/${id}`, req);
  return response.data?.data as ReleaseTriggerRuleVo;
};

export const deleteReleaseTriggerRule = async (id: number): Promise<void> => {
  await api.delete(`/admin/release-trigger-rules/${id}`);
};

/** 试正则:给一个 version, 返回命中的启用规则(与后台 dispatch 口径一致) */
export const testReleaseTriggerRules = async (
  version: string,
): Promise<ReleaseTriggerRuleVo[]> => {
  const response = await api.post("/admin/release-trigger-rules/test", { version });
  return (response.data?.data ?? []) as ReleaseTriggerRuleVo[];
};

/** 最近打版事件分页(配置页下半屏用) */
export const listReleaseBuilds = async (params: {
  current?: number;
  pageSize?: number;
  version?: string;
  buildId?: string;
}): Promise<{ items: ReleaseBuildVo[]; total: number; current: number; pageSize: number }> => {
  const response = await api.get("/admin/release-builds", {
    params: cleanParams({
      current: params.current,
      pageSize: params.pageSize,
      version: params.version,
      buildId: params.buildId,
    }),
  });
  const { total, list, page } = parseListResponse(response.data);
  return {
    items: list as ReleaseBuildVo[],
    total,
    current: page?.current ?? params.current ?? 1,
    pageSize: page?.pageSize ?? params.pageSize ?? 20,
  };
};

/** Job 冻结的模型清单与所选主版本当前内容的对比（后端 /job/model-release-status）。 */
export interface JobModelReleaseStatus {
  jobId: number;
  /** 未选择模型主版本时为 null。 */
  modelRelease: string | null;
  frozenSnapshotSha256?: string;
  /** 主版本当前内容的摘要；主版本已不存在 / 无法解析时为 null，原因见 currentError。 */
  currentSnapshotSha256?: string | null;
  currentError?: string | null;
  /** true：主版本在接受该 Job 之后被修改过，重试 task 仍用冻结的旧清单。 */
  stale: boolean;
}

export const getJobModelReleaseStatus = async (jobId: number): Promise<JobModelReleaseStatus> => {
  const response = await api.get("/job/model-release-status", { params: { jobId } });
  return response.data?.data as JobModelReleaseStatus;
};

// ================= 感知数据刷写（PE_DATA） =================
// 走 /perception-refresh/* 后端接口，与数据更新页共用 menu:prediction:update 权限。

/** 版本状态：running / succeeded（全部归档）/ partial（部分）/ failed（无）。 */
export type PerceptionRefreshVersionStatus = 'running' | 'succeeded' | 'partial' | 'failed';
/** 场景状态：succeeded / failed / no_output（task 成功但没有感知 record）。 */
export type PerceptionRefreshSceneStatus = 'succeeded' | 'failed' | 'no_output';

/** 列表页一行 = 一个 PE_DATA Job（一个版本）。 */
export interface PerceptionRefreshJob {
  jobId: number;
  jobName?: string | null;
  jobStatus?: number | null;
  jobStatusName?: string | null;
  sceneSetIds?: string | null;
  perceptionVersion: string;
  groupTag: string;
  versionSeq: number;
  status: PerceptionRefreshVersionStatus;
  sceneTotal: number;
  sceneSucceeded: number;
  sceneFailed: number;
  sceneNoOutput: number;
  bosPath: string;
  algorithmVersion?: string | null;
  modelRelease?: string | null;
  creator?: string | null;
  createTime?: string | null;
  finalizeTime?: string | null;
}

/** 明细页一行 = 一个场景的归档结果。 */
export interface PerceptionRefreshScene {
  taskId: number;
  sceneId: number;
  pklStem?: string | null;
  status: PerceptionRefreshSceneStatus;
  bosPath?: string | null;
  copiedFiles: number;
  reason?: string | null;
  updateTime?: string | null;
}

/** PE_DATA 的任务类型 id + 名称；未初始化时后端报错，由 axios 拦截器提示。 */
export const getPerceptionRefreshTaskType = async (): Promise<{ taskTypeId: number; name: string }> => {
  const response = await api.get("/perception-refresh/task-type-id");
  return response.data?.data;
};

export const getPerceptionRefreshJobs = async (params: {
  groupTag?: string;
  status?: PerceptionRefreshVersionStatus;
  current?: number;
  pageSize?: number;
}): Promise<{ total: number; list: PerceptionRefreshJob[] }> => {
  const response = await api.get("/perception-refresh/jobs", { params: cleanParams(params) });
  const { total, list } = parseListResponse(response.data);
  return { total, list };
};

/**
 * 场景明细按 id 游标翻页：afterId 传上一页的 nextAfterId（不传 = 第一页）；
 * withTotal=false 时不计数、total 返回 null（单 Job 几十万场景，翻页不必每次 COUNT）。
 */
export const getPerceptionRefreshScenes = async (
  jobId: number,
  params: {
    status?: PerceptionRefreshSceneStatus;
    afterId?: number;
    pageSize?: number;
    withTotal?: boolean;
  },
): Promise<{ list: PerceptionRefreshScene[]; total: number | null; nextAfterId: number | null }> => {
  const response = await api.get(`/perception-refresh/jobs/${jobId}/scenes`, {
    params: cleanParams(params),
  });
  const data = response.data?.data ?? {};
  return {
    list: data.list ?? [],
    total: data.total ?? null,
    nextAfterId: data.nextAfterId ?? null,
  };
};

/** 重新归档：对已结束但没归档成功的场景补做归档（正常跑完的重拷产物，崩溃 / 异常的补失败记录）。后台执行，接口立即返回。 */
export const rearchivePerceptionRefreshJob = async (jobId: number): Promise<void> => {
  await api.post(`/perception-refresh/jobs/${jobId}/rearchive`);
};

/** 「复制」用：取 PE_DATA Job 的完整 JobItem（主 /job/list 因 enable_flag=0 查不到）。 */
export const getPerceptionRefreshCopySource = async (jobId: number): Promise<JobItem> => {
  const response = await api.get(`/perception-refresh/jobs/${jobId}/copy-source`);
  return response.data?.data as JobItem;
};

// ───────────────────────── 行为对比（仿真 diff）─────────────────────────
// 对应后端 /behavior-compare/*（router 级 menu:behavior-compare 权限）。
// 页面见 pages/BehaviorCompare；判定口径见 docs/behavior-compare.md。

export type BehaviorLabel =
  | "NEEDS_REVIEW"
  | "SIGNIFICANT"
  | "SLIGHT"
  | "CONSISTENT"
  | "INCONCLUSIVE";

export interface BehaviorJobBrief {
  jobId: number;
  jobName?: string | null;
  algorithmVersion?: string | null;
  createTime?: string | null;
  totalTaskNum: number;
}

export interface BehaviorCompareSummary {
  compareId: number;
  status: string;
  ruleVersion: string;
  thresholds: Record<string, { floor: number; significant: number }>;
  curJob?: BehaviorJobBrief | null;
  baseJob?: BehaviorJobBrief | null;
  commonSceneNum: number;
  onlyOneSideNum: number;
  /** 可比较场景 = 共同场景 - 无法判断 */
  comparableNum: number;
  labelCounts: Partial<Record<BehaviorLabel, number>>;
  strongCounts: Record<string, number>;
  verdictMismatchNum: number;
  noiseBaseline?: string | null;
  costSeconds?: number | null;
  source: string;
  message?: string | null;
  createTime?: string | null;
  prevCompareId?: number | null;
  /** 需复核数较上一次对比的变化 */
  prevReviewDelta?: number | null;
  /** true = 只有翻转场景的结果，其余还在算：计数只含已算部分，不能当整体比例 */
  partial?: boolean;
  flippedTotal?: number | null;
  otherTotal?: number | null;
}

export interface BehaviorMainDiff {
  code: string;
  text: string;
  strong: boolean;
}

export interface BehaviorSceneRow {
  sceneId: number;
  sceneName?: string | null;
  apiCode?: string | null;
  curTaskId?: number | null;
  baseTaskId?: number | null;
  curVerdict?: boolean | null;
  baseVerdict?: boolean | null;
  label: BehaviorLabel;
  labelName: string;
  verdictMismatch: boolean;
  strongEvents: { code: string; name: string; detail?: string | null }[];
  mainDiffs: BehaviorMainDiff[];
  worst2s: Record<string, number | null>;
  /** 运动 / 交互 / 动力 三项摘要分 0~1 */
  summary: { motion?: number; interaction?: number; dynamics?: number };
  curve?: number[] | null;
  hasFailWindows: boolean;
}

export interface BehaviorSceneDetail extends BehaviorSceneRow {
  scores: Record<string, unknown>;
  facts: Record<string, unknown>;
  failWindows: Record<string, unknown>[];
  detail?: {
    series?: Record<string, number[]>;
    meta?: Record<string, unknown>;
    [k: string]: unknown;
  } | null;
}

export interface BehaviorCompareQuery {
  compareId?: number;
  jobId?: number;
  baseJobId?: number;
}

export interface BehaviorSceneQuery {
  compareId: number;
  label?: BehaviorLabel;
  strong?: string;
  verdictMismatch?: boolean;
  keyword?: string;
  sort?: string;
  desc?: boolean;
  current?: number;
  pageSize?: number;
}

export const getBehaviorCompareSummary = async (
  q: BehaviorCompareQuery,
): Promise<BehaviorCompareSummary | null> => {
  // 「还没有报告」是预期状态：区块自己显示提示（并给出发起入口），不要再弹一条全局错误
  const response = await api.get("/behavior-compare/summary", {
    params: cleanParams({ ...q }),
    _skipErrorHandler: true,
  } as any);
  return response.data?.data || null;
};

export const getBehaviorCompareScenes = async (
  q: BehaviorSceneQuery,
): Promise<{ rows: BehaviorSceneRow[]; total: number }> => {
  const response = await api.get("/behavior-compare/scenes", { params: cleanParams({ ...q }) });
  return {
    rows: response.data?.data || [],
    total: response.data?.page?.totalElements ?? 0,
  };
};

export const getBehaviorCompareSceneDetail = async (
  compareId: number,
  sceneId: number,
): Promise<BehaviorSceneDetail | null> => {
  const response = await api.get(`/behavior-compare/scenes/${sceneId}`, { params: { compareId } });
  return response.data?.data || null;
};

export const rejudgeBehaviorCompare = async (
  compareId: number,
  thresholds?: Record<string, { floor: number; significant: number }>,
): Promise<BehaviorCompareSummary | null> => {
  const response = await api.post(
    "/behavior-compare/rejudge",
    { thresholds: thresholds ?? null },
    { params: { compareId } },
  );
  return response.data?.data || null;
};

/** 导出 CSV 的下载地址（带当前筛选）；浏览器直接 GET，token 走 axios 实例以外时用 fetch。 */
export const exportBehaviorCompareCsv = async (q: BehaviorSceneQuery): Promise<Blob> => {
  const response = await api.get("/behavior-compare/export", {
    params: cleanParams({ ...q, current: undefined, pageSize: undefined }),
    responseType: "blob",
  });
  return response.data as Blob;
};

/** 一对任务在共有场景上的行为指纹就绪情况（没有对比报告时也能回答「指纹齐不齐、要不要补算」）。 */
export interface BehaviorSideReadiness {
  jobId: number;
  total: number;
  ready: number;
  failed: number;
  absent: number;
  unknown: number;
}

export type BehaviorReadinessState =
  | "report" // 已有对比报告
  | "ready" // 指纹齐全，可直接对比
  | "partial" // 部分场景已就绪
  | "missing" // 需补算
  | "unknown" // 探测无法判定（BOS 抖动），稍后重试
  | "no_common"; // 没有共同场景

export interface BehaviorReadiness {
  commonSceneNum: number;
  cur: BehaviorSideReadiness;
  base: BehaviorSideReadiness;
  /** 两侧指纹都就绪的场景数 */
  comparableNow: number;
  /** 两侧平台评估结论不同的场景数（只算翻转时按此计数） */
  flippedSceneNum?: number;
  /** 需要补算的指纹条数（两侧合计） */
  backfillNeeded: number;
  hasReport: boolean;
  compareId?: number | null;
  state: BehaviorReadinessState;
  /** >0 = 抽样探测，cur/base/comparableNow 只是这批样本的口径 */
  sampled?: number;
}

/** `probe=false` 只读登记表不碰 BOS（轮询补算进度时用）。 */
export const getBehaviorReadiness = async (
  jobId: number,
  baseJobId: number,
  probe = true,
  sample = 0,
): Promise<BehaviorReadiness | null> => {
  const response = await api.get("/behavior-compare/readiness", {
    params: { jobId, baseJobId, probe, ...(sample > 0 ? { sample } : {}) },
  });
  return response.data?.data || null;
};

export interface BehaviorLaunchResult {
  compareId: number;
  status: string;
  /** all = 全部共同场景；flipped = 只对比评估结论翻转的场景 */
  scope?: string;
  /** true = 这对任务已有进行中的对比，直接沿用，没有新发起 */
  reused: boolean;
  shardTotal?: number | null;
  flippedTotal?: number | null;
  otherTotal?: number | null;
  /** 本次使用的工具包地址（the code that runs compare_job） */
  packageUrl?: string | null;
  /** explicit = 手动指定；auto_orin_dev = 自动解析当天最新；config = 配置兜底 */
  packageSource?: string | null;
}

/** 对比范围：全部共同场景 / 只对比评估结论翻转的场景 */
export type BehaviorCompareScope = "all" | "flipped";

export interface BehaviorDefaultPackage {
  packageUrl?: string | null;
  packageSource?: string | null;
}

/** 发起弹窗预填：当前会自动使用哪个工具包。 */
export const getBehaviorDefaultPackage = async (): Promise<BehaviorDefaultPackage> => {
  const response = await api.get("/behavior-compare/package");
  return response.data?.data || {};
};

/** 发起行为对比：翻转场景优先算；已有进行中的对比时沿用。 */
export const launchBehaviorCompare = async (
  jobId: number,
  baseJobId: number,
  scope: BehaviorCompareScope = "all",
  packageUrl?: string,
): Promise<BehaviorLaunchResult> => {
  const response = await api.post("/behavior-compare/launch", {
    jobId, baseJobId, scope, ...(packageUrl ? { packageUrl } : {}),
  });
  return response.data?.data;
};

export type BehaviorProgressStatus =
  | "none" // 没发起过
  | "running"
  | "flipped_ready" // 翻转场景已算完，其余还在算
  | "succeeded"
  | "failed";

export interface BehaviorProgress {
  status: BehaviorProgressStatus;
  compareId?: number | null;
  /** all = 全部共同场景；flipped = 只对比评估结论翻转的场景 */
  scope?: string;
  shardTotal: number;
  doneShardNum: number;
  failedShardNum: number;
  flippedTotal?: number | null;
  otherTotal?: number | null;
  /** 已有结果的场景数 */
  computedSceneNum: number;
  flippedReady: boolean;
  packageUrl?: string | null;
  packageSource?: string | null;
  message?: string | null;
  updateTime?: string | null;
}

export const getBehaviorProgress = async (
  jobId: number,
  baseJobId: number,
): Promise<BehaviorProgress> => {
  const response = await api.get("/behavior-compare/progress", {
    params: { jobId, baseJobId },
  });
  return response.data?.data;
};

/** 任务对比明细「行为结论」列：`{sceneId: {label, mismatch}}`，没有报告时为空对象。 */
export type BehaviorSceneLabelMap = Record<
  number,
  { label: BehaviorLabel; mismatch: boolean; /** 主要差异（悬停用，强事件在前，至多 5 条） */ diffs: string[]; /** 强事件中文名 */ strong: string[] }
>;

export const getBehaviorSceneLabels = async (
  jobId: number,
  baseJobId: number,
): Promise<BehaviorSceneLabelMap> => {
  const response = await api.get("/behavior-compare/scene-labels", {
    params: { jobId, baseJobId },
  });
  return response.data?.data || {};
};

/**
 * 任务对比明细表**当页**的行为详情（折线 / 摘要 / 主要差异 / 强事件）：`{sceneId: 行}`。
 *
 * 只能取当页（≤200 个场景，后端超限直接报错）：整任务的折线数据 3 万场景约 40MB，绝不能一次拉取。
 * 没有报告或场景不在报告里时，该场景没有 key，前端显示「—」。
 */
export const getBehaviorSceneRows = async (
  jobId: number,
  baseJobId: number,
  sceneIds: number[],
): Promise<Record<number, BehaviorSceneRow>> => {
  if (sceneIds.length === 0) return {};
  const response = await api.get("/behavior-compare/scene-rows", {
    params: { jobId, baseJobId, sceneIds: Array.from(new Set(sceneIds)).join(",") },
  });
  return response.data?.data || {};
};
